import { useRef, useEffect, useLayoutEffect, useMemo, useCallback, useState, forwardRef, useImperativeHandle, useId } from 'react';

import { BryntumScheduler } from '@bryntum/scheduler-react';
import { Scheduler, Menu, StringHelper, type Column, type ColumnStore, type EventModel, type EventModelConfig, type ResourceModelConfig, type ViewPresetConfig } from '@bryntum/scheduler';
import '@bryntum/scheduler/scheduler.css';
// A Bryntum theme must be bundled so it is present when the widget initialises.
// Without it Bryntum treats the theme as "missing" and auto-injects one from its
// CDN, picking svalbard-dark/light from the OS `prefers-color-scheme` — which
// made the timeline render dark in OS dark mode.
import '@bryntum/scheduler/stockholm-light.css';
import '@/styles/bryntum-evo-theme.css';
import { useBryntumTheme } from '@/contexts/BryntumThemeContext';
import { useDataMode } from '@/contexts/DataModeContext';

import { useScheduler, getDefaultStatusColor } from '@/contexts/SchedulerContext';
import { getCancelledFill, getCancelledOutlineColor, isCancelledBooking } from '@/lib/cancelled-booking-style';
import { isDemandBooking } from '@/lib/demand-booking';
import { Resource, Booking, Absence, BankHoliday, CalendarView, FieldConfiguration, BookingType, StatusConfig, SchedulerGroup, WorkingHoursProfile, RecurrenceSettings, ProjectDetails, type WhatIfBookingReduction } from '@/types';
import type { BookingTypeUsageDto } from '@/services/api';
import { recurrenceSettingsApi } from '@/services/api';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, isValid } from 'date-fns';
import { isCrossDayHourly, oneSecondEarlier } from '@/utils/hourly-display-end';
import { convertTimezone, getTimezoneOffset } from '@/utils/timezone-utils';
import { escapeHtml } from '@/utils/escape-html';
import { formatReductionDelta, generateReductionTooltipSection, reductionTileLabel } from '@/utils/whatif-reduction';
import { applySchedulerView, type SchedulerView } from '@/utils/scheduler-view';
import { utilisationSignature } from '@/utils/utilisation-signature';
import { perfLog, perfStart } from '@/utils/perf-log';
import { buildYearMonthSummaries, YEAR_SUMMARY_EVENT_ID_PREFIX, type YearMonthSummary, type YearSummaryAbsenceInput, type YearSummaryBookingInput, type YearSummaryHolidayInput } from '@/utils/year-month-summary';
import {
  applySequentialRowPaging,
  createResourceRowIndex,
  groupIdsOf,
  isNearVerticalBottom,
  isViewportUnderfilled,
  materialiseAllResourceRows,
  RESOURCE_PAGE_SIZE,
  rowsToRevealGroup,
  withCollapsedGroups,
  type ResourceRowIndex,
  type ResourceTreeNode,
  type VerticalScrollMetrics,
} from '@/utils/resource-window';
import {
  applyResourceTreeInsert,
  diffResourceTreeInsert,
  loadSchedulerEventDataset,
  sameEventValue,
  shouldLoadEventDataset,
  shouldSyncEventStore,
  syncPassChangedScheduler,
  syncSchedulerResources,
  type ResourceTreeNodeConfig,
} from '@/utils/scheduler-stores';
import { getDynamicTooltipFields, getTooltipFieldValue, getTooltipFieldLabel, isFieldOnTooltip as isFieldOnTooltipShared, isColourField } from '@/utils/tooltip-fields';
import { partitionConflictBookings } from '@/utils/resolve-conflict-selection';
import {
  baseBookingIdFromEventId,
  bookingsFromSelectedEvents,
  bulkActionTargets,
  canCopyBooking,
  canMoveBooking,
  linkedEventIds,
  type SelectedEventLike,
} from '@/lib/bookingSelection';
import { featureFlags } from '@/lib/feature-flags';
import { distinctBookingCount } from '@/lib/autoAdjustGroup';
import { groupResizeTargets } from '@/lib/groupResize';
import { groupDragTargets } from '@/lib/groupDrag';
import { calculateDailyUtilization, computeHourlyBookingTotal, getWorkingHoursForDay as getProfileWorkingHoursForDay, type DayCapacity } from '@/utils/heatmap-utils';
import {
  createResourceBookingIndex,
  createResourcePeriodUtilisation,
} from '@/utils/resource-period-utilisation';
import { calculateHeatmapTickUtilisation } from '@/utils/heatmap-tick-utilisation';
import { DEFAULT_UTILIZATION_TIERS } from '@/utils/utilization-labels';
import { getUtilizationColor, getUtilizationLabel } from '@/utils/utilization-labels';
import { getBookingColor } from '@/hooks/use-booking-color';
import { getRelativeLuminance } from '@/lib/color-utils';
import { TIMEZONES, getTimezoneAbbreviation } from '@/lib/timezones';
import {
  getUnallocatedAwareGroupName,
  isUnallocatedBooking,
  UNALLOCATED_RESOURCE_GROUP_NAME,
  UNALLOCATED_RESOURCE_ID,
} from '@/utils/unallocated-booking';
import { toast } from 'sonner';
import { checkContractWindow } from '@/utils/resource-availability';
import { isDateWithinContract } from '@/lib/contractWindow';

// Generate unique ID with timestamp and random component to avoid conflicts in React Strict Mode
const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

// A Year-view tile covers a whole month: it only refuses a booking when the
// resource is contracted on none of its days.
const monthHasContractedDay = (resource: Resource, monthStart: Date): boolean => {
  const day = new Date(monthStart.getFullYear(), monthStart.getMonth(), 1);
  const month = day.getMonth();
  while (day.getMonth() === month) {
    if (isDateWithinContract(resource, day)) return true;
    day.setDate(day.getDate() + 1);
  }
  return false;
};

interface GroupRecordLike {
  id?: unknown;
  isParent?: boolean;
  isRoot?: boolean;
  expanded?: boolean;
  parent?: GroupRecordLike | null;
}

/**
 * How long collapse all / expand all keeps its own toggles from being read back
 * as the user's. The button opens the groups a row at a time and Bryntum raises
 * the toggle of each, some of them a frame or two later.
 */
const BULK_GROUP_TOGGLE_SETTLE_MS = 250;

/** Group rows the user opened or closed since the window last followed along. */
interface GroupToggles {
  opened: string[];
  closed: string[];
}

/** A Bryntum tree-toggle event, which names the row and the direction. */
interface ToggleNodeEvent {
  record?: { id?: unknown };
  collapse?: boolean;
}

/** Ids of the group rows the user currently has collapsed. */
function collapsedGroupIdsOf(scheduler: Scheduler): string[] {
  const store = scheduler.resourceStore as unknown as { allRecords?: GroupRecordLike[] };
  return (store.allRecords ?? [])
    .filter(record => record.isParent && !record.isRoot && !record.expanded)
    .map(record => String(record.id));
}

/** Ids of the group rows the tree currently holds, open or closed. */
function groupRowIdsOf(scheduler: Scheduler): Set<string> {
  const store = scheduler.resourceStore as unknown as { allRecords?: GroupRecordLike[] };
  return new Set((store.allRecords ?? [])
    .filter(record => record.isParent && !record.isRoot)
    .map(record => String(record.id)));
}

// Grouping mode type for timeline view
export type TimelineGroupingMode = 'resource' | 'project-resource' | 'role-resource' | 'customer';



interface BryntumSchedulerTimelineProps {
  selectedDate: Date;
  calendarView: CalendarView;
  zoomLevel: number;
  selectedTimezone?: string;
  resources: Resource[];
  /** Whether the next page of resources is already being loaded. */
  isLoadingMoreResources?: boolean;
  /** Bumped whenever the row paging is reset back to a first page per group. */
  resourceWindowResetToken?: number;
  /** Resource ids behind the rows the tree currently holds, in tree order. */
  onLoadedResourceIdsChange?: (resourceIds: string[]) => void;
  /** Called once the current event set has been committed and painted. */
  onEventsRendered?: () => void;
  bookings: Booking[];
  /**
   * Projects of the screen that owns this timeline (company/mode scoped). Falls back to the
   * scheduler context list, which is not scoped, when the owner doesn't supply one.
   */
  projectDetails?: ProjectDetails[];
  /** Ids of recurring occurrences being deleted; rendered greyed-out and locked. */
  lockedBookingIds?: Set<string>;
  resourceTimeRanges?: Array<{
    id: string;
    resourceId: string;
    name: string;
    startDate: Date;
    endDate: Date;
    timeRangeColor?: string;
    cls?: string;
    style?: string;
  }>;
  timeRanges?: Array<{
    id: string;
    name: string;
    startDate: Date;
    endDate: Date;
    cls?: string;
  }>;
  groupingMode?: TimelineGroupingMode;
  schedulerGroups?: SchedulerGroup[];
  statusConfigs?: StatusConfig[];
  isHeatmapMode?: boolean;
  absences?: Absence[];
  bankHolidays?: BankHoliday[];
  searchQuery?: string;
  enableTimezones?: boolean;
  onEditBooking?: (booking: Booking, contextResourceId?: string) => void;
  onResolveConflict?: (bookings: Booking[]) => void;
  onDeleteBooking?: (booking: Booking) => void;
  /** Delete every booking of a Ctrl/Cmd-click multi-selection in one go. */
  onDeleteBookings?: (bookings: Booking[]) => void;
  onDuplicateBooking?: (booking: Booking) => void;
  onMoveBooking?: (booking: Booking) => void;
  onStatusChange?: (booking: Booking, newStatus: string, statusColor: string) => void;
  /** Change status for every booking of a Ctrl/Cmd-click multi-selection. */
  onStatusChangeBookings?: (bookings: Booking[], newStatus: string, statusColor: string) => void;
  onCopyBooking?: (booking: Booking) => void;
  onSlotClick?: (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Partial<Booking>) => void;
  onDragSelect?: (
    resource: Resource,
    date: Date,
    startTime: { hour: number; minute: number },
    endTime: { hour: number; minute: number },
    bookingData?: Partial<Booking>,
    endDate?: Date
  ) => void;
  onBookingDragEnd?: (
    booking: Booking,
    originalResource: Resource,
    newResourceId: string,
    newStartTime: Date,
    newEndTime: Date,
    isResize?: boolean,
    /** Project view only: the project of the row the booking was dropped on */
    newProjectId?: string
  ) => void;
  onBookingsDragEnd?: (
    targets: Array<{
      booking: Booking;
      resource: Resource;
      newStartTime: Date;
      newEndTime: Date;
    }>,
    meta?: { skippedCount: number },
  ) => void;
  onBookingsResizeEnd?: (
    targets: Array<{
      booking: Booking;
      resource: Resource;
      newStartTime: Date;
      newEndTime: Date;
    }>,
  ) => void;
  onAddBooking?: (resource: Resource, date: Date) => void;
  onQuickAddBooking?: (resource: Resource, date: Date) => void;
  onPasteBooking?: (resource: Resource, date: Date, startTime: { hour: number; minute: number }, bookingData?: Booking) => void;
  copiedBooking?: Booking | null;
  /** What-if scenario handed off from the heatmap — drives ghost tile rendering */
  whatIfScenario?: import('@/types').WhatIfScenario | null;
  /** Booking type usage permissions for the logged-in user — used to restrict
   *  context-menu actions and drag/resize on bookings the user cannot edit. */
  bookingTypeUsages?: BookingTypeUsageDto[] | null;
  /** Role permission: whether the user may create bookings. When false the
   *  Add/Quick Add/Paste context-menu items are hidden. Defaults to true. */
  canCreateBooking?: boolean;
  /** Whether quick add is enabled. When false (Quick Add Default set to
   *  "None") the Quick Add Booking context-menu item is hidden. Defaults to true. */
  quickAddEnabled?: boolean;
  /** Role permission: whether the user may edit bookings. When false the event
   *  context menu is reduced to View only. Defaults to true. */
  canEditBooking?: boolean;
  /** Role permission: whether the user may delete bookings. When false the
   *  Delete Booking context-menu item is hidden. Defaults to true. */
  canDeleteBooking?: boolean;
  /** Role permission: whether the user may override read-only booking types.
   *  When true, bookings of a read-only booking type are still editable — the
   *  event context menu shows "Edit Booking" (not "View Booking") and
   *  drag/resize are allowed — matching the backend override behaviour in
   *  BookingsController.Update. Defaults to true. */
  canOverrideReadOnly?: boolean;
}

export interface BryntumSchedulerTimelineHandle {
  revertEvent: (bookingId: string, originalResourceId: string, originalStartTime: Date, originalEndTime: Date) => void;
  forceStoreRefresh: () => void;
  cleanupPhantomEvents: () => void;
}

interface BryntumResource {
  id: string;
  name: string;
  role?: string;
  department?: string;
  imageUrl?: string;
  /** Set on project-resource child rows — the real resource behind the composite row id */
  originalResourceId?: string;
  /** Set on project group rows and project-resource child rows */
  projectId?: string;
  isProjectGroup?: boolean;
}

interface BryntumEvent {
  id: string;
  resourceId: string;
  name: string;
  startDate: Date;
  endDate: Date;
  eventColor?: string;
  cls?: string;
  /** Inline style Bryntum applies to the event bar. */
  style?: string;
  /** Classes of the leading icon element Bryntum renders inside the bar. */
  iconCls?: string;
  originalBooking?: Booking;
  /** The single resource this tile stands for; a multi-resource booking has one tile per resource. */
  tileResourceId?: string;
  isHeatmapEvent?: boolean;
  isWhatIfGhost?: boolean;
  /** Reduce-hours preview: the booking's before/after hours, shown on the ghost tile and tooltip. */
  whatIfReduction?: WhatIfBookingReduction;
  isPlanPreview?: boolean;
  isAbsenceEvent?: boolean;
  absenceId?: string;
  /** YEAR view monthly summary tile standing in for the row's bookings that month. */
  yearSummary?: YearMonthSummary;
  draggable?: boolean;
  resizable?: boolean;
  eventStyle?: EventModelConfig['eventStyle'];
  heatmapData?: {
    bookedHours: number;
    availableHours: number;
    utilization: number;
    band: string;
    bandColor: string;
    bookings: Booking[];
    confirmedHours: number;
    plannedHours: number;
    absenceHours: number;
    absenceReasons: string[];
    hasHardConflict: boolean;
    weeklyAvgUtilization: number | null;
    /** True when ALL bookings in this cell are non-confirmed (planned/tentative) */
    isPlannedOnly: boolean;
    /** True when the cell has BOTH confirmed and planned bookings */
    isMixed: boolean;
    /**
     * Ratio of planned hours to total booked hours (0–1).
     * 0 = fully confirmed, 1 = fully planned, 0.6 = 60% planned.
     */
    plannedRatio: number;
    /** True when the resource's customWorkingProfile is being used */
    isCustomProfile: boolean;
    /** Working hours for this cell's start day from the active profile */
    dailyHours: number;
  };
}

type EventBarContent = string | Array<Record<string, unknown>>;

/** Bookings listed in a YEAR summary before the rest collapse behind "+ N more". */
const YEAR_SUMMARY_VISIBLE_BOOKINGS = 6;

/** Bryntum's event tooltip hover delay, used outside the YEAR summary. */
const EVENT_TOOLTIP_HOVER_DELAY_MS = 250;
/** Summary tiles fill most of the YEAR planner, so their list waits for the pointer to rest. */
const YEAR_SUMMARY_HOVER_DELAY_MS = 600;
/** How long Bryntum's event tooltip lingers after the pointer leaves its target. */
const EVENT_TOOLTIP_HIDE_DELAY_MS = 300;
/** A YEAR summary list can open beside its tile, so the pointer gets longer to reach it. */
const YEAR_SUMMARY_HIDE_DELAY_MS = 600;

/** The parts of Bryntum's event tooltip used to show and pin a YEAR summary list. */
interface YearSummaryTip {
  autoClose: boolean;
  autoHide: boolean;
  element?: HTMLElement;
  hide: () => void;
  abortDelayedHide: () => void;
  hoverDelay: number;
  hideDelay: number;
  hideOnDelegateChange: boolean;
  isVisible: boolean;
  activeTarget: HTMLElement | null;
  forSelector: string;
  updateContent: () => Promise<unknown>;
  showByTarget: (target: HTMLElement) => void;
  on: (eventName: 'pointerOver', fn: (event: { target: HTMLElement }) => boolean) => () => void;
}

/** Appends the reduce-hours "Proposed change" section when the booking is being reduced. */
function withWhatIfReductionHtml(
  html: string,
  booking: Booking,
  reductions: Map<string, WhatIfBookingReduction>,
): string {
  const reduction = reductions.get(String(booking.id));
  if (!reduction) return html;
  let totalCut = 0;
  reductions.forEach((r) => { totalCut += r.beforeHours - r.afterHours; });
  // Booking details plus the change can be taller than the tooltip's capped
  // height, so let the whole card scroll rather than clip the table.
  return `<div style="overflow-y: auto; min-height: 0; max-height: min(600px, calc(100vh - 80px));">${html}${generateReductionTooltipSection(reduction, totalCut)}</div>`;
}

// Bars show their name, as Bryntum does without a renderer; a YEAR summary tile
// shows its booking count, a strip of its booking colours and absence /
// bank-holiday counts.
function renderSchedulerEvent({ eventRecord, renderData }: {
  eventRecord: EventModel;
  renderData: { ariaLabel: string };
}): EventBarContent {
  const reduction = eventRecord.getData('whatIfReduction') as WhatIfBookingReduction | undefined;
  if (reduction) {
    const delta = formatReductionDelta(reduction.afterHours - reduction.beforeHours);
    renderData.ariaLabel = `Proposed: ${reductionTileLabel(reduction)}, ${delta}`;
    return [{
      tag: 'span',
      className: 'whatif-ghost-label',
      children: [
        { tag: 'span', text: `◈ ${reductionTileLabel(reduction)}` },
        { tag: 'span', className: 'whatif-ghost-label-cut', text: delta },
      ],
    }];
  }

  const summary = (eventRecord.getData('yearSummary') ?? undefined) as YearMonthSummary | undefined;
  if (!summary) return StringHelper.encodeHtml(eventRecord.name ?? '');

  const count = summary.bookings.length;
  const absenceCount = summary.absences.length;
  const holidayCount = summary.holidays.length;
  renderData.ariaLabel = [
    `${count} ${count === 1 ? 'booking' : 'bookings'}`,
    absenceCount > 0 ? `${absenceCount} ${absenceCount === 1 ? 'absence' : 'absences'}` : '',
    holidayCount > 0 ? `${holidayCount} ${holidayCount === 1 ? 'bank holiday' : 'bank holidays'}` : '',
  ].filter(Boolean).join(', ');

  const badges: Array<Record<string, unknown>> = [];
  if (absenceCount > 0) {
    badges.push({
      tag: 'span',
      className: 'eb-year-summary-badge eb-year-summary-badge-absence',
      style: { color: summary.absences[0].color },
      children: [{ tag: 'span', className: 'eb-year-summary-dot' }, { tag: 'span', text: String(absenceCount) }],
    });
  }
  if (holidayCount > 0) {
    badges.push({
      tag: 'span',
      className: 'eb-year-summary-badge eb-year-summary-badge-holiday',
      style: { color: summary.holidays[0].color },
      children: [{ tag: 'span', className: 'eb-year-summary-dot' }, { tag: 'span', text: String(holidayCount) }],
    });
  }

  return [
    {
      tag: 'div',
      className: 'eb-year-summary-strip',
      children: summary.colorSegments.map((segment) => ({
        tag: 'span',
        style: { flexGrow: String(segment.count), backgroundColor: segment.color },
      })),
    },
    {
      tag: 'div',
      className: 'eb-year-summary-body',
      children: [
        {
          tag: 'span',
          className: 'eb-year-summary-total',
          children: [
            { tag: 'span', className: 'eb-year-summary-count', text: String(count) },
            { tag: 'span', className: 'eb-year-summary-label', text: count === 1 ? 'booking' : 'bookings' },
          ],
        },
        ...badges,
      ],
    },
  ];
}

// The row viewport's vertical scroll position, read from the grid's scroller
// with a fallback to its element for the cases where the Scroller's own
// metrics are not populated.
function readVerticalScrollMetrics(scheduler: Scheduler): VerticalScrollMetrics | null {
  const scrollable = (scheduler as unknown as {
    scrollable?: {
      y?: number;
      clientHeight?: number;
      scrollHeight?: number;
      element?: HTMLElement;
    };
  }).scrollable;
  if (!scrollable) return null;

  const element = scrollable.element;
  const scrollTop = scrollable.y ?? element?.scrollTop;
  const clientHeight = scrollable.clientHeight ?? element?.clientHeight;
  const scrollHeight = scrollable.scrollHeight ?? element?.scrollHeight;
  if (scrollTop == null || clientHeight == null || scrollHeight == null) return null;

  return { scrollTop, clientHeight, scrollHeight };
}

function setVerticalScroll(scheduler: Scheduler | undefined, y: number): void {
  if (!scheduler || scheduler.isDestroyed) return;
  const scrollable = (scheduler as unknown as {
    scrollable?: { y?: number; element?: HTMLElement };
  }).scrollable;
  if (!scrollable) return;
  scrollable.y = y;
  if (scrollable.element) scrollable.element.scrollTop = y;
}

// Show a context menu at the pointer, flipping it above/left of the cursor when
// it would overflow the viewport. Without this, a menu opened near the bottom of
// the screen is constrained to the remaining space and becomes scrollable instead
// of repositioning above the click point.
function showMenuAtPointer(menu: Menu, clientX: number, clientY: number): void {
  const margin = 4;

  // Render once near the top-left first: with full space below/right, Bryntum
  // lays the menu out at its natural size instead of constraining it into a
  // scrollable box. Both showBy calls run synchronously in this task, so the
  // browser only paints the final position — the first placement is never seen.
  menu.showBy([margin, margin]);

  const el = menu.element as HTMLElement | undefined;
  if (!el) {
    menu.showBy([clientX, clientY]);
    return;
  }

  const rect = el.getBoundingClientRect();
  const menuHeight = rect.height;
  const menuWidth = rect.width;

  // Flip above/left of the cursor when the menu would overflow the viewport.
  const y = clientY + menuHeight > window.innerHeight - margin
    ? Math.max(margin, clientY - menuHeight)
    : clientY;
  const x = clientX + menuWidth > window.innerWidth - margin
    ? Math.max(margin, clientX - menuWidth)
    : clientX;

  menu.showBy([x, y]);
}

// ── Event tile presentation ──────────────────────────────────────────────────
// Tiles are described entirely by data: Bryntum applies an event's `style` to
// the bar, its `cls` to the same element and renders `iconCls` as a leading
// <i>, so the look lives in the stylesheet keyed off those classes rather than
// in a renderer that runs for every visible event on every repaint.

const WHAT_IF_GHOST_STYLE = [
  'background:transparent',
  'background-color:transparent',
  'border:2px dashed rgba(13,148,136,0.85)',
  'border-radius:4px',
  'box-shadow:none',
  'overflow:visible',
  'position:relative',
  'animation:whatif-border-pulse 1.8s ease-in-out infinite',
].join(';');

const DARK_TEXT_STYLE = 'color: rgba(0,0,0,0.75) !important;';

/** Cap on how long the loading overlay waits for Bryntum to draw the rows. */
const EVENT_RENDER_WAIT_MS = 2000;

/** Per-booking presentation, carried on the event model onto the event bar. */
interface BookingTileDisplay {
  /** Inline style for the event bar. */
  style: string;
  /** Classes for the event bar. */
  cls: string[];
  /** Classes of the leading icon element, empty when the tile carries no icons. */
  iconCls: string;
}

const computeBookingTileDisplay = (
  booking: Booking,
  statusConfigs: Parameters<typeof getBookingColor>[1],
  colorOverrides: Parameters<typeof getBookingColor>[2],
): BookingTileDisplay => {
  const bookingResourceIds = booking.resourceIds || (booking.resourceId ? [booking.resourceId] : []);
  const isMultiResource = bookingResourceIds.length > 1;
  const isRecurring = !!booking.recurrenceGroupId;

  const bgColor = getBookingColor(booking, statusConfigs, colorOverrides);
  // Cancelled bookings are drawn as a dashed outline in the cancelled
  // colour over a faded fill, with the label struck through.
  const isCancelled = isCancelledBooking(booking);
  const luminance = getRelativeLuminance(bgColor);
  // Dark background (luminance < 0.4) gets white icons, light background dark icons.
  const iconsOnDark = !isCancelled && luminance < 0.4;

  // Force background-color via inline !important so Bryntum's hover CSS cannot override it.
  // Inline !important has the highest cascade priority and prevents color from disappearing on hover.
  const declarations = [
    DARK_TEXT_STYLE,
    isCancelled
      ? `background-color: ${getCancelledFill(bgColor)} !important; border: 2px dashed ${getCancelledOutlineColor(bgColor)} !important; text-decoration: line-through;`
      : `background-color: ${bgColor} !important;`,
  ];

  const iconCls: string[] = [];
  if (isMultiResource) {
    iconCls.push('eb-icon-multi');
    // Drawn as the text of the icon element, next to the multi-resource glyph.
    declarations.push(`--eb-resource-count: "${bookingResourceIds.length}";`);
  }
  if (isRecurring) iconCls.push('eb-icon-recurring');
  if (isDemandBooking(booking)) iconCls.push('eb-icon-demand');

  return {
    style: declarations.join(' '),
    cls: ['eb-booking', iconsOnDark ? 'eb-icons-on-dark' : 'eb-icons-on-light'],
    iconCls: iconCls.length > 0 ? ['eb-icons', ...iconCls].join(' ') : '',
  };
};

export const BryntumSchedulerTimeline = forwardRef<BryntumSchedulerTimelineHandle, BryntumSchedulerTimelineProps>(({
  selectedDate,
  calendarView,
  zoomLevel,
  selectedTimezone = 'UTC',
  resources,
  isLoadingMoreResources = false,
  resourceWindowResetToken = 0,
  onLoadedResourceIdsChange,
  onEventsRendered,
  bookings,
  lockedBookingIds,
  resourceTimeRanges = [],
  timeRanges = [],
  groupingMode = 'resource',
  schedulerGroups = [],
  statusConfigs = [],
  isHeatmapMode = false,
  absences = [],
  bankHolidays = [],
  searchQuery = '',
  enableTimezones = false,
  onEditBooking,
  onResolveConflict,
  onDeleteBooking,
  onDeleteBookings,
  onMoveBooking,
  onStatusChange,
  onStatusChangeBookings,
  onCopyBooking,
  onSlotClick,
  onDragSelect,
  projectDetails: projectDetailsProp,
  onBookingDragEnd,
  onBookingsDragEnd,
  onBookingsResizeEnd,
  onAddBooking,
  onQuickAddBooking,
  onPasteBooking,
  copiedBooking,
  whatIfScenario,
  bookingTypeUsages,
  canCreateBooking = true,
  quickAddEnabled = true,
  canEditBooking = true,
  canDeleteBooking = true,
  canOverrideReadOnly = true,
}, ref) => {
  const { getScreenMode } = useDataMode();
  const currentMode = getScreenMode('calendar');

  // SAFETY CHECK: Ensure selectedDate is always valid
  const safeSelectedDate = useMemo(() => {
    if (selectedDate instanceof Date && !isNaN(selectedDate.getTime())) {
      return selectedDate;
    }
    console.warn('Invalid selectedDate received, using current date:', selectedDate);
    return new Date();
  }, [selectedDate]);

  const schedulerRef = useRef<BryntumScheduler>(null);
  const initialDatesRef = useRef({ startDate: safeSelectedDate, endDate: safeSelectedDate });
  // Track whether the initial computed dates have been applied to the ref.
  // After the first valid startDate/endDate are set, we stop updating the ref
  // so the BryntumScheduler wrapper never sees date prop changes. All subsequent
  // date updates are handled imperatively via setTimeSpan() in effects, which
  // prevents the "Invalid start/end dates" error caused by the wrapper applying
  // startDate and endDate as individual prop changes during large date jumps
  // (e.g. clicking "Today" after navigating weeks into the future).
  const hasSetInitialDates = useRef(false);
  const initialViewPresetRef = useRef<ViewPresetConfig | null>(null);
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);

  // SVG icons for the global expand/collapse button (show available ACTION)
  // Minus-in-square = "Collapse all" = shown when all groups are currently expanded
  // Plus-in-square = "Expand all" = shown when all groups are currently collapsed
  const COLLAPSE_ALL_ICON = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;
  const EXPAND_ALL_ICON = `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>`;

  /** Update the global expand/collapse button DOM to reflect the available action */
  const updateExpandCollapseButton = useCallback((expanded: boolean) => {
    const button = document.getElementById('expand-collapse-all-btn');
    if (button) {
      button.innerHTML = expanded ? COLLAPSE_ALL_ICON : EXPAND_ALL_ICON;
      button.title = expanded ? 'Collapse all' : 'Expand all';
    }
  }, []);

  // Ref mirrors allGroupsExpanded so the sync callback never has a stale closure
  const allGroupsExpandedRef = useRef(allGroupsExpanded);
  allGroupsExpandedRef.current = allGroupsExpanded;

  // Groups the sequential window has not reached. They are held out of the rows
  // until a scroll pages them in, which is a state of the window rather than of
  // the user, so neither the global expand state nor the collapsed set may read
  // them as collapsed by the user. Filled in with each paged tree (see below).
  const pendingGroupIdsRef = useRef<ReadonlySet<string>>(new Set());

  // Collapse all and expand all are taken from the button rather than read back
  // off the rows: Bryntum's collapseAll and expandAll raise no toggle event per
  // group, so the rows and the collapsed set would disagree — the window would
  // go on spending its budget on groups it believed open while nothing showed.
  // Held open while the button applies itself (see toggleAllGroups), so the
  // toggles its own row-by-row expansion raises are dropped rather than read as
  // the user opening every group by hand — and the next sync is the user's own
  // next toggle rather than an echo of the button.
  const bulkGroupToggleRef = useRef(false);
  const bulkGroupToggleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const applyBulkGroupToggleRef = useRef<(expand: boolean) => void>(() => {});

  /** Check whether all parent (group) nodes share the same expanded/collapsed state and sync the button */
  const syncGlobalExpandState = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;

    const store = scheduler.resourceStore as unknown as {
      allRecords?: Array<{ id?: unknown; isParent?: boolean; expanded?: boolean }>;
    };
    const pending = pendingGroupIdsRef.current;
    const parents = (store.allRecords ?? [])
      .filter(r => r.isParent && !pending.has(String(r.id)));
    if (parents.length === 0) return;

    const allExpanded = parents.every(r => r.expanded);
    const allCollapsed = parents.every(r => !r.expanded);

    if (allExpanded && !allGroupsExpandedRef.current) {
      setAllGroupsExpanded(true);
      updateExpandCollapseButton(true);
    } else if (allCollapsed && allGroupsExpandedRef.current) {
      setAllGroupsExpanded(false);
      updateExpandCollapseButton(false);
    }
  }, [updateExpandCollapseButton]);

  // Build a Set of booking-type IDs the user has full-access to for O(1) lookups.
  const fullAccessBookingTypeIds = useMemo(() => {
    if (!bookingTypeUsages) return null; // still loading — treat all as editable
    const ids = new Set<string>();
    for (const u of bookingTypeUsages) {
      if (u.isFullAccess) ids.add(String(u.bookingTypeId));
    }
    return ids;
  }, [bookingTypeUsages]);

  /** Returns true when the logged-in user may NOT edit the given booking. */
  const isBookingRestricted = useCallback((booking: Booking): boolean => {
    // Users with the "Override Read-Only Bookings" permission bypass the
    // per-booking-type read-only restriction, matching backend behaviour
    // (see canOverrideReadOnly in BookingsController.Update) and the booking
    // modal (isBookingReadOnly in ComprehensiveBookingModal).
    if (canOverrideReadOnly) return false;
    if (!fullAccessBookingTypeIds) return false; // still loading — allow
    if (!booking.bookingTypeId) return false; // no type — allow
    return !fullAccessBookingTypeIds.has(String(booking.bookingTypeId));
  }, [fullAccessBookingTypeIds, canOverrideReadOnly]);

  // Generate a stable unique ID for the scheduler instance (does not change with groupingMode).
  // Switching grouping modes updates the resource/event stores in-place via a dedicated effect.
  const schedulerIdRef = useRef<string | undefined>(undefined);
  if (!schedulerIdRef.current) {
    schedulerIdRef.current = `bryntum-scheduler-${generateUniqueId()}`;
  }
  const schedulerId = schedulerIdRef.current;

  // NOTE: the Bryntum instance key (schedulerKey) is computed further below,
  // just after the bryntumEvents useMemo.  See the "Scheduler instance key"
  // block there.

  // Cache of recurrence settings fetched from the API, keyed by recurrenceGroupId.
  // Used by the tooltip to show "Occurs every ..." for recurring bookings whose
  // parent (with inline recurrence data) is outside the current date-range view.
  const recurrenceSettingsCacheRef = useRef<Map<string, RecurrenceSettings>>(new Map());

  // Bumped by SchedulerContext whenever a series' recurrence settings change (e.g. the
  // repeat end date is shortened after removing recurrence on an "all future" edit) so
  // the cache below refetches and tooltips show the new end date without a reload.
  const { recurrenceSettingsVersion } = useScheduler();
  const lastRecurrenceVersionRef = useRef(recurrenceSettingsVersion);
  const lastRecurrenceModeRef = useRef(currentMode);

  // Fetch recurrence settings from the API for recurring bookings whose
  // inline recurrence data is missing (child instances whose parent is
  // outside the current date-range).
  useEffect(() => {
    if (lastRecurrenceModeRef.current !== currentMode) {
      lastRecurrenceModeRef.current = currentMode;
      recurrenceSettingsCacheRef.current.clear();
    }

    // A recurrence-settings change invalidates every cached entry; drop them so the
    // affected groups are refetched with their new end date.
    if (lastRecurrenceVersionRef.current !== recurrenceSettingsVersion) {
      lastRecurrenceVersionRef.current = recurrenceSettingsVersion;
      recurrenceSettingsCacheRef.current.clear();
    }

    const groupIds = [...new Set(
      bookings
        .filter((b: Booking) => b.recurrenceGroupId && !b.recurrence)
        .map((b: Booking) => b.recurrenceGroupId as string)
    )].filter(id => !recurrenceSettingsCacheRef.current.has(id));

    if (groupIds.length === 0) return;

    if (currentMode === 'demo') {
      for (const groupId of groupIds) {
        const recurrence = bookings.find(
          (booking: Booking) =>
            booking.recurrenceGroupId === groupId
            && booking.recurrence
            && booking.recurrence.pattern !== 'none'
        )?.recurrence;
        if (recurrence) {
          recurrenceSettingsCacheRef.current.set(groupId, recurrence);
        }
      }
      return;
    }

    let cancelled = false;
    Promise.allSettled(
      groupIds.map(gid =>
        recurrenceSettingsApi.getByRecurrenceGroupId(gid)
          .then(rs => {
            if (cancelled || !rs) return;
            // Map weekly/monthly-specific fields from the API response
            let daysOfWeek: string[] | undefined;
            let monthlyRecurrenceType: RecurrenceSettings['monthlyRecurrenceType'];
            let dayOfMonth: number | undefined;
            let relativeWeekday: RecurrenceSettings['relativeWeekday'];

            if (rs.pattern === 'weekly' && rs.daysOfWeek) {
              try {
                const parsed = JSON.parse(rs.daysOfWeek);
                daysOfWeek = Array.isArray(parsed) ? parsed : [rs.daysOfWeek];
              } catch {
                daysOfWeek = [rs.daysOfWeek];
              }
            }

            if (rs.pattern === 'monthly') {
              if (rs.weekOfMonth && rs.daysOfWeek) {
                monthlyRecurrenceType = 'relative-weekday';
                const occurrenceMap: Record<number, 'first' | 'second' | 'third' | 'fourth' | 'last'> = {
                  1: 'first', 2: 'second', 3: 'third', 4: 'fourth', 5: 'last',
                };
                relativeWeekday = {
                  occurrence: occurrenceMap[rs.weekOfMonth] || 'first',
                  weekday: rs.daysOfWeek,
                };
              } else if (rs.dayOfMonth) {
                monthlyRecurrenceType = 'day-of-month';
                dayOfMonth = rs.dayOfMonth;
              }
            }

            recurrenceSettingsCacheRef.current.set(gid, {
              pattern: rs.pattern as RecurrenceSettings['pattern'],
              interval: rs.interval,
              endType: rs.endType as RecurrenceSettings['endType'],
              occurrences: rs.endAfterOccurrences,
              endDate: rs.endDate,
              daysOfWeek,
              monthlyRecurrenceType,
              dayOfMonth,
              relativeWeekday,
            });
          })
          .catch(() => { /* API unavailable (e.g. demo mode) — skip */ })
      )
    );
    return () => { cancelled = true; };
  }, [bookings, currentMode, recurrenceSettingsVersion]);

  const resourcesRef = useRef(resources);
  resourcesRef.current = resources;

  // Stable ref for settings so the column renderer (captured once at mount)
  // always reads fresh utilisation tier colours and labels.
  const settingsRef = useRef<typeof settings | null>(null);

  const lastSyncedViewRef = useRef<SchedulerView | null>(null);
  const lastSyncedEventsRef = useRef<unknown>(null);
  const lastSyncedWhatIfRef = useRef<unknown>(null);
  const lastSyncedGroupingRef = useRef(groupingMode);
  const prevAppliedResourcesRef = useRef<unknown>(null);

  // Cleanup effect to destroy scheduler instance on unmount
  // Using useLayoutEffect for synchronous cleanup before next render
  useLayoutEffect(() => {
    // Check if an instance with this ID already exists and destroy it
    const existingWidget = (window as any).bryntum?.query?.(schedulerId);
    if (existingWidget && !existingWidget.isDestroyed) {
      console.warn(`Destroying existing scheduler with ID: ${schedulerId}`);
      existingWidget.destroy();
    }

    return () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (scheduler && !scheduler.isDestroyed) {
        scheduler.destroy();
      }
    };
  }, [schedulerId]);



  const { settings, projects, projectDetails: contextProjectDetails, clients, costCentres } = useScheduler();
  // The owning screen resolves drop targets against its own project list, so the rows and
  // project lookups here have to come from that same list.
  const projectDetails = projectDetailsProp ?? contextProjectDetails;
  const { isThemeLoaded } = useBryntumTheme();

  // Drag/drop is always enabled — the Confirm Drag/Drop preference only controls
  // whether a confirmation dialog is shown after the drop, not whether dragging
  // is allowed at all.
  const isDragEnabled = true;

  // Quarterly (week cells) and Yearly (month cells) views use multi-day cells.
  // Drag/drop and resize are disabled here because the time resolution is too
  // coarse for meaningful booking manipulation. Day-level checks (non-working
  // day / non-business hour) are also skipped, since a single cell spans many
  // days and would otherwise always match a weekend and block create/menu.
  const isCoarseGranularityView = calendarView === 'quarter' || calendarView === 'year';

  // Helper function to check if a specific day is a non-working day for a resource
  const isNonWorkingDay = useCallback((resource: Resource, date: Date): boolean => {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const dayKey = dayNames[date.getDay()];

    // Check if resource has custom working profile enabled
    if (resource.useCustomWorkingProfile && resource.customWorkingProfile) {
      const hours = resource.customWorkingProfile[dayKey] || 0;
      return hours === 0;
    }

    // Otherwise use global working profile
    const hours = settings.globalWorkingProfile?.[dayKey] || 0;
    return hours === 0;
  }, [settings.globalWorkingProfile]);

  // Out-of-contract days are refused like non-working days, with a toast giving the
  // contract dates. `endExclusive` defaults to the end of the day `start` falls on.
  const refuseOutOfContract = useCallback((resource: Resource, start: Date, endExclusive?: Date): boolean => {
    const dayStart = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const end = endExclusive ?? new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate() + 1);
    const result = checkContractWindow(resource, dayStart, end);
    if (result.allowed) return false;
    toast.error('Outside contract', { id: 'out-of-contract', description: result.message });
    return true;
  }, []);

  // Helper function to check if a specific time falls within non-business hours (Day view only)
  const isNonBusinessHour = useCallback((date: Date): boolean => {
    if (calendarView !== 'day') return false;

    const bizStartRaw = settings.businessHoursStart ?? 9;
    const bizEndRaw = settings.businessHoursEnd ?? 17;

    // Convert date's time to a decimal hour for comparison (e.g. 10:30 → 10.5)
    const timeDecimal = date.getHours() + date.getMinutes() / 60;

    return timeDecimal < bizStartRaw || timeDecimal >= bizEndRaw;
  }, [calendarView, settings.businessHoursStart, settings.businessHoursEnd]);

  const toggleAllGroups = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) {
      return;
    }

    const shouldExpand = !allGroupsExpanded;
    applyBulkGroupToggleRef.current(shouldExpand);

    // Access the tree feature directly
    const treeFeature = (scheduler as unknown as { features?: { tree?: { collapseAll?: () => void; expandAll?: () => void } } }).features?.tree;

    // Expanding through the rows rather than `expandAll`: a group the window has
    // not reached is not a row Bryntum holds, so it is opened as its rows page
    // in on scroll.
    if (shouldExpand && featureFlags.isEnabled('lazyLoad')) {
      syncGroupRowExpansionRef.current(scheduler);
    } else if (treeFeature) {
      if (shouldExpand && treeFeature.expandAll) {
        treeFeature.expandAll();
      } else if (!shouldExpand && treeFeature.collapseAll) {
        treeFeature.collapseAll();
      }
    }

    // Update button icon to reflect the new state
    updateExpandCollapseButton(shouldExpand);

    setAllGroupsExpanded(shouldExpand);
  }, [allGroupsExpanded, updateExpandCollapseButton]);


  const cleanupPhantomEvents = useCallback(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    const eventStore = scheduler.eventStore;
    const bookingIds = new Set(bookings.map((b: Booking) => String(b.id)));
    const toRemove = (eventStore as any).allRecords?.filter(
      (rec: { id?: string; isPhantom?: boolean; data?: any; originalData?: any }) => {
        if (rec.isPhantom) return true;
        const eventId = String(rec.id || '');
        // Never remove heatmap events — they are keyed by tick index, not booking IDs,
        // and are managed entirely by the heatmapEvents useMemo + the store-sync effect.
        if ((rec as any).data?.isHeatmapEvent || (rec as any).isHeatmapEvent || eventId.startsWith('heatmap-')) return false;
        // Never remove what-if ghost events — they are managed by the whatIfScenario useEffect
        if (eventId.startsWith('__whatif_ghost__') || (rec as any).data?.isWhatIfGhost) return false;
        // Never remove absence events — they are managed by the bryntumEvents useMemo
        if (eventId.startsWith('absence-evt-') || (rec as any).data?.isAbsenceEvent) return false;
        // Never remove YEAR summary tiles — they are managed by the bryntumEvents useMemo
        if (eventId.startsWith(YEAR_SUMMARY_EVENT_ID_PREFIX)) return false;
        // Plan-preview ghost events: only keep them while they are in the active bookings set
        if (eventId.startsWith('plan-preview-') || (rec as any).data?.isPlanPreview) return !bookingIds.has(eventId);
        const hasOriginal = !!(rec as any).data?.originalBooking || !!(rec as any).originalData?.originalBooking;
        // Strip multi-resource suffix to check against base booking IDs
        const baseEventId = eventId.includes('__mr__') ? eventId.split('__mr__')[0] : eventId;
        // Remove any event that isn't mapped from our data (no originalBooking)
        // OR whose id doesn't exist in our current bookings set
        return !hasOriginal || (baseEventId && !bookingIds.has(baseEventId));
      }
    ) || [];
    if (toRemove.length > 0) {
      eventStore.remove(toRemove);
    }
  }, [bookings]);

  // Ref to bryntumEvents — initialised after bryntumEvents useMemo, updated
  // every render so the imperative handle always reads the latest value.
  const bryntumEventsRef = useRef<BryntumEvent[]>([]);

  useImperativeHandle(ref, () => ({
    revertEvent: (bookingId: string, originalResourceId: string, originalStartTime: Date, originalEndTime: Date) => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;

      const eventStore = scheduler.eventStore;
      const eventRecord = eventStore.getById(String(bookingId));

      if (eventRecord) {

        eventRecord.beginBatch();
        (eventRecord as any).startDate = originalStartTime;
        (eventRecord as any).endDate = originalEndTime;
        (eventRecord as any).resourceId = originalResourceId;
        eventRecord.endBatch();
      }
    },
    forceStoreRefresh: () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;

      // Re-sync every event in Bryntum's store against the React-derived
      // bryntumEvents array.  Since React bookings have NOT been mutated
      // (the user discarded), the diff will reset any drag/resize-modified
      // events back to their original positions.
      const eventsData = bryntumEventsRef.current;
      const newEventsById = new Map<string, (typeof eventsData)[number]>();
      eventsData.forEach(e => newEventsById.set(String(e.id), e));

      scheduler.suspendRefresh();
      try {
        (scheduler.eventStore.getRange() as any[]).forEach((existing: any) => {
          const origData = newEventsById.get(String(existing.id));
          if (origData) {
            const d = existing.data ?? {};
            const startChanged = String(d.startDate) !== String(origData.startDate);
            const endChanged = String(d.endDate) !== String(origData.endDate);
            const resChanged = String(d.resourceId ?? '') !== String(origData.resourceId ?? '');
            if (startChanged || endChanged || resChanged) {
              existing.set(origData);
            }
          }
        });
      } finally {
        scheduler.resumeRefresh(true);
      }
    },
    cleanupPhantomEvents,
  }), [cleanupPhantomEvents]);

  // ── What-if ghost tile ────────────────────────────────────────────────────
  // Ghost tile injection is handled directly inside the main store-sync effect
  // below so that it is always atomic with the removeAll/add cycle and fires
  // exactly when the Bryntum scheduler is confirmed to be ready.
  // These constants are used by both the sync effect and cleanupPhantomEvents.
  const GHOST_EVENT_ID_PREFIX = '__whatif_ghost__';
  // Reduce-hours preview details by booking id, so both the ghost and the
  // faded original tile explain the change on hover.
  const whatIfReductionsRef = useRef(new Map<string, WhatIfBookingReduction>());
  whatIfReductionsRef.current = new Map(
    (whatIfScenario?.ghostAssignments ?? []).flatMap((ga) =>
      ga.reduction ? [[String(ga.bookingId), ga.reduction] as const] : []),
  );
  const MOVING_CLS = 'whatif-moving';

  // Helper: extract first-letter-of-first-name + first-letter-of-last-name
  const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 0) return '?';
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    return (first + last).toUpperCase();
  };

  // Helper function to generate avatar URL - returns photoUrl or empty string
  // so the renderer can decide whether to show an <img> or the teal-gradient initials fallback
  const getAvatarUrl = (name: string, photoUrl?: string): string => {
    if (photoUrl && photoUrl.trim() !== '' && photoUrl !== 'null' && photoUrl !== 'undefined') return photoUrl;
    return '';
  };

  // Booking-derived indexes for the resource tree.  Memoised on the bookings
  // alone: paging in another 30 rows rebuilds the tree, and re-bucketing and
  // re-sorting every booking in the range on each page is what makes the
  // append janky in the coarse (month / quarter / year) presets.
  const { bookingsByResource, maxOverlapByResource } = useMemo(() => {
    const byResource = new Map<string, Booking[]>();
    const intervalsByResource = new Map<string | number, Array<{ start: number; end: number }>>();
    const maxOverlap = new Map<string | number, number>();

    bookings.forEach(b => {
      const rid = b.resourceId;
      if (!rid) return;
      if (!intervalsByResource.has(rid)) intervalsByResource.set(rid, []);
      intervalsByResource.get(rid)!.push({
        start: new Date(b.startTime).getTime(),
        end: new Date(b.endTime).getTime(),
      });
      const resourceId = String(rid);
      const resourceBookings = byResource.get(resourceId);
      if (resourceBookings) {
        resourceBookings.push(b);
      } else {
        byResource.set(resourceId, [b]);
      }
    });

    intervalsByResource.forEach((intervals, rid) => {
      const points: Array<{ time: number; type: number }> = [];
      intervals.forEach(iv => {
        points.push({ time: iv.start, type: 1 });
        points.push({ time: iv.end, type: -1 });
      });
      points.sort((a, b) => a.time - b.time || a.type - b.type);
      let concurrent = 0;
      let maxConcurrent = 0;
      points.forEach(p => {
        concurrent += p.type;
        if (concurrent > maxConcurrent) maxConcurrent = concurrent;
      });
      maxOverlap.set(rid, maxConcurrent);
    });

    return { bookingsByResource: byResource, maxOverlapByResource: maxOverlap };
  }, [bookings]);

  // Create tree structure with groups as parent nodes and resources as children
  // Uses the resources prop which is already filtered by ResourceCalendar
  const bryntumResources = useMemo(() => {
    const endBuild = perfStart('build bryntumResources');
    const hasSearchQuery = searchQuery.trim().length > 0;

    // Helper function to check if a resource/project/booking matches the search query
    const matchesSearch = (searchFields: string[]): boolean => {
      if (!hasSearchQuery) return true; // No search query, show all

      const query = searchQuery.toLowerCase();
      return searchFields.some(field =>
        field && field.toLowerCase().includes(query)
      );
    };

    // Row heights grow with the number of overlapping bookings on a row
    const BASE_ROW_HEIGHT = 50;
    const EXTRA_HEIGHT_PER_BOOKING = 25;
    const MAX_BOOKINGS_BEFORE_GROW = 2;

    const resourceById = new Map<string, Resource>();
    const projectById = new Map<string, (typeof projectDetails)[number]>();
    const costCentreById = new Map<string, (typeof costCentres)[number]>();

    resources.forEach(resource => resourceById.set(String(resource.id), resource));
    projectDetails.forEach(project => projectById.set(String(project.id), project));
    costCentres.forEach(costCentre => costCentreById.set(String(costCentre.id), costCentre));

    // A resource's bookings are only read to build search fields, so without a
    // query they cost a per-row fan-out over every booking for nothing.
    const getResourceBookings = (resourceId: string | number): Booking[] =>
      (hasSearchQuery ? bookingsByResource.get(String(resourceId)) : undefined) ?? [];

    const getDynamicRowHeight = (resourceId: string | number): number | undefined => {
      // In heatmap mode each row has exactly one event bar per tick — no stacking
      // occurs, so the row should always use the base height.
      if (isHeatmapMode) return undefined;
      const maxOverlap = maxOverlapByResource.get(resourceId) ?? 0;
      return maxOverlap > MAX_BOOKINGS_BEFORE_GROW
        ? BASE_ROW_HEIGHT + (maxOverlap - MAX_BOOKINGS_BEFORE_GROW) * EXTRA_HEIGHT_PER_BOOKING
        : undefined;
    };

    // In every grouping mode the Unallocated resource is rendered as a single
    // top-level row (no group wrapper) so its bookings are always visible.
    const buildUnallocatedTopLevelNode = (resource: Resource) => {
      const resourceBookings = getResourceBookings(resource.id);
      const searchFields = [
        resource.name,
        resource.schedulerGroup || '',
        resource.department || '',
        UNALLOCATED_RESOURCE_GROUP_NAME,
        ...resourceBookings.flatMap(booking => {
          const project = projectById.get(String(booking.projectId));
          const costCentre = costCentreById.get(String(booking.costCentreId));
          return [
            project?.billingCode || '',
            project?.name || '',
            booking.projectName || '',
            costCentre?.code || '',
            costCentre?.name || '',
            booking.description || ''
          ];
        })
      ];

      if (!matchesSearch(searchFields)) return undefined;

      const dynamicHeight = getDynamicRowHeight(resource.id);
      return {
        id: resource.id,
        name: resource.name,
        role: resource.role,
        department: resource.department || 'Unassigned',
        schedulerGroup: resource.schedulerGroup || 'Unassigned',
        imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
        icon: false,
        iconCls: '',
        cls: 'unallocated-resource-row',
        ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
      };
    };

    // The Unallocated row is prepended to the tree in all grouping modes, so it
    // is never nested inside a project / role / customer group.
    const unallocatedTopLevelResource = resources.find(
      (resource: Resource) => String(resource.id) === UNALLOCATED_RESOURCE_ID
    );

    const withUnallocatedTopLevelRow = <T,>(tree: T[]) => {
      const unallocatedNode = unallocatedTopLevelResource
        ? buildUnallocatedTopLevelNode(unallocatedTopLevelResource)
        : undefined;
      return unallocatedNode ? [unallocatedNode, ...tree] : tree;
    };

    // Project & Resource grouping mode
    if (groupingMode === 'project-resource') {

      // Build map of projects to resources that have bookings for those projects
      const projectResourceMap = new Map<string, Set<string>>();

      bookings.forEach(booking => {
        // Unallocated bookings live on the top-level Unallocated row, not inside
        // a project group.
        if (String(booking.resourceId) === UNALLOCATED_RESOURCE_ID) return;

        if (booking.projectId && booking.projectId !== '') {
          if (!projectResourceMap.has(booking.projectId)) {
            projectResourceMap.set(booking.projectId, new Set());
          }
          projectResourceMap.get(booking.projectId)!.add(booking.resourceId ?? "");
        }
      });

      // Create hierarchical resource structure: Projects > Resources
      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isProjectGroup: boolean;
        projectColor?: string;
        projectCode?: string;
        children: Array<{
          id: string;
          originalResourceId: string;
          projectId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      projectDetails.forEach(project => {
        const resourcesForProject = projectResourceMap.get(String(project.id));

        const hasBookings = !!resourcesForProject && resourcesForProject.size > 0;
        if (!hasBookings) {
          return;
        }

        const projectNode = {
          id: `project-${project.id}`,
          name: project.name,
          expanded: true,
          icon: false,
          iconCls: '',
          isProjectGroup: true,
          projectColor: project.color,
          projectCode: project.billingCode,
          children: [] as Array<{
            id: string;
            originalResourceId: string;
            projectId: string;
            name: string;
            role?: string;
            department: string;
            schedulerGroup: string;
            imageUrl: string;
            icon: boolean;
            iconCls: string;
          }>,
        };

        // Add resources as children under this project
        (resourcesForProject ?? new Set<string>()).forEach(resourceId => {
          if (String(resourceId) === UNALLOCATED_RESOURCE_ID) return;
          const resource = resourceById.get(String(resourceId));
          if (resource) {
            // Get bookings for this resource and project to search in their fields
            const resourceProjectBookings = getResourceBookings(resource.id)
              .filter(b => String(b.projectId) === String(project.id));

            // Collect all searchable fields for this resource-project combination
            const searchFields = [
              resource.name,
              resource.schedulerGroup || '',
              resource.department || '',
              project.billingCode || '',
              project.name,
              ...resourceProjectBookings.flatMap(booking => {
                const costCentre = costCentreById.get(String(booking.costCentreId));
                return [
                  costCentre?.code || '',
                  costCentre?.name || '',
                  booking.description || ''
                ];
              })
            ];

            // Only add resource if it matches the search query
            if (matchesSearch(searchFields)) {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              projectNode.children.push({
                id: `${project.id}-${resource.id}`, // Unique ID for this project-resource combination
                originalResourceId: resource.id, // Store original resource ID for booking operations
                projectId: String(project.id), // Store project ID for filtering
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              });
            }
          }
        });

        // Sort children (resources) alphabetically by name in ascending order
        projectNode.children.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

        if (projectNode.children.length > 0) {
          hierarchicalResources.push(projectNode);
        }
      });

      // Sort projects alphabetically by name in ascending order
      hierarchicalResources.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

      return withUnallocatedTopLevelRow(hierarchicalResources);
    }

    // Role & Resource grouping mode - groups resources by their role
    if (groupingMode === 'role-resource') {

      // Group resources by role
      const roleResourceMap = new Map<string, Resource[]>();

      resources.forEach(resource => {
        if (String(resource.id) === UNALLOCATED_RESOURCE_ID) return;
        const roleName = resource.role || 'Unassigned';
        if (!roleResourceMap.has(roleName)) {
          roleResourceMap.set(roleName, []);
        }
        roleResourceMap.get(roleName)!.push(resource);
      });

      // Create hierarchical resource structure: Roles > Resources
      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isRoleGroup: boolean;
        children: Array<{
          id: string;
          originalResourceId: string;
          roleId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      // Sort roles alphabetically, with "Unassigned" last
      const sortedRoles = Array.from(roleResourceMap.keys()).sort((a, b) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });

      sortedRoles.forEach(roleName => {
        const roleResources = roleResourceMap.get(roleName)!;

        const roleNode = {
          id: `role-${roleName}`,
          name: roleName,
          expanded: true,
          icon: false,
          iconCls: '',
          isRoleGroup: true,
          // Sort children (resources) alphabetically by name in ascending order
          children: roleResources
            .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
            .filter(resource => {
              // Get bookings for this resource to search in their fields
              const resourceBookings = getResourceBookings(resource.id);

              // Collect all searchable fields for this resource
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                roleName,
                ...resourceBookings.flatMap(booking => {
                  const project = projectById.get(String(booking.projectId));
                  const costCentre = costCentreById.get(String(booking.costCentreId));
                  return [
                    project?.billingCode || '',
                    project?.name || '',
                    booking.projectName || '',
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];

              return matchesSearch(searchFields);
            })
            .map(resource => {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              return {
                id: `${roleName}-${resource.id}`, // Unique ID for this role-resource combination
                originalResourceId: resource.id, // Store original resource ID for booking operations
                roleId: roleName, // Store role for reference
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              };
            }),
        };

        // Only add role if it has children after filtering OR if the role itself matches the search
        const roleSearchFields = [roleName];
        if (roleNode.children.length > 0 || matchesSearch(roleSearchFields)) {
          hierarchicalResources.push(roleNode);
        }
      });

      return withUnallocatedTopLevelRow(hierarchicalResources);
    }

    // Customer grouping mode - groups resources by the customer name on their bookings
    if (groupingMode === 'customer') {

      // Build a map of customer name -> Set of resource IDs that have bookings for that customer
      const customerResourceMap = new Map<string, Set<string>>();

      bookings.forEach(booking => {
        // Skip unallocated resource
        if (booking.resourceId === UNALLOCATED_RESOURCE_ID) return;

        // Derive customer name: use booking.customer, or look up via projectDetails clientName
        let customerName: string = booking.customer?.trim() || '';
        if (!customerName && booking.projectId) {
          const project = projectById.get(String(booking.projectId));
          customerName = project?.clientName?.trim() || '';
        }
        if (!customerName) customerName = 'Unassigned';

        if (!customerResourceMap.has(customerName)) {
          customerResourceMap.set(customerName, new Set());
        }
        customerResourceMap.get(customerName)!.add(String(booking.resourceId));
      });

      // Sort customer names alphabetically, Unassigned at the end
      const sortedCustomers = Array.from(customerResourceMap.keys()).sort((a, b) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });

      const hierarchicalResources: Array<{
        id: string;
        name: string;
        expanded: boolean;
        icon: boolean;
        iconCls: string;
        isCustomerGroup: boolean;
        children: Array<{
          id: string;
          originalResourceId: string;
          customerId: string;
          name: string;
          role?: string;
          department: string;
          schedulerGroup: string;
          imageUrl: string;
          icon: boolean;
          iconCls: string;
        }>;
      }> = [];

      sortedCustomers.forEach(customerName => {
        const resourceIdsForCustomer = customerResourceMap.get(customerName)!;

        const customerNode = {
          id: `customer-${customerName}`,
          name: customerName,
          expanded: true,
          icon: false,
          iconCls: '',
          isCustomerGroup: true,
          children: [] as Array<{
            id: string;
            originalResourceId: string;
            customerId: string;
            name: string;
            role?: string;
            department: string;
            schedulerGroup: string;
            imageUrl: string;
            icon: boolean;
            iconCls: string;
          }>,
        };

        resourceIdsForCustomer.forEach(resourceId => {
          const resource = resourceById.get(String(resourceId));
          if (resource) {
            // Collect searchable fields for this customer-resource combination
            const resourceCustomerBookings = getResourceBookings(resource.id).filter(b => {
              if (String(b.resourceId) !== String(resource.id)) return false;
              let bCustomer: string = b.customer?.trim() || '';
              if (!bCustomer && b.projectId) {
                const project = projectById.get(String(b.projectId));
                bCustomer = project?.clientName?.trim() || '';
              }
              if (!bCustomer) bCustomer = 'Unassigned';
              return bCustomer === customerName;
            });

            const searchFields = [
              resource.name,
              resource.schedulerGroup || '',
              resource.department || '',
              customerName,
              ...resourceCustomerBookings.flatMap(booking => {
                const project = projectById.get(String(booking.projectId));
                const costCentre = costCentreById.get(String(booking.costCentreId));
                return [
                  project?.billingCode || '',
                  project?.name || '',
                  booking.projectName || '',
                  costCentre?.code || '',
                  costCentre?.name || '',
                  booking.description || '',
                ];
              }),
            ];

            if (matchesSearch(searchFields)) {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              customerNode.children.push({
                id: `${customerName}-${resource.id}`,
                originalResourceId: resource.id,
                customerId: customerName,
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              });
            }
          }
        });

        // Sort children alphabetically by resource name
        customerNode.children.sort((a, b) => (a.name || '').localeCompare((b as any).name || ''));

        const customerSearchFields = [customerName];
        if (customerNode.children.length > 0 || matchesSearch(customerSearchFields)) {
          hierarchicalResources.push(customerNode);
        }
      });


      return withUnallocatedTopLevelRow(hierarchicalResources);
    }

    // Default Resource grouping mode (by department or scheduler group)
    if (settings.groupingMethod === 'scheduler-group') {
      // Use DB scheduler groups: group resources by schedulerGroupId,
      // only show groups that have resources, put unassigned resources under "Unassigned"
      const groupIdToName = new Map<number, string>();
      schedulerGroups.forEach(g => groupIdToName.set(Number(g.id), g.name));

      const groupedByGroupId: Record<string, Resource[]> = {};
      let unallocatedResource: Resource | undefined;

      resources.forEach((resource: Resource) => {
        if (String(resource.id) === UNALLOCATED_RESOURCE_ID) {
          unallocatedResource = resource;
        } else if (resource.schedulerGroupId != null && groupIdToName.has(resource.schedulerGroupId)) {
          const groupName = groupIdToName.get(resource.schedulerGroupId)!;
          if (!groupedByGroupId[groupName]) {
            groupedByGroupId[groupName] = [];
          }
          groupedByGroupId[groupName].push(resource);
        } else {
          // Resource has no group or group not found — put under "Unassigned"
          if (!groupedByGroupId['Unassigned']) {
            groupedByGroupId['Unassigned'] = [];
          }
          groupedByGroupId['Unassigned'].push(resource);
        }
      });

      const treeData = Object.entries(groupedByGroupId)
        .sort(([a], [b]) => {
          if (a === 'Unassigned') return 1;
          if (b === 'Unassigned') return -1;
          return a.localeCompare(b);
        })
        .map(([groupName, groupResources]) => {
          const filteredGroupResources = groupResources
            .filter((resource: Resource) => {
              const resourceBookings = getResourceBookings(resource.id);
              const searchFields = [
                resource.name,
                resource.schedulerGroup || '',
                resource.department || '',
                groupName,
                ...resourceBookings.flatMap(booking => {
                  const project = projectById.get(String(booking.projectId));
                  const costCentre = costCentreById.get(String(booking.costCentreId));
                  return [
                    project?.billingCode || '',
                    project?.name || '',
                    booking.projectName || '',
                    costCentre?.code || '',
                    costCentre?.name || '',
                    booking.description || ''
                  ];
                })
              ];
              return matchesSearch(searchFields);
            });

          return {
            id: `group-${groupName}`,
            name: groupName,
            expanded: true,
            icon: false,
            iconCls: '',
            children: filteredGroupResources
              .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
              .map((resource: Resource) => {
                const dynamicHeight = getDynamicRowHeight(resource.id);
                return {
                  id: resource.id,
                  name: resource.name,
                  role: resource.role,
                  department: resource.department || 'Unassigned',
                  schedulerGroup: resource.schedulerGroup || 'Unassigned',
                  imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                  icon: false,
                  iconCls: '',
                  ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
                };
              }),
          };
        })
        .filter(group => {
          const groupSearchFields = [group.name];
          return group.children.length > 0 || matchesSearch(groupSearchFields);
        });

      const unallocatedNode = unallocatedResource
        ? buildUnallocatedTopLevelNode(unallocatedResource)
        : undefined;

      return unallocatedNode ? [unallocatedNode, ...treeData] : treeData;
    }

    // Default: group by department
    const groupedResources: Record<string, Resource[]> = {};
    let unallocatedResource: Resource | undefined;

    resources.forEach((resource: Resource) => {
      if (String(resource.id) === UNALLOCATED_RESOURCE_ID) {
        unallocatedResource = resource;
        return;
      }
      const groupName = getUnallocatedAwareGroupName(resource, resource.department || 'Unassigned');
      if (!groupedResources[groupName]) {
        groupedResources[groupName] = [];
      }
      groupedResources[groupName].push(resource);
    });

    // Create tree structure with sorting
    const treeData = Object.entries(groupedResources)
      // Sort groups alphabetically, with "Unassigned" last
      .sort(([a], [b]) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      })
      .map(([groupName, groupResources]) => {
        // Filter resources based on search query
        const filteredGroupResources = groupResources
          .filter((resource: Resource) => {
            // Get bookings for this resource to search in their fields
            const resourceBookings = getResourceBookings(resource.id);

            // Collect all searchable fields for this resource
            const searchFields = [
              resource.name,
              resource.schedulerGroup || '',
              resource.department || '',
              groupName,
              ...resourceBookings.flatMap(booking => {
                const project = projectById.get(String(booking.projectId));
                const costCentre = costCentreById.get(String(booking.costCentreId));
                return [
                  project?.billingCode || '',
                  project?.name || '',
                  booking.projectName || '',
                  costCentre?.code || '',
                  costCentre?.name || '',
                  booking.description || ''
                ];
              })
            ];

            return matchesSearch(searchFields);
          });

        return {
          id: `group-${groupName}`,
          name: groupName,
          expanded: true,
          icon: false,
          iconCls: '',
          // Sort children (resources) alphabetically by name in ascending order
          children: filteredGroupResources
            .sort((a, b) => (a.name || '').localeCompare((b as any).name || ''))
            .map((resource: Resource) => {
              const dynamicHeight = getDynamicRowHeight(resource.id);
              return {
                id: resource.id,
                name: resource.name,
                role: resource.role,
                department: resource.department || 'Unassigned',
                schedulerGroup: resource.schedulerGroup || 'Unassigned',
                imageUrl: getAvatarUrl(resource.name, resource.photoUrl),
                icon: false,
                iconCls: '',
                ...(dynamicHeight !== undefined && { rowHeight: dynamicHeight }),
              };
            }),
        };
      })
      // Filter out groups with no children after filtering
      .filter(group => {
        // Keep group if it has children OR if the group name itself matches the search
        const groupSearchFields = [group.name];
        return group.children.length > 0 || matchesSearch(groupSearchFields);
      });

    const unallocatedNode = unallocatedResource
      ? buildUnallocatedTopLevelNode(unallocatedResource)
      : undefined;

    const tree = unallocatedNode ? [unallocatedNode, ...treeData] : treeData;
    endBuild({ rows: tree.length });
    return tree;
  }, [resources, bookings, bookingsByResource, maxOverlapByResource, projects, projectDetails, costCentres, schedulerGroups, settings.groupingMethod, groupingMode, searchQuery, isHeatmapMode]);

  // ── Sequential row paging ────────────────────────────────────────────────
  // One window of rows over the whole tree: the first group fills its page
  // before the next group holds any rows, and each scroll to the bottom adds a
  // page wherever the rows have reached. Every group is still a row of the tree
  // carrying its full resource count, so collapsing — or collapsing all —
  // always shows every group rather than only the ones the window reached.
  const [loadedRowCount, setLoadedRowCount] = useState(RESOURCE_PAGE_SIZE);
  // Only rows on screen are charged to the window, so a closed group spends
  // nothing and opening one pages its rows in. Mirrored from the live rows (see
  // the toggle listener) so it follows every route to a collapse — the row's own
  // expander, collapse all and expand all alike.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<ReadonlySet<string>>(() => new Set());
  // Rows a group the user opened may hold: opening one is a page of its own, so
  // a group is not handed every row the window has left over just because the
  // rows above it have already been scrolled past.
  const [groupRowCaps, setGroupRowCaps] = useState<ReadonlyMap<string, number>>(() => new Map());

  const previousPagingResetTokenRef = useRef(resourceWindowResetToken);
  if (previousPagingResetTokenRef.current !== resourceWindowResetToken) {
    previousPagingResetTokenRef.current = resourceWindowResetToken;
    setLoadedRowCount(RESOURCE_PAGE_SIZE);
  }

  // With `lazyLoad` off every row is materialised at once, so the rows scroll
  // normally and nothing is paged in behind the scroll position.
  const pagedTree = useMemo(
    () => (featureFlags.isEnabled('lazyLoad')
      ? applySequentialRowPaging(
        bryntumResources as ResourceTreeNode[],
        loadedRowCount,
        collapsedGroupIds,
        RESOURCE_PAGE_SIZE,
        groupRowCaps,
      )
      : materialiseAllResourceRows(bryntumResources as ResourceTreeNode[], collapsedGroupIds)),
    [bryntumResources, loadedRowCount, collapsedGroupIds, groupRowCaps],
  );
  const pagedResources = pagedTree.tree;

  const loadMoreRows = useCallback(() => {
    setLoadedRowCount(current => {
      const next = current + RESOURCE_PAGE_SIZE;
      perfLog('scroll-load: rows paged in', { loadedRows: next });
      return next;
    });
    // The groups the user opened take their next page along with the window,
    // rather than staying at the page they were opened with for good.
    setGroupRowCaps(current => (current.size === 0
      ? current
      : new Map([...current].map(([id, cap]) => [id, cap + RESOURCE_PAGE_SIZE]))));
  }, []);

  const loadMoreRowsRef = useRef(loadMoreRows);
  loadMoreRowsRef.current = loadMoreRows;

  // Read by the collapse/expand sync, which runs from a Bryntum listener and so
  // must not be rebuilt every time the tree or the collapsed groups change.
  const collapsedGroupIdsRef = useRef(collapsedGroupIds);
  collapsedGroupIdsRef.current = collapsedGroupIds;
  const bryntumResourcesRef = useRef(bryntumResources);
  bryntumResourcesRef.current = bryntumResources;
  pendingGroupIdsRef.current = useMemo(
    () => new Set(pagedTree.pendingGroupIds),
    [pagedTree.pendingGroupIds],
  );

  /**
   * Mirror which groups the user has collapsed, so only the open ones carry
   * rows, and pull the rows of a group the user opens into the window.
   *
   * `toggled` holds the groups the user opened or closed since the last run.
   */
  const syncCollapsedGroupIds = useCallback((
    scheduler: Scheduler,
    toggled: GroupToggles = { opened: [], closed: [] },
  ) => {
    // A group the window has not reached is held out of the rows, and Bryntum
    // reports a row it still holds mid-render as collapsed. Reading that back as
    // a collapse would keep the group out of the window for good and stall the
    // paging there, so it only counts as collapsed once the user says so.
    const pending = pendingGroupIdsRef.current;
    const previous = collapsedGroupIdsRef.current;
    const closedRows = new Set(collapsedGroupIdsOf(scheduler));
    const collapsed = new Set([...closedRows].filter(id => (
      !pending.has(id) || previous.has(id) || toggled.closed.includes(id)
    )));
    for (const id of toggled.opened) collapsed.delete(id);

    // Opening a group has to show that group's rows even when the window
    // stopped above it: the rows in between have not been scrolled past, so
    // nothing would ever page the opened group's rows in. Growing only as far
    // as the topmost group opened keeps "expand all" from materialising the
    // whole tree — its first group already sits inside the window, and the rest
    // page in as the user scrolls.
    // Only groups that are rows: a group the window has not reached, and any
    // group nested below it, is held out of the tree, and its absence is not the
    // user opening it — read as one, it would grow the window to reveal the very
    // group being held back.
    const groupRows = groupRowIdsOf(scheduler);
    const opened = [...new Set([
      ...toggled.opened,
      // A group opened by its own expander rather than through a toggle event:
      // it was held closed and its rows are now on screen.
      ...[...pending, ...previous].filter(id => groupRows.has(id) && !closedRows.has(id)),
    ])].filter(id => !collapsed.has(id));
    if (opened.length > 0) {
      const needed = Math.min(...opened.map(id => rowsToRevealGroup(
        bryntumResourcesRef.current as ResourceTreeNode[],
        id,
        collapsed,
      )));
      setLoadedRowCount(rows => Math.max(rows, needed));
      // Opened by hand, so it starts at a page of its rows however far the
      // window reaches, and takes the next page on the next scroll.
      setGroupRowCaps(current => {
        const next = new Map(current);
        for (const id of opened) next.set(id, RESOURCE_PAGE_SIZE);
        return next;
      });
    }

    setCollapsedGroupIds(current => (
      current.size === collapsed.size && [...collapsed].every(id => current.has(id))
        ? current
        : collapsed
    ));
  }, []);

  const syncCollapsedGroupIdsRef = useRef(syncCollapsedGroupIds);
  syncCollapsedGroupIdsRef.current = syncCollapsedGroupIds;

  applyBulkGroupToggleRef.current = (expand: boolean) => {
    // Released once the button has finished opening or closing its rows, which
    // it does one row at a time: the toggles raised in between are the button's
    // own, and a group opened by the button must not be given a page of rows the
    // window never reached.
    bulkGroupToggleRef.current = true;
    if (bulkGroupToggleTimerRef.current !== null) clearTimeout(bulkGroupToggleTimerRef.current);
    bulkGroupToggleTimerRef.current = setTimeout(() => {
      bulkGroupToggleRef.current = false;
      bulkGroupToggleTimerRef.current = null;
    }, BULK_GROUP_TOGGLE_SETTLE_MS);
    // Either button puts the window back to its first page and the view back to
    // the top, so both read like a fresh load: expand all opens every group over
    // the first page of rows and the rest page in on scroll, rather than
    // materialising the whole tree, and collapse all leaves the next group
    // opened by hand starting at a page of rows again. Scrolling back up as
    // well, so the rows the user had scrolled past are not left off screen with
    // the window no longer reaching them.
    setLoadedRowCount(RESOURCE_PAGE_SIZE);
    setGroupRowCaps(current => (current.size === 0 ? current : new Map()));
    lastViewportTopUpRowsRef.current = null;
    lastVerticalScrollRef.current = 0;
    setVerticalScroll(schedulerRef.current?.instance as Scheduler | undefined, 0);

    const collapsed = expand
      ? new Set<string>()
      : groupIdsOf(bryntumResourcesRef.current as ResourceTreeNode[]);
    // Ahead of the render, so a sync running before it does not read the groups
    // as still collapsed and collapse them again.
    collapsedGroupIdsRef.current = collapsed;
    setCollapsedGroupIds(collapsed);
  };

  /**
   * Open the groups the window has reached. Bryntum keeps the state of a row it already
   * holds, so a group that rendered collapsed while the window was above it
   * stays collapsed once its rows arrive — the rows would be charged to the
   * window with nothing on screen to show for them, and the paging would stall.
   *
   * Expand all opens the groups through here rather than through Bryntum's
   * `expandAll`, which opens the groups the window has not reached as well —
   * over the page of rows they carry off screen, showing rows the window never
   * paged in.
   */
  const syncGroupRowExpansion = useCallback((scheduler: Scheduler) => {
    if (!featureFlags.isEnabled('lazyLoad')) return;
    const collapsed = collapsedGroupIdsRef.current;
    const pending = pendingGroupIdsRef.current;
    const store = scheduler.resourceStore as unknown as {
      allRecords?: Array<{ id?: unknown; isParent?: boolean; expanded?: boolean }>;
    };
    // Opened through the tree feature, which renders the rows it reveals: the
    // store's own expand belongs to its row grouping, and leaves the group open
    // in the data with its rows still off screen.
    const tree = (scheduler as unknown as {
      features?: { tree?: { expand?: (record: unknown) => Promise<void> } };
    }).features?.tree;
    for (const record of store.allRecords ?? []) {
      if (!record.isParent || record.expanded) continue;
      const id = String(record.id);
      if (collapsed.has(id) || pending.has(id)) continue;
      void tree?.expand?.(record);
    }
  }, []);

  const syncGroupRowExpansionRef = useRef(syncGroupRowExpansion);
  syncGroupRowExpansionRef.current = syncGroupRowExpansion;

  const onLoadedResourceIdsChangeRef = useRef(onLoadedResourceIdsChange);
  onLoadedResourceIdsChangeRef.current = onLoadedResourceIdsChange;

  const materialisedResourceIds = pagedTree.materialisedResourceIds;
  const materialisedResourceIdsKey = materialisedResourceIds.join('|');
  useEffect(() => {
    onLoadedResourceIdsChangeRef.current?.(materialisedResourceIds);
    // The ids are compared by content: a rebuild of the same rows must not make
    // the owner recompute the time ranges of every loaded row again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialisedResourceIdsKey]);

  const resourceRowIndex = useMemo(() => createResourceRowIndex(pagedResources), [pagedResources]);
  const loadedRowIds = resourceRowIndex.rowIds;

  // The resource column renderer runs while rows paint, so it reads the index
  // off a ref instead of taking it as a dependency and rebuilding the columns.
  const resourceRowIndexRef = useRef<ResourceRowIndex>(resourceRowIndex);
  resourceRowIndexRef.current = resourceRowIndex;

  const onEventsRenderedRef = useRef(onEventsRendered);
  onEventsRenderedRef.current = onEventsRendered;

  // Uses the bookings prop which is already filtered by ResourceCalendar
  const bryntumEvents: BryntumEvent[] = useMemo(() => {
    // In heatmap mode, return empty array to hide all bookings
    if (isHeatmapMode) {
      return [];
    }

    const endBuild = perfStart('build bryntumEvents');

    const resourceById = new Map<string, Resource>();
    const projectById = new Map<string, (typeof projectDetails)[number]>();
    resources.forEach(resource => resourceById.set(String(resource.id), resource));
    projectDetails.forEach(project => projectById.set(String(project.id), project));

    // Maps a booking's assigned resource to the row it is drawn on, which
    // depends on the grouping mode.
    const displayRowId = (booking: Booking, resId: string): string => {
      if (String(resId) === UNALLOCATED_RESOURCE_ID) {
        // The Unallocated row is a single top-level row in every grouping
        // mode, so its bookings never use a composite group-resource id.
        return UNALLOCATED_RESOURCE_ID;
      }
      if (groupingMode === 'project-resource' && booking.projectId) {
        return `${booking.projectId}-${resId}`;
      }
      if (groupingMode === 'role-resource') {
        const resource = resourceById.get(String(resId));
        return `${resource?.role || 'Unassigned'}-${resId}`;
      }
      if (groupingMode === 'customer') {
        let customerName: string = booking.customer?.trim() || '';
        if (!customerName && booking.projectId) {
          const project = projectById.get(String(booking.projectId));
          customerName = project?.clientName?.trim() || '';
        }
        return `${customerName || 'Unassigned'}-${resId}`;
      }
      return resId;
    };

    const bookingEvents: BryntumEvent[] = bookings
      .flatMap((booking: Booking) => {
        // Determine which resource IDs to display this booking on.
        // For multi-resource bookings, expand into one event per assigned resource.
        const displayResourceIds: string[] = booking.resourceIds && booking.resourceIds.length > 1
          ? booking.resourceIds.map(String)
          // A booking with no resource of its own belongs on the Unallocated row.
          : [booking.resourceId ?? UNALLOCATED_RESOURCE_ID];

        // Rows outside the loaded window hold no events: the record would be
        // indexed and diffed on every sync without ever being drawn.  Resolve
        // the rows first so a booking that lands entirely outside the window
        // skips the date, colour and tile work below — paging in another 30
        // rows re-runs this memo over every booking in the range.
        const targetRows = displayResourceIds
          .map((resId, idx) => ({ resId, idx, rowId: displayRowId(booking, resId) }))
          .filter(({ rowId }) => loadedRowIds.has(rowId));

        if (targetRows.length === 0) return [];

        // Parse booking times - stored without Z suffix to prevent UTC interpretation
        // Use the times directly without any timezone conversion to avoid 5:30 AM offset for IST users
        const startDate = new Date(booking.startTime);
        const endDate = new Date(booking.endTime);

        // Shift based on difference between booking timezone and current header timezone
        // Pass booking's start date so DST is calculated for the correct date
        const sourceTz = (booking as any).timezone || 'Europe/London';
        const targetTz = selectedTimezone || sourceTz;
        const offsetMin = enableTimezones ? getTimezoneOffset(sourceTz, targetTz, startDate) : 0;
        const displayStartDate = new Date(startDate.getTime() + offsetMin * 60000);
        const displayEndDate = new Date(endDate.getTime() + offsetMin * 60000);

        // For half-day AM/PM bookings, use the actual stored start/end times
        // Morning bookings are stored as 00:00–11:59, Afternoon as 12:00–23:59
        // No clamping to business hours — display matches stored data

        // Build CSS class list for the event
        const clsList: string[] = [];
        const isLocked = !!lockedBookingIds && lockedBookingIds.has(String(booking.id));
        const isRecurringBooking = !!booking.recurrenceGroupId;
        if (booking.isFiltered) clsList.push('filtered-booking');
        if (booking.recurrencePending) clsList.push('pending-recurring-booking');
        if (isLocked) clsList.push('booking-deleting');
        if (isRecurringBooking) clsList.push('recurring-booking');
        if (isDemandBooking(booking)) clsList.push('demand-booking');
        if (isUnallocatedBooking(booking)) {
          clsList.push('unallocated-booking');
        }
        if ((booking as any).isPlanPreview) clsList.push('plan-preview-booking');
        if ((booking as any).isPlanPreview) clsList.push('b-suggested');

        const tile = computeBookingTileDisplay(booking, settings.statusConfigs, settings.colorOverrides);
        clsList.push(...tile.cls);

        const isPlanPreview = !!(booking as any).isPlanPreview;
        const eventColor = getBookingColor(booking, settings.statusConfigs, settings.colorOverrides);
        const cls = clsList.length > 0 ? clsList.join(' ') : undefined;

        return targetRows.map(({ resId, idx, rowId }) => {
          // Use composite ID for additional resource copies to keep Bryntum event IDs unique.
          // The primary resource (idx === 0) keeps the original booking ID for compatibility.
          const eventId = idx === 0 ? String(booking.id) : `${booking.id}__mr__${idx}`;

          return {
            id: eventId,
            resourceId: rowId,
            tileResourceId: String(resId),
            name: booking.projectName || booking.description || 'Booking',
            startDate: displayStartDate,
            endDate: displayEndDate,
            eventColor,
            cls,
            style: tile.style,
            ...(tile.iconCls ? { iconCls: tile.iconCls } : {}),
            originalBooking: booking,
            isPlanPreview,
            ...(isLocked || isRecurringBooking
              ? { draggable: false, resizable: false }
              : {}),
          };
        });
      });

    // Add annual-leave absences as dashed event bars and public-holiday
    // absences as line-style event bars (coloured with the Public Holiday
    // colour configured in Settings).
    const absenceColor = settings.absenceColor || '#94a3b8';
    const publicHolidayColor = settings.publicHolidayColor || '#fbbf24';
    // Absences drawn only as row shading, which the YEAR summary still lists.
    const shadedAbsences: YearSummaryAbsenceInput[] = [];
    const absenceEvents: BryntumEvent[] = absences
      .flatMap(absence => {
        const reason = absence.reason.toLowerCase().replace(/[-_]/g, ' ');
        const isAnnualLeave = reason.includes('annual leave');
        const isPublicHoliday = reason.includes('public holiday') || reason.includes('bank holiday');

        const resId = String(absence.resourceId);
        let resourceId: string;
        if (groupingMode === 'role-resource') {
          const resource = resourceById.get(resId);
          const roleName = resource?.role || 'Unassigned';
          resourceId = `${roleName}-${resId}`;
        } else {
          resourceId = resId;
        }

        if (!loadedRowIds.has(resourceId)) return [];

        const absStart = new Date(absence.startDate);
        const absEnd = new Date(absence.endDate);
        // Ensure end date covers the full day
        const displayEnd = new Date(absEnd);
        if (displayEnd.getHours() === 0 && displayEnd.getMinutes() === 0) {
          displayEnd.setHours(23, 59, 59, 999);
        }

        if (!isAnnualLeave && !isPublicHoliday) {
          shadedAbsences.push({
            id: `absence-${absence.id}-${resourceId}`,
            rowId: resourceId,
            label: absence.reason || 'Absence',
            start: absStart,
            end: displayEnd,
            color: absenceColor,
            isHoliday: false,
          });
          return [];
        }

        return [{
          id: `absence-evt-${absence.id}-${resourceId}-${absStart.getTime()}`,
          absenceId: String(absence.id),
          resourceId,
          name: isPublicHoliday
            ? (absence.reason || 'Public Holiday')
            : (settings.absenceLabelOverride || 'Annual Leave'),
          startDate: absStart,
          endDate: displayEnd,
          eventColor: isPublicHoliday ? publicHolidayColor : absenceColor,
          eventStyle: isPublicHoliday ? 'line' : 'dashed',
          // Bryntum's dashed/line styles draw from --event-color and the border,
          // both of which the theme leaves to the record.
          style: `--event-color: ${isPublicHoliday ? publicHolidayColor : absenceColor}; border-color: ${isPublicHoliday ? publicHolidayColor : absenceColor}; border-radius: 0 !important; padding: 0 2px;`,
          // Keeps the selection ring off absence tiles, which are never bookings.
          cls: 'b-absence-event',
          isAbsenceEvent: true,
        }];
      });

    if (calendarView === 'year' && featureFlags.isEnabled('yearMonthSummary')) {
      const statusTextOf = (status: string): string => {
        const statusConfig = settings.statusConfigs?.find((s) => String(s.id) === String(status));
        return statusConfig?.customText || statusConfig?.text || status;
      };
      const summaryBookings: YearSummaryBookingInput[] = bookingEvents.flatMap((evt) => {
        const booking = evt.originalBooking;
        if (!booking) return [];
        return [{
          eventId: evt.id,
          bookingId: String(booking.id),
          rowId: evt.resourceId,
          resourceId: evt.tileResourceId ?? evt.resourceId,
          title: evt.name,
          start: evt.startDate,
          end: evt.endDate,
          color: evt.eventColor || '',
          status: booking.status ? statusTextOf(booking.status) : undefined,
        }];
      });
      const summaryAbsences: YearSummaryAbsenceInput[] = absenceEvents.map((evt): YearSummaryAbsenceInput => ({
        id: evt.id,
        rowId: evt.resourceId,
        label: evt.name,
        reason: absences.find((a) => String(a.id) === evt.absenceId)?.reason,
        start: evt.startDate,
        end: evt.endDate,
        color: evt.eventColor || absenceColor,
        isHoliday: evt.eventStyle === 'line',
      })).concat(shadedAbsences);
      const summaryHolidays: YearSummaryHolidayInput[] = resourceTimeRanges
        .filter((range) => (range.cls ?? '').split(' ').includes('bank-holiday-range') && loadedRowIds.has(String(range.resourceId)))
        .map((range) => ({
          id: range.id,
          rowId: String(range.resourceId),
          name: range.name,
          start: range.startDate,
          end: range.endDate,
          color: range.timeRangeColor || publicHolidayColor,
        }));
      const yearStart = startOfYear(safeSelectedDate);
      const summaries = buildYearMonthSummaries({
        rangeStart: yearStart,
        rangeEnd: new Date(yearStart.getFullYear() + 1, 0, 1),
        bookings: summaryBookings,
        absences: summaryAbsences,
        holidays: summaryHolidays,
      });
      const summaryEvents: BryntumEvent[] = summaries.map((summary) => {
        const count = summary.bookings.length;
        return {
          id: summary.id,
          resourceId: summary.rowId,
          name: `${count} ${count === 1 ? 'booking' : 'bookings'}`,
          startDate: summary.monthStart,
          endDate: summary.monthEnd,
          cls: 'eb-year-summary',
          draggable: false,
          resizable: false,
          yearSummary: summary,
        };
      });
      endBuild({ events: summaryEvents.length, bookings: bookings.length });
      return summaryEvents;
    }

    const events = [...bookingEvents, ...absenceEvents];
    endBuild({ events: events.length, bookings: bookings.length });
    return events;
  }, [bookings, lockedBookingIds, groupingMode, resources, loadedRowIds, projectDetails, isHeatmapMode, selectedTimezone, enableTimezones, settings.businessHoursStart, settings.businessHoursEnd, settings.statusConfigs, settings.colorOverrides, absences, settings.absenceColor, settings.absenceLabelOverride, settings.publicHolidayColor, calendarView, safeSelectedDate, resourceTimeRanges]);

  // Keep the ref in sync every render so forceStoreRefresh reads the latest data
  bryntumEventsRef.current = bryntumEvents;

  // ── Scheduler instance key ───────────────────────────────────────────────
  // The key is deliberately STABLE across date navigation and view-preset
  // changes: a remount tears down and rebuilds the whole Bryntum instance,
  // which freezes the UI with large resource/booking sets.  Navigation, coarse
  // view switches and late-arriving data are all applied in place by the
  // store-sync effect (incremental event diff under suspendRefresh, imperative
  // viewPreset assignment, setTimeSpan, and commitAsync().then(repaint) to lay
  // out records that arrive after the span has advanced).
  //
  // Only a change that genuinely requires a different widget configuration —
  // switching between heatmap and normal mode, which swaps the event pipeline
  // and the drag/resize/dragCreate features — rebuilds the instance.
  const schedulerKey = isHeatmapMode ? `${schedulerId}-heatmap` : schedulerId;

  // On the remaining remount (heatmap mode toggle) the Bryntum instance is
  // rebuilt, so reset the one-shot refs and let the set-once logic further
  // below re-populate initialDatesRef / initialViewPresetRef with the current
  // span's dates and preset for the freshly mounted instance.
  const prevSchedulerKeyRef = useRef<string>(schedulerKey);
  if (schedulerKey !== prevSchedulerKeyRef.current) {
    prevSchedulerKeyRef.current = schedulerKey;
    hasSetInitialDates.current = false;
    initialViewPresetRef.current = null;
    lastSyncedViewRef.current = null;
    lastSyncedEventsRef.current = null;
    prevAppliedResourcesRef.current = null;
  }

  const { startDate, endDate } = useMemo<{ startDate: Date; endDate: Date }>(() => {
    let start: Date;
    let end: Date;

    // CRITICAL: Always create a fresh Date object to avoid mutation issues
    // This prevents race conditions when selectedDate changes rapidly (e.g., navigating then clicking Today)
    const validDate = new Date(safeSelectedDate.getTime());

    // In Day view, restrict the timeline to visible hours from settings
    const visibleHoursStart = settings.visibleHoursStart ?? 7;
    const visibleHoursEnd = settings.visibleHoursEnd ?? 19;

    switch (calendarView) {
      case 'day':
        start = new Date(validDate);
        start.setHours(Math.floor(visibleHoursStart), Math.round((visibleHoursStart % 1) * 60), 0, 0);
        end = new Date(validDate);
        end.setHours(Math.floor(visibleHoursEnd), Math.round((visibleHoursEnd % 1) * 60), 0, 0);
        break;
      case 'week':
        // Create new Date objects to avoid mutation issues
        start = startOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        end = endOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        break;
      case 'month':
        // CRITICAL: Create fresh Date objects for date-fns functions
        start = startOfMonth(new Date(validDate.getTime()));
        end = endOfMonth(new Date(validDate.getTime()));
        break;
      case 'quarter':
        start = startOfQuarter(new Date(validDate.getTime()));
        end = endOfQuarter(new Date(validDate.getTime()));
        break;
      case 'year':
        start = startOfYear(new Date(validDate.getTime()));
        end = endOfYear(new Date(validDate.getTime()));
        break;
      default:
        // Create new Date objects to avoid mutation issues
        start = startOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
        end = endOfWeek(new Date(validDate.getTime()), { weekStartsOn: 1 });
    }

    // CRITICAL VALIDATION: Ensure dates are valid before returning
    const isStartValid = start instanceof Date && !isNaN(start.getTime());
    const isEndValid = end instanceof Date && !isNaN(end.getTime());

    // If dates are inverted, swap them (this can happen during rapid state changes)
    if (isStartValid && isEndValid && start.getTime() >= end.getTime()) {
      console.warn('⚠︝ WARNING: Dates are inverted, swapping them:', {
        originalStart: start.toString(),
        originalEnd: end.toString(),
        calendarView,
        selectedDate: validDate.toString()
      });
      const temp = start;
      start = end;
      end = temp;
    }

    const isOrderCorrect = isStartValid && isEndValid && start.getTime() < end.getTime();

    if (!isOrderCorrect) {
      console.error('🚨 CRITICAL: Invalid date range calculated in useMemo:', {
        start: start?.toString(),
        end: end?.toString(),
        selectedDate: validDate.toString(),
        calendarView,
        startTime: start?.getTime(),
        endTime: end?.getTime(),
        isStartValid,
        isEndValid,
        isOrderCorrect,
        reason: !isStartValid ? 'Invalid start' : !isEndValid ? 'Invalid end' : 'Start >= End'
      });
      // Fallback to a valid week range using current date
      const now = new Date();
      start = startOfWeek(now, { weekStartsOn: 1 });
      end = endOfWeek(now, { weekStartsOn: 1 });
    }

    // Final safety check before returning - swap if still inverted
    if (start.getTime() >= end.getTime()) {
      console.error('🚨 CRITICAL: Dates still inverted after fallback! Swapping as last resort.');
      const temp = start;
      start = end;
      end = temp;
    }

    return { startDate: start, endDate: end };
  }, [safeSelectedDate, calendarView, settings.visibleHoursStart, settings.visibleHoursEnd]);

  settingsRef.current = settings;
  const resourceBookingIndex = useMemo(
    () => createResourceBookingIndex({ bookings, absences }),
    [bookings, absences],
  );
  const resourcePeriodUtilisation = useMemo(() => createResourcePeriodUtilisation({
    resources, bookings, absences, bankHolidays,
    bookingTypes: settings.bookingTypes,
    globalWorkingProfile: settings.globalWorkingProfile,
    startDate, endDate,
    index: resourceBookingIndex,
  }), [resources, bookings, absences, bankHolidays, settings.bookingTypes, settings.globalWorkingProfile, startDate, endDate, resourceBookingIndex]);
  const resourcePeriodUtilisationRef = useRef(resourcePeriodUtilisation);
  resourcePeriodUtilisationRef.current = resourcePeriodUtilisation;

  const utilisationRefreshKey = useMemo(() => [
    utilisationSignature({ bookings, absences, bankHolidays, startDate, endDate }),
    JSON.stringify(settings.bookingTypes?.map(type => [type.id, type.obeyUserWorkingProfileAndBankHolidays]) ?? []),
    JSON.stringify(settings.globalWorkingProfile ?? {}),
    JSON.stringify(settings.utilisationTiers ?? []),
  ].join('|'), [bookings, absences, bankHolidays, startDate, endDate, settings.bookingTypes, settings.globalWorkingProfile, settings.utilisationTiers]);

  // Set initialDatesRef ONCE with the first valid computed date range so that
  // the Bryntum scheduler mounts with correct dates. We intentionally do NOT
  // update this ref on subsequent renders — all date changes after mount are
  // handled imperatively via setTimeSpan() in effects. This prevents the
  // BryntumScheduler React wrapper from detecting date prop changes and trying
  // to apply startDate/endDate individually, which causes the "Invalid
  // start/end dates" error during large backward date jumps (e.g. clicking
  // "Today" after navigating 2-3 weeks into the future, where the new endDate
  // is before the old startDate).
  if (!hasSetInitialDates.current && startDate < endDate) {
    initialDatesRef.current = { startDate, endDate };
    hasSetInitialDates.current = true;
  }

  const viewPreset = useMemo<ViewPresetConfig>(() => {
    // an extra class is added so CSS can target the parent cell for full-cell highlighting.
    switch (calendarView) {
      case 'day':
        // Day View zoom levels:
        // Min (0): Default hourly view
        // Max (1): 15 minute view with 15-min intervals in header
        if (zoomLevel >= 1) {
          return {
            tickWidth: 30,
            shiftIncrement: 1,
            shiftUnit: 'day',
            timeResolution: {
              unit: 'minute',
              increment: 15,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'dddd, MMMM Do YYYY',
              },
              {
                unit: 'hour',
                dateFormat: 'H',
              },
              {
                unit: 'minute',
                increment: 15,
                dateFormat: 'mm',
              },
            ],
          };
        }
        return {
          base: 'hourAndDay',
          timeResolution: {
            unit: 'hour',
            increment: 1,
          },
        };
      case 'week':
        // Week View zoom levels:
        // Min (0): Daily view (current)
        // Low (1): 12 hours view
        // Med (2): 6 hours view
        // High (3): 3 hours view
        // Max (4): 1 hour view
        if (zoomLevel === 4) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 1,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 1,
                dateFormat: 'HH',
              },
            ],
          };
        } else if (zoomLevel === 3) {
          return {
            tickWidth: 50,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 3,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 3,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 2) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 6,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 6,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 1) {
          return {
            tickWidth: 80,
            shiftIncrement: 1,
            shiftUnit: 'week',
            timeResolution: {
              unit: 'hour',
              increment: 12,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 12,
                dateFormat: 'HH:mm',
              },
            ],
          };
        }
        // Default week view (zoomLevel 0) - show exactly 7 days (Monday to Sunday)
        return {
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'week',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
          headers: [
            {
              unit: 'week',
              dateFormat: 'YYYY MMMM DD',
            },
            {
              unit: 'day',
              dateFormat: 'DD ddd',
            },
          ],
        };
      case 'month':
        // Month View zoom levels (same as Week view):
        // Min (0): Daily view (default)
        // Low (1): 12 hours view
        // Med (2): 6 hours view
        // High (3): 3 hours view
        // Max (4): 1 hour view
        if (zoomLevel === 4) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 1,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 1,
                dateFormat: 'HH',
              },
            ],
          };
        } else if (zoomLevel === 3) {
          return {
            tickWidth: 50,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 3,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 3,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 2) {
          return {
            tickWidth: 60,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 6,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 6,
                dateFormat: 'HH:mm',
              },
            ],
          };
        } else if (zoomLevel === 1) {
          return {
            tickWidth: 80,
            shiftIncrement: 1,
            shiftUnit: 'month',
            timeResolution: {
              unit: 'hour',
              increment: 12,
            },
            headers: [
              {
                unit: 'day',
                dateFormat: 'ddd DD MMM',
              },
              {
                unit: 'hour',
                increment: 12,
                dateFormat: 'HH:mm',
              },
            ],
          };
        }
        // Default month view (zoomLevel 0) - show exactly the days from start to end of the current month
        return {
          tickWidth: 50,
          shiftIncrement: 1,
          shiftUnit: 'month',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'YYYY MMMM',
            },
            {
              unit: 'day',
              dateFormat: 'DD',
            },
          ],
        };
      case 'quarter':
        // Quarter view - show months as top header and weeks (W1, W2, etc.) as second header
        return {
          tickWidth: 80,
          shiftIncrement: 3,
          shiftUnit: 'month',
          timeResolution: {
            unit: 'week',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'MMMM',
            },
            {
              unit: 'week',
              renderer: (startDate: Date) => {
                // Calculate week number of the year
                const start = new Date(startDate.getFullYear(), 0, 1);
                const diff = startDate.getTime() - start.getTime();
                const oneWeek = 1000 * 60 * 60 * 24 * 7;
                const weekNum = Math.ceil((diff / oneWeek) + 1);
                return `W${weekNum}`;
              },
            },
          ],
        };
      case 'year':
        // Year view - show months (Jan, Feb, Mar, etc.) as columns
        return {
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'year',
          timeResolution: {
            unit: 'month',
            increment: 1,
          },
          headers: [
            {
              unit: 'month',
              dateFormat: 'MMM',
            },
          ],
        };
      default:
        return {
          base: 'weekAndDay',
          tickWidth: 100,
          shiftIncrement: 1,
          shiftUnit: 'week',
          timeResolution: {
            unit: 'day',
            increment: 1,
          },
        };
    }
  }, [calendarView, zoomLevel]);

  // Capture the viewPreset on first render so the <BryntumScheduler> component
  // always receives a stable initial value.  All subsequent preset changes are
  // applied imperatively inside the store-sync useEffect (within suspendRefresh)
  // to prevent Bryntum's React wrapper from calling setViewPreset unguarded and
  // wiping the event store.
  if (!initialViewPresetRef.current) {
    initialViewPresetRef.current = viewPreset;
  }

  const heatmapEvents: BryntumEvent[] = useMemo(() => {
    if (!isHeatmapMode) return [];

    const events: BryntumEvent[] = [];
    const workingProfile = settings.globalWorkingProfile ?? ({} as WorkingHoursProfile);
    const businessHoursStart = settings.businessHoursStart || 9;

    const getWorkingHoursForDay = (resource: Resource, date: Date): number =>
      getProfileWorkingHoursForDay(resource, date, workingProfile);

    const ticks: Array<{ start: Date; end: Date }> = [];
    let tickCurrent = new Date(startDate);

    while (tickCurrent < endDate) {
      const next = new Date(tickCurrent);
      switch (calendarView) {
        case 'day':
          next.setHours(next.getHours() + 1);
          break;
        case 'week':
        case 'month':
          next.setDate(next.getDate() + 1);
          break;
        case 'quarter':
          next.setDate(next.getDate() + 7);
          break;
        case 'year':
          next.setMonth(next.getMonth() + 1);
          break;
        default:
          next.setDate(next.getDate() + 1);
      }
      ticks.push({
        start: new Date(tickCurrent),
        end: next > endDate ? new Date(endDate) : next,
      });
      tickCurrent = next;
    }

    const resourceById = new Map<string, Resource>();
    const projectById = new Map<string, (typeof projectDetails)[number]>();
    const bookingsByResource = new Map<string, Booking[]>();
    const absencesByResource = new Map<string, typeof absences>();

    resources.forEach(resource => resourceById.set(String(resource.id), resource));
    projectDetails.forEach(project => projectById.set(String(project.id), project));

    const addBookingForResource = (resourceId: string | number, booking: Booking) => {
      const key = String(resourceId);
      const resourceBookings = bookingsByResource.get(key);
      if (resourceBookings) {
        resourceBookings.push(booking);
      } else {
        bookingsByResource.set(key, [booking]);
      }
    };

    bookings.forEach(booking => {
      if (booking.resourceId != null) {
        addBookingForResource(booking.resourceId, booking);
      }
      booking.resourceIds?.forEach(resourceId => {
        if (String(resourceId) !== String(booking.resourceId)) {
          addBookingForResource(resourceId, booking);
        }
      });
    });

    absences.forEach(absence => {
      const key = String(absence.resourceId);
      const resourceAbsences = absencesByResource.get(key);
      if (resourceAbsences) {
        resourceAbsences.push(absence);
      } else {
        absencesByResource.set(key, [absence]);
      }
    });

    const resourceMappings: Array<{ resource: Resource; bryntumId: string; projectFilter?: string; customerFilter?: string }> = [];

    if (groupingMode === 'project-resource') {
      resources.forEach((resource) => {
        if (String(resource.id) === UNALLOCATED_RESOURCE_ID) {
          resourceMappings.push({ resource, bryntumId: UNALLOCATED_RESOURCE_ID });
          return;
        }
        const projectIds = new Set<string>();
        (bookingsByResource.get(String(resource.id)) ?? []).forEach((b) => {
          if (String(b.resourceId) === String(resource.id) && b.projectId) {
            projectIds.add(b.projectId);
          }
        });
        if (projectIds.size === 0) {
          resourceMappings.push({ resource, bryntumId: resource.id });
        } else {
          projectIds.forEach((projectId) => {
            resourceMappings.push({
              resource,
              bryntumId: `${projectId}-${resource.id}`,
              projectFilter: projectId,
            });
          });
        }
      });
    } else if (groupingMode === 'role-resource') {
      resources.forEach((resource) => {
        if (String(resource.id) === UNALLOCATED_RESOURCE_ID) {
          resourceMappings.push({ resource, bryntumId: UNALLOCATED_RESOURCE_ID });
          return;
        }
        const roleName = resource.role || 'Unassigned';
        resourceMappings.push({ resource, bryntumId: `${roleName}-${resource.id}` });
      });
    } else if (groupingMode === 'customer') {
      // Build a map of customerName -> Set of resource IDs (same logic as the row builder)
      const customerResourceMap = new Map<string, Set<string>>();
      bookings.forEach((b) => {
        if (b.resourceId === UNALLOCATED_RESOURCE_ID) return;
        let customerName: string = b.customer?.trim() || '';
        if (!customerName && b.projectId) {
          const project = projectById.get(String(b.projectId));
          customerName = project?.clientName?.trim() || '';
        }
        if (!customerName) customerName = 'Unassigned';
        if (!customerResourceMap.has(customerName)) {
          customerResourceMap.set(customerName, new Set());
        }
        customerResourceMap.get(customerName)!.add(String(b.resourceId));
      });

      customerResourceMap.forEach((resourceIds, customerName) => {
        resourceIds.forEach((resourceId) => {
          const resource = resourceById.get(resourceId);
          if (resource) {
            resourceMappings.push({
              resource,
              bryntumId: `${customerName}-${resource.id}`,
              customerFilter: customerName,
            });
          }
        });
      });

      const unallocatedResource = resourceById.get(UNALLOCATED_RESOURCE_ID);
      if (unallocatedResource) {
        resourceMappings.push({ resource: unallocatedResource, bryntumId: UNALLOCATED_RESOURCE_ID });
      }
    } else {
      resources.forEach((resource) => {
        resourceMappings.push({ resource, bryntumId: resource.id });
      });
    }

    resourceMappings.forEach(({ resource, bryntumId, projectFilter, customerFilter }) => {
      const resIdStr = String(resource.id);
      const dayCapacityCache = new Map<string, DayCapacity>();
      const resourceBookings = (bookingsByResource.get(resIdStr) ?? []).filter((b) => {
        if (projectFilter && String(b.projectId) !== String(projectFilter)) return false;
        if (customerFilter) {
          // Derive booking customer name the same way as the row builder
          let bCustomer: string = b.customer?.trim() || '';
          if (!bCustomer && b.projectId) {
            const project = projectById.get(String(b.projectId));
            bCustomer = project?.clientName?.trim() || '';
          }
          if (!bCustomer) bCustomer = 'Unassigned';
          if (bCustomer !== customerFilter) return false;
        }
        return true;
      });

      ticks.forEach((tick, tickIndex) => {
        const {
          workingHours,
          availableHours,
          bookedHours,
          confirmedHours,
          plannedHours,
          absenceHours,
          utilisation: utilization,
          hasHardConflict,
          matchedBookings,
        } = calculateHeatmapTickUtilisation({
          resource,
          tick,
          bookings: resourceBookings,
          globalWorkingProfile: workingProfile,
          absences: absencesByResource.get(resIdStr) ?? [],
          bankHolidays,
          bookingTypes: settings.bookingTypes,
          businessHoursStart,
          dayCapacityCache,
        });

        // ── Absence / bank-holiday reasons shown in the tooltip ──────────────
        const absenceReasonSet = new Set<string>();
        (absencesByResource.get(resIdStr) ?? [])
          .forEach((abs) => {
            const absStart = new Date(abs.startDate); absStart.setHours(0, 0, 0, 0);
            const absEnd = new Date(abs.endDate); absEnd.setHours(23, 59, 59, 999);
            if (absStart >= tick.end || absEnd <= tick.start) return;
            absenceReasonSet.add(abs.reason || 'Absence');
          });
        bankHolidays.forEach((bh) => {
          const bhDate = new Date(bh.date); bhDate.setHours(0, 0, 0, 0);
          const bhEnd = new Date(bhDate); bhEnd.setHours(23, 59, 59, 999);
          if (bhDate < tick.end && bhEnd > tick.start && getWorkingHoursForDay(resource, bhDate) > 0) {
            absenceReasonSet.add(bh.name || 'Bank Holiday');
          }
        });

        let color: string;
        let band: string;
        if (workingHours === 0) {
          color = '#f5f5f5'; band = 'Non-working';
        } else {
          // bookedHours === 0 (unbooked) is handled by the early-return below,
          // so here we always have actual utilisation to colour.
          color = bookedHours === 0
            ? 'transparent'
            : getUtilizationColor(utilization, settings.utilisationTiers);
          band = bookedHours === 0
            ? 'Unbooked'
            : getUtilizationLabel(utilization, settings.utilisationTiers);
        }

        // ── Planned-only / mixed detection ────────────────────────────────
        // isPlannedOnly: has bookings but NONE are confirmed/completed.
        // isMixed:       has BOTH confirmed and planned hours in the same cell.
        // plannedRatio:  share of total booked hours that are unconfirmed (0–1).
        const isPlannedOnly = matchedBookings.length > 0 && confirmedHours === 0 && plannedHours > 0;
        const isMixed       = confirmedHours > 0 && plannedHours > 0;
        const plannedRatio  = bookedHours > 0 ? plannedHours / bookedHours : 0;

        // Forecast views: Month, Quarter, Year
        const isForecastView = calendarView === 'month' || calendarView === 'quarter' || calendarView === 'year';

        // Per-view threshold: use forecastSettings if present, fall back to legacy single value
        const fs = settings.forecastSettings;
        const warnThreshold = fs
          ? (calendarView === 'month' ? fs.monthThreshold
            : calendarView === 'quarter' ? fs.quarterThreshold
            : calendarView === 'year' ? fs.yearThreshold
            : 70)
          : (settings.plannedOnlyWarningThreshold ?? 70);
        const showPlannedPrefix = fs ? fs.showPlannedPrefix : true;

        // ── CSS class composition ──────────────────────────────────────────
        // Priority order: conflict > mixed > planned-only > confirmed
        // Forecast ticks are too narrow for glyph prefixes, so status is carried
        // entirely by the background treatments below.
        let cellCls = 'b-heatmap-event';
        if (hasHardConflict) {
          cellCls += ' b-heatmap-conflict';
        } else if (isMixed && isForecastView && workingHours > 0) {
          // Mix of confirmed + planned — show split bar, intensity based on planned ratio
          cellCls += plannedRatio > 0.5
            ? ' b-heatmap-mixed b-heatmap-mixed-majority'   // majority unconfirmed
            : ' b-heatmap-mixed';                           // minority unconfirmed
        } else if (isPlannedOnly && isForecastView && showPlannedPrefix && workingHours > 0) {
          // Fully unconfirmed — muted dashed treatment, corner flag above threshold
          cellCls += utilization >= warnThreshold
            ? ' b-heatmap-planned b-heatmap-planned-high'
            : ' b-heatmap-planned';
        }

        // ── Cell label ────────────────────────────────────────────────────
        // Forecast views (Month / Quarter / Year) show the bare percentage so a
        // 3-digit value stays readable in a ~50px tick; the ⚠ conflict glyph is
        // kept on the wider day / week ticks. Status detail lives in the tooltip.
        const cellLabel = (() => {
          if (workingHours === 0) return '';
          if (bookedHours === 0) return ''; // empty day — no label needed
          const pct = `${Math.round(utilization)}%`;
          if (hasHardConflict && !isForecastView) return `⚠ ${pct}`;
          return pct;
        })();

        // Skip creating an event for non-working day ticks, and for ticks whose
        // capacity has been fully removed by bank holidays / absences — they have
        // no available hours and should appear as empty (no overlay / label).
        if (workingHours === 0 || availableHours === 0) return;

        // Skip creating an event for genuinely unbooked ticks — transparent
        // Bryntum event nodes still render visible borders/outlines even with
        // color = 'transparent', producing stray lines on otherwise empty rows.
        if (bookedHours === 0) return;

        events.push({
          id: `heatmap-${bryntumId}-${tickIndex}`,
          resourceId: bryntumId,
          name: cellLabel,
          startDate: tick.start,
          endDate: tick.end,
          eventColor: color,
          cls: cellCls,
          // Band colour wins over Bryntum's own hover/state background rules.
          style: `${DARK_TEXT_STYLE} background-color: ${color} !important;`,
          isHeatmapEvent: true,
          heatmapData: {
            bookedHours: Math.round(bookedHours * 10) / 10,
            availableHours: Math.round(availableHours * 10) / 10,
            utilization: Math.round(utilization),
            band,
            bandColor: color,
            bookings: matchedBookings,
            confirmedHours: Math.round(confirmedHours * 10) / 10,
            plannedHours: Math.round(plannedHours * 10) / 10,
            absenceHours: Math.round(absenceHours * 10) / 10,
            absenceReasons: Array.from(absenceReasonSet),
            hasHardConflict,
            weeklyAvgUtilization: null, // populated in second pass below
            isPlannedOnly,
            isMixed,
            plannedRatio: Math.round(plannedRatio * 100) / 100,
            isCustomProfile: !!(resource.customWorkingProfile && Object.keys(resource.customWorkingProfile).length > 0),
            dailyHours: getWorkingHoursForDay(resource, tick.start),
          },
        });
      });
    });

    // ── Second pass: compute weekly average utilization per resource per week ──
    // Key: `${resourceId}::${ISO-week-string}` e.g. "res-1::2026-W16"
    // This means every event gets the avg for *its own* calendar week, regardless
    // of what week the view is anchored to — fixing the bug where events outside
    // startOfWeek(viewStart) got weeklyAvgUtilization = null.
    const weekBuckets = new Map<string, { sum: number; count: number }>();

    events.forEach((ev) => {
      if (!ev.heatmapData || ev.heatmapData.availableHours === 0) return;
      // Derive ISO week key from the event's own start date
      const evWeekStart = startOfWeek(ev.startDate as Date, { weekStartsOn: 1 });
      const isoKey = format(evWeekStart, 'yyyy-MM-dd');           // e.g. "2026-04-14" (Monday)
      const bucketKey = `${ev.resourceId}::${isoKey}`;
      const bucket = weekBuckets.get(bucketKey) ?? { sum: 0, count: 0 };
      bucket.sum += ev.heatmapData.utilization;
      bucket.count += 1;
      weekBuckets.set(bucketKey, bucket);
    });

    // Stamp the correct week's average onto every event
    events.forEach((ev) => {
      if (!ev.heatmapData) return;
      const evWeekStart = startOfWeek(ev.startDate as Date, { weekStartsOn: 1 });
      const isoKey = format(evWeekStart, 'yyyy-MM-dd');
      const bucketKey = `${ev.resourceId}::${isoKey}`;
      const bucket = weekBuckets.get(bucketKey);
      ev.heatmapData.weeklyAvgUtilization = bucket
        ? Math.round(bucket.sum / bucket.count)
        : null;
    });

    return events;
  }, [isHeatmapMode, startDate, endDate, calendarView, resources, bookings, absences, bankHolidays, groupingMode, settings]);

  const initialDataRef = useRef({
    schedulerKey,
    resources: pagedResources,
    events: isHeatmapMode ? heatmapEvents : bryntumEvents,
    resourceTimeRanges,
    timeRanges,
  });
  if (initialDataRef.current.schedulerKey !== schedulerKey) {
    initialDataRef.current = {
      schedulerKey,
      resources: pagedResources,
      events: isHeatmapMode ? heatmapEvents : bryntumEvents,
      resourceTimeRanges,
      timeRanges,
    };
  }

  const findResourceById = useCallback((id: string): Resource | undefined => {
    // First try direct match
    let resource = resources.find((r: Resource) => String(r.id) === String(id));

    // In project-resource mode, the ID might be a combined "projectId-resourceId"
    // Try to extract the actual resource ID
    if (!resource && groupingMode === 'project-resource' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('project-')) {
      const parts = id.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (project ID)
        const actualResourceId = parts.slice(1).join('-');
        resource = resources.find((r: Resource) => String(r.id) === String(actualResourceId));
      }
    }

    // In role-resource mode, the ID might be a combined "roleName-resourceId"
    // Try to extract the actual resource ID
    if (!resource && groupingMode === 'role-resource' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('role-')) {
      const parts = id.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (role name)
        const actualResourceId = parts.slice(1).join('-');
        resource = resources.find((r: Resource) => String(r.id) === String(actualResourceId));
      }
    }

    // In customer mode, the ID might be a combined "customerName-resourceId"
    // Try to match the actual resource by finding one whose ID the composite ends with
    if (!resource && groupingMode === 'customer' && id.includes('-') && !id.startsWith('group-') && !id.startsWith('customer-')) {
      // Find any resource whose id appears at the end of the composite ID after a hyphen
      resource = resources.find((r: Resource) => {
        const rid = String(r.id);
        return id === `${id.slice(0, id.length - rid.length - 1)}-${rid}` && id.endsWith(`-${rid}`);
      });
      if (!resource) {
        // Fallback: take everything after the first hyphen
        const dashIdx = id.indexOf('-');
        if (dashIdx !== -1) {
          const actualResourceId = id.slice(dashIdx + 1);
          resource = resources.find((r: Resource) => String(r.id) === actualResourceId);
        }
      }
    }

    return resource;
  }, [resources, groupingMode]);

  // Extract the customer name from a customer-mode composite row id
  // ("customerName-resourceId"). Customer names may contain hyphens, so strip
  // the real resource id by suffix match. Returns undefined outside customer
  // mode, for group rows, or for the 'Unassigned' bucket.
  const extractCustomerName = useCallback((combinedId: string): string | undefined => {
    if (groupingMode !== 'customer') return undefined;
    if (!combinedId.includes('-') || combinedId.startsWith('group-') || combinedId.startsWith('customer-')) {
      return undefined;
    }
    const matched = resources.find((r: Resource) => combinedId.endsWith(`-${String(r.id)}`));
    const realId = matched ? String(matched.id) : combinedId.slice(combinedId.indexOf('-') + 1);
    const name = combinedId.slice(0, combinedId.length - realId.length - 1);
    if (!name || name === 'Unassigned') return undefined;
    return name;
  }, [resources, groupingMode]);

  const findBookingById = useCallback((id: string): Booking | undefined => {
    // Strip multi-resource display suffix if present (e.g., "123__mr__1" → "123")
    const baseId = id.includes('__mr__') ? id.split('__mr__')[0] : id;
    return bookings.find((b: Booking) => String(b.id) === String(baseId));
  }, [bookings]);

  // The resource a single tile of a multi-resource booking stands for. Drag and
  // resize move that one tile, so its own resource — not the booking's primary
  // one — is the resource the gesture starts from; using the primary resource
  // instead makes a move look like a swap away from it and drops it from the
  // booking's assignments. Single-resource bookings return undefined and keep
  // their existing behaviour.
  const tileResourceIdFor = useCallback((
    eventData: Pick<BryntumEvent, 'id' | 'tileResourceId'>,
    booking: Booking,
  ): string | undefined => {
    if (!booking.resourceIds || booking.resourceIds.length <= 1) return undefined;
    if (eventData.tileResourceId) return String(eventData.tileResourceId);

    const siblingIndex = String(eventData.id).match(/__mr__(\d+)$/)?.[1];
    const resourceId = booking.resourceIds[siblingIndex === undefined ? 0 : Number(siblingIndex)];
    return resourceId == null ? undefined : String(resourceId);
  }, []);



  // ─── Heatmap rich tooltip ────────────────────────────────────────────────────
  const generateHeatmapTooltipHtml = useCallback((hData: NonNullable<BryntumEvent['heatmapData']>, tickStart: Date): string => {
    // Don't show a tooltip at all when there are no bookings on this cell
    if (hData.bookings.length === 0) return '';

    // Determine if bandColor is dark so we pick a contrasting text colour
    const hexToRgb = (hex: string) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return { r, g, b };
    };
    const rgb = hexToRgb(hData.bandColor || '#6b7280');
    const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
    const headerTextColor = luminance > 0.55 ? '#1f2937' : '#ffffff';

    // Forecast view context for tooltip
    const isForecastViewTooltip = calendarView === 'month' || calendarView === 'quarter' || calendarView === 'year';
    const fsTooltip = settings.forecastSettings;
    const warnThresholdTooltip = fsTooltip
      ? (calendarView === 'month' ? fsTooltip.monthThreshold
        : calendarView === 'quarter' ? fsTooltip.quarterThreshold
        : calendarView === 'year' ? fsTooltip.yearThreshold
        : 70)
      : (settings.plannedOnlyWarningThreshold ?? 70);
    const showSplitTooltip = fsTooltip ? fsTooltip.showConfirmedPlannedSplit : true;

    // Format helpers
    const fmtTime = (iso: string) => {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const fmtDuration = (startIso: string, endIso: string, durationType: string, halfDayPeriod?: string): string => {
      if (durationType === 'full-day') return 'Full day';
      if (durationType === 'half-day-am') return 'Half day (AM)';
      if (durationType === 'half-day-pm') return 'Half day (PM)';
      if (durationType === 'half-day') return halfDayPeriod === 'pm' ? 'Half day (PM)' : 'Half day (AM)';
      const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
      const totalMins = Math.round(ms / 60000);
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      if (h === 0) return `${m}m`;
      if (m === 0) return `${h}h`;
      return `${h}h ${m}m`;
    };
    const capitalize = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    const statusDot = (status: string): string => {
      const sc = settings.statusConfigs?.find(s => String(s.id) === status?.toLowerCase());
      const c = sc?.color || getDefaultStatusColor(status);
      return `<span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${c};margin-right:4px;flex-shrink:0;margin-top:2px;"></span>`;
    };

    // Dynamic colours for confirmed/planned split visualisations
    const confirmedColor = settings.statusConfigs?.find(s => String(s.id) === 'confirmed')?.color || getDefaultStatusColor('confirmed');
    const plannedColor = settings.statusConfigs?.find(s => String(s.id) === 'planned')?.color || getDefaultStatusColor('planned');

    const clockSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`;
    const tagSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>`;
    const briefcaseSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/></svg>`;
    const pinSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;margin-top:1px;"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>`;

    // ── Confirmed / Planned split label ──────────────────────────────────────
    const confirmedPlannedDetail = (() => {
      // Respect the showConfirmedPlannedSplit toggle on all views
      if (!showSplitTooltip) return '';
      const parts: string[] = [];
      if (hData.confirmedHours > 0) {
        parts.push(`<span style="display:inline-flex;align-items:center;gap:3px;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${confirmedColor};flex-shrink:0;"></span><span>${hData.confirmedHours}h confirmed</span></span>`);
      }
      if (hData.plannedHours > 0) {
        parts.push(`<span style="display:inline-flex;align-items:center;gap:3px;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${plannedColor};flex-shrink:0;"></span><span>${hData.plannedHours}h planned</span></span>`);
      }
      if (parts.length === 0) return '';
      return `<div style="font-size:11px;color:#6b7280;padding:5px 12px 2px;display:flex;gap:10px;flex-wrap:wrap;">${parts.join('')}</div>`;
    })();

    // ── Forecast banner (Month/Quarter/Year views) ────────────────────────────
    // Covers three states: fully planned-only, mixed-majority-planned, mixed-minority-planned
    const forecastBanner = (() => {
      if (!isForecastViewTooltip || hData.bookedHours === 0) return '';

      const { isPlannedOnly, isMixed, plannedRatio, utilization } = hData;
      if (!isPlannedOnly && !isMixed) return ''; // fully confirmed — no banner needed

      // ── Determine banner tier ──────────────────────────────────────────
      // Tier A: fully planned-only, high risk (≥ threshold)
      // Tier B: fully planned-only, low risk (< threshold)
      // Tier C: mixed, majority planned (>50%)
      // Tier D: mixed, minority planned (≤50%)
      type BannerTier = 'A' | 'B' | 'C' | 'D';
      const tier: BannerTier = isPlannedOnly
        ? (utilization >= warnThresholdTooltip ? 'A' : 'B')
        : (plannedRatio > 0.5 ? 'C' : 'D');

      const plannedPct = Math.round((plannedRatio ?? 0) * 100);
      const confirmedPct = 100 - plannedPct;

      const config: Record<BannerTier, { bg: string; border: string; text: string; iconColor: string; label: string; icon: 'warn' | 'info' }> = {
        A: {
          bg: '#fef3c7', border: '#fde68a', text: '#92400e', iconColor: '#d97706',
          label: `Forecast — all ${hData.bookedHours}h unconfirmed at ${utilization}% utilisation`,
          icon: 'warn',
        },
        B: {
          bg: '#eff6ff', border: '#bfdbfe', text: '#1e40af', iconColor: '#3b82f6',
          label: `Forecast — all bookings are planned (unconfirmed)`,
          icon: 'info',
        },
        C: {
          bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', iconColor: '#ea580c',
          label: showSplitTooltip
            ? `Forecast — ${plannedPct}% planned, ${confirmedPct}% confirmed`
            : `Forecast — mostly planned (unconfirmed)`,
          icon: 'warn',
        },
        D: {
          bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', iconColor: '#16a34a',
          label: showSplitTooltip
            ? `Mixed — ${confirmedPct}% confirmed, ${plannedPct}% planned`
            : `Mixed — confirmed and planned`,
          icon: 'info',
        },
      };

      const { bg, border, text, iconColor, label, icon } = config[tier];
      const svgIcon = icon === 'warn'
        ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`
        : `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${iconColor}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`;

      // Split ratio bar — shows confirmed / planned proportion visually
      const splitBar = (isMixed && showSplitTooltip)
        ? `<div style="margin-top:5px;height:4px;border-radius:2px;overflow:hidden;background:#e5e7eb;display:flex;">
             <div style="width:${confirmedPct}%;background:${confirmedColor};"></div>
             <div style="width:${plannedPct}%;background:${plannedColor};"></div>
           </div>
           <div style="display:flex;justify-content:space-between;margin-top:2px;">
             <span style="font-size:10px;color:#6b7280;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${confirmedColor};margin-right:3px;"></span>${hData.confirmedHours}h confirmed</span>
             <span style="font-size:10px;color:#6b7280;"><span style="display:inline-block;width:7px;height:7px;border-radius:50%;background:${plannedColor};margin-right:3px;"></span>${hData.plannedHours}h planned</span>
           </div>`
        : '';

      return `<div style="padding:6px 12px ${isMixed ? '8px' : '6px'};background:${bg};border-bottom:1px solid ${border};">
        <div style="display:flex;align-items:center;gap:6px;">
          ${svgIcon}
          <span style="font-size:11px;color:${text};font-weight:500;">${label}</span>
        </div>
        ${splitBar}
      </div>`;
    })();

    // ── Absence / leave row ───────────────────────────────────────────────────
    const absenceRow = hData.absenceHours > 0
      ? `<div style="display:flex;align-items:center;gap:6px;padding:5px 12px;background:#fef3c7;border-top:1px solid #fde68a;border-bottom:1px solid #fde68a;">
           <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
           <span style="font-size:11px;color:#92400e;font-weight:500;">${hData.absenceHours}h absence${hData.absenceReasons.length ? ` — ${escapeHtml(hData.absenceReasons.join(', '))}` : ''}</span>
         </div>`
      : '';

    // ── Conflict banner ───────────────────────────────────────────────────────
    // One booking (the anchor) stays with the original resource; only the
    // surplus bookings are handed to the Batch Assignment modal to reassign.
    const { surplus: surplusBookings } = partitionConflictBookings(hData.bookings);
    const allConflictIds = surplusBookings.map(b => b.id).join(',');
    const conflictCount = surplusBookings.length;
    const conflictBanner = hData.hasHardConflict
      ? `<div style="display:flex;align-items:center;gap:6px;padding:6px 12px;background:#fffbeb;border-bottom:1px solid #fde68a;">
           <div style="display:flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;background:#f59e0b;flex-shrink:0;">
             <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
           </div>
           <div style="flex:1;min-width:0;">
             <div style="font-size:11px;color:#92400e;font-weight:700;line-height:1.3;">Scheduling Conflict</div>
           </div>
           <button
             data-heatmap-resolve-bookings="${allConflictIds}"
             style="flex-shrink:0;display:inline-flex;align-items:center;gap:3px;padding:3px 9px;font-size:10px;font-weight:700;color:#ffffff;background:#f59e0b;border:1px solid #d97706;border-radius:4px;cursor:pointer;white-space:nowrap;line-height:1.6;"
             onmouseover="this.style.background='#d97706'"
             onmouseout="this.style.background='#f59e0b'"
           >
             <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
             Resolve ${conflictCount > 1 ? `(${conflictCount})` : ''}
           </button>
         </div>`
      : '';

    // ── Weekly context bar ────────────────────────────────────────────────────
    const weeklyBar = hData.weeklyAvgUtilization !== null
      ? (() => {
          const pct = Math.min(hData.weeklyAvgUtilization, 100);
          const barColor = pct > 100 ? '#fecaca' : pct >= 91 ? '#fde68a' : pct >= 71 ? '#bbf7d0' : '#bfdbfe';
          return `
            <div style="padding:6px 12px 7px;border-top:1px solid rgba(0,0,0,0.06);background:#f9fafb;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
                <span style="font-size:10px;font-weight:600;color:#9ca3af;letter-spacing:0.04em;text-transform:uppercase;">Avg utilisation this week</span>
                <span style="font-size:11px;font-weight:700;color:rgba(0,0,0,0.75);">${hData.weeklyAvgUtilization}%</span>
              </div>
              <div style="height:4px;background:#e5e7eb;border-radius:2px;overflow:hidden;">
                <div style="height:100%;width:${pct}%;background:${barColor};border-radius:2px;transition:width 0.2s;"></div>
              </div>
            </div>`;
        })()
      : '';

    // ── Summary stats strip ───────────────────────────────────────────────────
    // Use the reconciled total (confirmedHours + plannedHours) so the numbers
    // are always internally consistent. Label the middle column contextually:
    //   • Planned only  → "Planned"   (no confirmed hours at all)
    //   • Mixed         → "Total"     (confirmed + planned combined)
    //   • Confirmed     → "Booked"    (all confirmed, classic label)
    const totalHours   = Math.round((hData.confirmedHours + hData.plannedHours) * 10) / 10;
    const bookedLabel  = hData.isPlannedOnly ? 'Planned' : hData.isMixed ? 'Total' : 'Booked';
    const bookedValue  = hData.isPlannedOnly ? hData.plannedHours : hData.isMixed ? totalHours : hData.confirmedHours;
    const remainingVal = Math.round((hData.availableHours - totalHours) * 10) / 10;

    const summaryStrip = `
      <div style="display:flex;gap:0;border-top:1px solid rgba(0,0,0,0.08);border-bottom:1px solid rgba(0,0,0,0.08);background:#f9fafb;">
        <div style="flex:1;text-align:center;padding:6px 4px;border-right:1px solid rgba(0,0,0,0.08);">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">Available</div>
          <div style="font-size:13px;font-weight:600;color:#1f2937;">${hData.availableHours}h</div>
        </div>
        <div style="flex:1;text-align:center;padding:6px 4px;border-right:1px solid rgba(0,0,0,0.08);">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">${bookedLabel}</div>
          <div style="font-size:13px;font-weight:600;color:#1f2937;">${bookedValue}h</div>
        </div>
        <div style="flex:1;text-align:center;padding:6px 4px;">
          <div style="font-size:11px;color:#6b7280;margin-bottom:1px;">${remainingVal < 0 ? 'Overbooked' : 'Remaining'}</div>
          <div style="font-size:13px;font-weight:600;color:${remainingVal < 0 ? '#b91c1c' : '#1f2937'};">${remainingVal < 0 ? `+${Math.abs(remainingVal)}h` : `${remainingVal}h`}</div>
        </div>
      </div>
      ${forecastBanner ? '' : confirmedPlannedDetail}`;

    // Booking rows
    let bookingRows = '';
    if (hData.bookings.length === 0) {
      bookingRows = `<div style="padding:10px 12px;font-size:12px;color:#9ca3af;font-style:italic;">No bookings</div>`;
    } else {
      hData.bookings.forEach((b, idx) => {
        const isLast = idx === hData.bookings.length - 1;
        const isHalfDayAm = b.durationType === 'half-day-am' || (b.durationType === 'half-day' && b.halfDayPeriod !== 'pm');
        const isHalfDayPm = b.durationType === 'half-day-pm' || (b.durationType === 'half-day' && b.halfDayPeriod === 'pm');
        const timeStr = (b.durationType === 'full-day')
          ? 'All day'
          : isHalfDayAm
            ? `${fmtTime(b.startTime)} – midday (AM)`
            : isHalfDayPm
              ? `Midday – ${fmtTime(b.endTime)} (PM)`
              : `${fmtTime(b.startTime)} – ${fmtTime(b.endTime)}`;
        const duration = fmtDuration(b.startTime, b.endTime, b.durationType, b.halfDayPeriod);

        bookingRows += `
          <div style="padding:8px 12px;${isLast ? '' : 'border-bottom:1px solid #f3f4f6;'}display:flex;align-items:center;gap:8px;">
            <!-- Project + status -->
            <div style="flex:1;min-width:0;">
              <div style="font-size:12px;font-weight:600;color:#111827;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(b.projectName || b.description || 'Booking')}</div>
              <div style="display:flex;align-items:center;gap:4px;margin-top:1px;">
                ${statusDot(b.status)}<span style="font-size:10px;color:#6b7280;">${capitalize(b.status || '')}</span>
                <span style="font-size:10px;color:#d1d5db;">·</span>
                <span style="font-size:10px;color:#6b7280;">${timeStr}</span>
              </div>
            </div>
            <!-- Edit button -->
            <button
              data-heatmap-edit-booking="${b.id}"
              style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;font-size:10px;font-weight:500;color:#3b82f6;background:#eff6ff;border:1px solid #bfdbfe;border-radius:4px;cursor:pointer;white-space:nowrap;flex-shrink:0;"
              onmouseover="this.style.background='#dbeafe'"
              onmouseout="this.style.background='#eff6ff'"
            >
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              Edit
            </button>
          </div>`;
      });
    }

    const dateLabel = format(tickStart, calendarView === 'week' || calendarView === 'month' ? 'EEE d MMM yyyy' : calendarView === 'day' ? 'EEE d MMM yyyy, HH:mm' : 'd MMM yyyy');

    return `
      <div style="width:300px;max-width:min(300px,calc(100vw - 32px));font-family:inherit;border-radius:8px;overflow:hidden;box-shadow:0 4px 20px rgba(0,0,0,0.15);display:flex;flex-direction:column;">
        <!-- Header -->
        <div style="background:${hData.bandColor};padding:10px 12px;">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:2px;">
            <div style="display:flex;align-items:center;gap:5px;">
              ${hData.hasHardConflict ? `<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="${headerTextColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>` : ''}
              <span style="font-size:13px;font-weight:700;color:${headerTextColor};">${hData.band}</span>
            </div>
            <span style="font-size:13px;font-weight:700;color:${headerTextColor};">${hData.utilization}%</span>
          </div>
          <div style="font-size:11px;color:${headerTextColor};opacity:0.85;">${dateLabel} &nbsp;·&nbsp; ${hData.bookedHours}h booked</div>
        </div>
        <!-- Working profile indicator -->
        <div style="display:flex;align-items:center;justify-content:space-between;padding:5px 12px;border-bottom:1px solid rgba(0,0,0,0.06);background:#f9fafb;">
          <span style="font-size:10px;color:#9ca3af;">${hData.isCustomProfile ? 'Custom Profile' : 'Standard Profile'}</span>
          <span style="font-size:10px;font-weight:600;color:#374151;">${hData.dailyHours}h/day</span>
        </div>
        <!-- Forecast / planned-only banner (blue = low risk, amber = high risk) -->
        ${forecastBanner}
        <!-- Summary strip -->
        ${summaryStrip}
        <!-- Absence row (amber) -->
        ${absenceRow}
        <!-- Conflict banner (red) -->
        ${conflictBanner}
        <!-- Bookings -->
        <div style="flex:1 1 auto;min-height:0;max-height:340px;overflow-y:auto;background:#ffffff;">
          ${hData.bookings.length > 0 ? `<div style="padding:7px 12px 3px;font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:0.06em;text-transform:uppercase;display:flex;align-items:center;gap:5px;"><span style="display:inline-flex;align-items:center;justify-content:center;min-width:16px;height:16px;border-radius:8px;background:#e5e7eb;color:#6b7280;font-size:10px;font-weight:700;padding:0 4px;">${hData.bookings.length}</span>${hData.bookings.length !== 1 ? 'Bookings' : 'Booking'}</div>` : ''}
          ${bookingRows}
        </div>
        <!-- Weekly context bar -->
        ${weeklyBar}
      </div>`;
  }, [calendarView, settings.statusConfigs]);
  // ─────────────────────────────────────────────────────────────────────────────

  const generateTooltipHtml = useCallback((booking: Booking): string => {
    const bookingType = settings.bookingTypes.find((bt: BookingType) => String(bt.id) === String(booking.bookingTypeId));

    let fieldConfigurations: FieldConfiguration[] = [];
    if (bookingType?.fieldConfigurations) {
      try {
        fieldConfigurations = typeof bookingType.fieldConfigurations === 'string'
          ? JSON.parse(bookingType.fieldConfigurations)
          : bookingType.fieldConfigurations || [];
      } catch (error) {
        console.error('Error parsing field configurations:', error);
      }
    }

    const getFieldValue = (fieldName: string): string | null =>
      getTooltipFieldValue(booking, fieldName, { bookingType, projectDetails, clients, costCentres });

    const header = booking.description || booking.projectName || 'Booking';

    const startTime = booking.startTime ? new Date(booking.startTime) : null;
    const endTime = booking.endTime ? new Date(booking.endTime) : null;
    const isValidStartTime = startTime && isValid(startTime);
    const isValidEndTime = endTime && isValid(endTime);

    // The From/To dates follow the timezone currently selected on the planner
    // header (shift by the offset between the booking's timezone and the header
    // timezone), while the timezone line below reports the booking's own timezone.
    // Use the booking's start date so DST is calculated for the correct date.
    const bookingTzTooltip = (booking as any).timezone || settings.timelineDefaultTimezone || 'UTC';
    const targetTzTooltip = selectedTimezone || bookingTzTooltip;
    const tooltipOffsetMin = enableTimezones
      ? getTimezoneOffset(bookingTzTooltip, targetTzTooltip, isValidStartTime ? startTime as Date : new Date())
      : 0;
    const tooltipStart = isValidStartTime
      ? new Date((startTime as Date).getTime() + tooltipOffsetMin * 60000)
      : null;
    const tooltipEnd = isValidEndTime
      ? new Date((endTime as Date).getTime() + tooltipOffsetMin * 60000)
      : null;
    // Half-day bookings use their actual stored start/end times for tooltip display
    // Morning = 00:00–11:59, Afternoon = 12:00–23:59 — no business hours override

    const statusConfig = settings.statusConfigs?.find((s) => String(s.id) === String(booking.status));
    const statusColor = statusConfig?.color || getDefaultStatusColor(booking.status);
    const statusText = statusConfig?.customText || statusConfig?.text || booking.status;

    // Helper to check if a specific field has showTooltip enabled (case-insensitive)
    const isFieldOnTooltip = (name: string): boolean => isFieldOnTooltipShared(fieldConfigurations, name);
    // Helper to get the display label (customName from booking type screen, or default fieldName)
    const getFieldLabel = (name: string): string => getTooltipFieldLabel(fieldConfigurations, name);

    const showStatus = isFieldOnTooltip('Status');
    const showFrom = isFieldOnTooltip('From');
    const showTo = isFieldOnTooltip('To');

    const tooltipFields = getDynamicTooltipFields(fieldConfigurations);

    // ── Booking type indicator ──
    const durationLabel = (() => {
      switch (booking.durationType) {
        case 'full-day': return '◼ Full Day';
        case 'half-day':
          return booking.halfDayPeriod === 'pm' ? '◑ Afternoon' : '◝ Morning';
        case 'half-day-am': return '◝ Morning';
        case 'half-day-pm': return '◑ Afternoon';
        case 'hourly': return '❰ Hourly';
        default: return '◼ Full Day';
      }
    })();

    // ── Recurrence info ──
    // Detect recurring via recurrenceGroupId (child instances may not carry recurrence themselves).
    // Try three sources: (1) inline field, (2) sibling in current view, (3) API-fetched cache.
    const isRecurring = !!booking.recurrenceGroupId;
    const recurrence = booking.recurrence
      || (isRecurring
        ? bookings.find((b: Booking) => b.recurrenceGroupId === booking.recurrenceGroupId && b.recurrence && b.recurrence.pattern !== 'none')?.recurrence
        : undefined)
      || (isRecurring && booking.recurrenceGroupId
        ? recurrenceSettingsCacheRef.current.get(booking.recurrenceGroupId)
        : undefined);
    const repeatSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/></svg>`;
    const isFromDemand = isDemandBooking(booking);
    const layersSvg = `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`;
    const repeatTypeLabel = recurrence && recurrence.pattern !== 'none'
      ? recurrence.pattern.charAt(0).toUpperCase() + recurrence.pattern.slice(1)
      : '';

    let html = `<div style="padding: 6px 8px; max-width: 340px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">`;

    // ── Header section ──
    html += `<div style="font-weight: 700; font-size: 14px; color: #1f2937; line-height: 1.3;">${escapeHtml(header)}</div>`;
    // Sub-header: duration indicator + status + repeat
    html += `<div style="display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 6px;">`;
    html += `<span style="font-size: 11px; color: #6b7280;">${durationLabel}</span>`;
    if (showStatus && statusText) {
      html += `<span style="display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 9999px; font-size: 10px; font-weight: 500; background-color: ${statusColor}15; color: ${statusColor}; border: 1px solid ${statusColor}30;">`;
      html += `<span style="width: 5px; height: 5px; border-radius: 50%; background-color: ${statusColor};"></span>`;
      html += `${statusText}`;
      html += `</span>`;
    }
    if (isRecurring) {
      html += `<span style="display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: #6b7280;">${repeatSvg} ${repeatTypeLabel || 'Repeat'}</span>`;
    }
    if (isFromDemand) {
      html += `<span style="display: inline-flex; align-items: center; gap: 3px; font-size: 11px; color: #6b7280;">${layersSvg} Open Demand</span>`;
    }
    html += `</div>`;

    // ── Light horizontal ruler ──
    html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 6px 0;" />`;

    // ── Content section: dates ──
    // Times are always shown in 24-hour format (e.g. "09:00", "17:30")
    const isFullDay = booking.durationType === 'full-day';
    const isPlanPreviewBooking = !!(booking as any).isPlanPreview;
    const fmtTooltipTime = (d: Date) => format(d, 'HH:mm');
    // Full-day bookings store an exclusive end (e.g. next-day 00:00), so the raw
    // end date can read one day past the booking. Show the inclusive last day
    // by stepping back one millisecond before formatting the date.
    const fullDayEndDate = tooltipEnd && isValid(tooltipEnd)
      ? new Date(tooltipEnd.getTime() - 1)
      : tooltipEnd;
    // Cross-day hourly bookings display their To one second earlier; every
    // computed value below keeps using tooltipEnd.
    const tooltipEndDisplay = isCrossDayHourly(booking.durationType, tooltipStart, tooltipEnd)
      ? oneSecondEarlier(tooltipEnd as Date)
      : tooltipEnd;

    if ((showFrom || showTo) && tooltipStart && isValid(tooltipStart)) {
      html += `<div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b7280;">`;
      if (isPlanPreviewBooking) {
        if (showFrom) {
          // A proposed day covers only the hours the planner found free, so show
          // that window rather than claiming a full day.
          const hasWindow = !isFullDay && tooltipEnd && isValid(tooltipEnd) && tooltipEnd > tooltipStart;
          const previewHours = hasWindow
            ? (tooltipEnd!.getTime() - tooltipStart.getTime()) / (1000 * 60 * 60)
            : 0;
          const previewDuration = hasWindow
            ? `${format(tooltipStart, 'HH:mm')} – ${format(tooltipEnd!, 'HH:mm')} (${previewHours % 1 === 0 ? previewHours : previewHours.toFixed(1)}h)`
            : 'Full day';

          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">${getFieldLabel('From')}:</span>`;
          html += `<span>${format(tooltipStart, 'EEE, MMM d, yyyy')}</span>`;
          html += `</div>`;
          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">Duration:</span>`;
          html += `<span>${previewDuration}</span>`;
          html += `</div>`;
        }
      } else {
        if (showFrom) {
          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">${getFieldLabel('From')}:</span>`;
          html += `<span>${format(tooltipStart, 'EEE, MMM d, yyyy')}${isFullDay ? '' : ` at ${fmtTooltipTime(tooltipStart)}`}</span>`;
          html += `</div>`;
        }
        if (showTo && tooltipEnd && isValid(tooltipEnd)) {
          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="color: #9ca3af;">${getFieldLabel('To')}:</span>`;
          html += `<span>${isFullDay ? format(fullDayEndDate as Date, 'EEE, MMM d, yyyy') : `${format(tooltipEndDisplay as Date, 'EEE, MMM d, yyyy')} at ${fmtTooltipTime(tooltipEndDisplay as Date)}`}</span>`;
          html += `</div>`;
        }
      }
      // ── Timezone line ── Shown below the To date when the timezone feature is
      // enabled and the booking has a timezone set. Reflects the booking's own
      // timezone, not the timezone currently selected on the planner header
      // (the header timezone is what the From/To times above are displayed in).
      if (enableTimezones && booking.timezone) {
        const tzEntry = TIMEZONES.find((tz) => tz.value === bookingTzTooltip);
        const tzLabel = tzEntry ? tzEntry.label : bookingTzTooltip;
        const tzAbbr = getTimezoneAbbreviation(
          bookingTzTooltip,
          isValidStartTime ? startTime as Date : new Date()
        );
        html += `<div style="display: flex; align-items: center; gap: 6px;">`;
        html += `<span style="color: #9ca3af;">Timezone:</span>`;
        html += `<span>${escapeHtml(tzLabel)}${tzAbbr ? ` (${escapeHtml(tzAbbr)})` : ''}</span>`;
        html += `</div>`;
      }
      html += `</div>`;
    }

    // ── Total hours for hourly bookings — only shown when From/To are on tooltip ──
    if ((showFrom || showTo) && booking.durationType === 'hourly' && tooltipStart && isValid(tooltipStart) && tooltipEnd && isValid(tooltipEnd)) {
      const totalHours = computeHourlyBookingTotal(tooltipStart, tooltipEnd, booking.durationHours);
      if (totalHours > 0) {
        const displayHours = totalHours % 1 === 0 ? totalHours.toString() : totalHours.toFixed(1);
        const hoursLabel = Number(displayHours) === 1 ? 'hour' : 'hours';
        html += `<div style="margin: 8px 0;">`;
        html += `<div style="display: flex; align-items: center; gap: 6px; font-size: 12px; color: #6b7280;">`;
        html += `<span>❰</span>`;
        html += `<span>${displayHours} ${hoursLabel}</span>`;
        html += `</div>`;
        html += `</div>`;
      }
    }

    // ── Recurrence occurrence line ──
    if (isRecurring) {
      html += `<div style="display: flex; align-items: center; gap: 4px; font-size: 11px; color: #6b7280; margin-top: 6px;">`;
      html += `<span style="display: inline-flex; align-items: center;">${repeatSvg}</span>`;
      if (recurrence && recurrence.pattern !== 'none') {
        const intervalNum = recurrence.interval || 1;
        const capitalizeWord = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
        let patternText = '';
        if (recurrence.pattern === 'daily') {
          const label = intervalNum === 1 ? 'day' : `${intervalNum} days`;
          patternText = `Occurs every ${label}`;
        } else if (recurrence.pattern === 'weekly') {
          const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          const dayNames = recurrence.daysOfWeek && recurrence.daysOfWeek.length > 0
            ? [...recurrence.daysOfWeek]
                .sort((a, b) => dayOrder.indexOf(a.toLowerCase()) - dayOrder.indexOf(b.toLowerCase()))
                .map(d => capitalizeWord(d))
                .join(', ')
            : '';
          const weekLabel = intervalNum === 1 ? 'week' : `${intervalNum} weeks`;
          patternText = dayNames
            ? `Occurs every ${weekLabel} on ${dayNames}`
            : `Occurs every ${weekLabel}`;
        } else if (recurrence.pattern === 'monthly') {
          const monthLabel = intervalNum === 1 ? 'month' : `${intervalNum} months`;
          if (recurrence.monthlyRecurrenceType === 'relative-weekday' && recurrence.relativeWeekday) {
            patternText = `Occurs every ${monthLabel} on the ${recurrence.relativeWeekday.occurrence} ${recurrence.relativeWeekday.weekday}`;
          } else if (recurrence.dayOfMonth) {
            patternText = `Occurs every ${monthLabel} on day ${recurrence.dayOfMonth}`;
          } else {
            patternText = `Occurs every ${monthLabel}`;
          }
        } else {
          const label = intervalNum === 1 ? recurrence.pattern : `${intervalNum} ${recurrence.pattern}s`;
          patternText = `Occurs every ${label}`;
        }
        let recurrenceEndLabel = '';
        if (recurrence.endType === 'on-date' && recurrence.endDate) {
          try {
            const endDt = new Date(recurrence.endDate);
            if (isValid(endDt)) {
              recurrenceEndLabel = ` until ${format(endDt, 'EEE, MMM d, yyyy')}`;
            }
          } catch { /* ignore */ }
        } else if (recurrence.endType === 'after' && recurrence.occurrences) {
          recurrenceEndLabel = ` for ${recurrence.occurrences} occurrences`;
        }
        html += `<span>${patternText}${recurrenceEndLabel}</span>`;
      } else {
        html += `<span>Recurring booking</span>`;
      }
      html += `</div>`;
    }

    if (tooltipFields.length > 0) {
      html += `<div style="border-top: 1px solid #f3f4f6; padding-top: 6px; margin-top: 6px;">`;
      for (const field of tooltipFields) {
        const fieldValue = getFieldValue(field.fieldName);
        if (fieldValue) {
          const fieldLabel = field.customName || field.fieldName;
          html += `<div style="display: flex; gap: 8px; font-size: 11px; margin-bottom: 3px; line-height: 1.4; align-items: center;">`;
          html += `<span style="color: #9ca3af; min-width: 90px; flex-shrink: 0;">${escapeHtml(fieldLabel)}:</span>`;
          if (isColourField(field.fieldName)) {
            html += `<span style="display: inline-block; width: 14px; height: 14px; border-radius: 4px; background-color: ${escapeHtml(fieldValue)}; flex-shrink: 0;" title="${escapeHtml(fieldValue)}"></span>`;
          } else {
            html += `<span style="color: #374151; flex: 1; word-break: break-word;">${escapeHtml(fieldValue)}</span>`;
          }
          html += `</div>`;
        }
      }
      html += `</div>`;
    }

    // ── Resource avatar(s) section ──
    const allResourceIds: string[] = [];
    if (booking.resourceIds && booking.resourceIds.length > 0) {
      allResourceIds.push(...booking.resourceIds);
    } else if (booking.resourceId) {
      allResourceIds.push(String(booking.resourceId));
    }

    const matchedResources = allResourceIds
      .map((rid) => resources.find((r) => String(r.id) === String(rid)))
      .filter(Boolean) as Resource[];

    if (matchedResources.length > 0) {
      const MAX_VISIBLE = 5;
      const visible = matchedResources.slice(0, MAX_VISIBLE);
      const overflow = matchedResources.length - MAX_VISIBLE;
      const isMulti = matchedResources.length > 1;
      const avatarSize = 26;
      const tealBg = 'oklch(50% .0896 194.8)';

      html += `<div style="border-top: 1px solid #f3f4f6; padding-top: 6px; margin-top: 6px;">`;

      if (isMulti) {
        html += `<div style="font-size: 11px; color: #9ca3af; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">`;
        html += `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`;
        html += `<span>${matchedResources.length} Resources</span>`;
        html += `</div>`;
      }

      const tooltipId = `res-overflow-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const renderAvatar = (res: Resource) => {
        const resInitials = (() => {
          const parts = res.name.trim().split(/\s+/);
          const first = parts[0]?.[0] || '';
          const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
          return (first + last).toUpperCase();
        })();
        const hasResPhoto = !!res.photoUrl && res.photoUrl.trim() !== '' && res.photoUrl !== 'null' && res.photoUrl !== 'undefined';
        const resNameEsc = escapeHtml(res.name);
        const resInitialsEsc = escapeHtml(resInitials);
        const resPhotoEsc = escapeHtml(res.photoUrl || '');
        let avatarHtml = '';
        if (hasResPhoto) {
          avatarHtml += `<div title="${resNameEsc}" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; overflow: hidden; flex-shrink: 0;">`;
          avatarHtml += `<img src="${resPhotoEsc}" alt="${resNameEsc}" style="width: 100%; height: 100%; object-fit: cover; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />`;
          avatarHtml += `<div style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; letter-spacing: 0.5px;">${resInitialsEsc}</div>`;
          avatarHtml += `</div>`;
        } else {
          avatarHtml += `<div title="${resNameEsc}" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; letter-spacing: 0.5px; flex-shrink: 0;">${resInitialsEsc}</div>`;
        }
        return avatarHtml;
      };

      html += `<div style="display: flex; align-items: center; gap: 4px; flex-wrap: wrap;">`;
      for (const res of visible) {
        html += renderAvatar(res);
      }

      if (overflow > 0) {
        const hiddenResources = matchedResources.slice(MAX_VISIBLE);
        // Expand button (visible by default)
        html += `<div id="${tooltipId}-expand" title="Show ${overflow} more resource${overflow > 1 ? 's' : ''}" onclick="event.stopPropagation(); document.getElementById('${tooltipId}-hidden').style.display='contents'; document.getElementById('${tooltipId}-expand').style.display='none'; document.getElementById('${tooltipId}-collapse').style.display='flex';" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; flex-shrink: 0; cursor: pointer; transition: opacity 0.15s;" onmouseover="this.style.opacity='0.8'" onmouseout="this.style.opacity='1'">+${overflow}</div>`;
        // Hidden overflow avatars container
        html += `<span id="${tooltipId}-hidden" style="display: none; gap: 4px;">`;
        for (const res of hiddenResources) {
          html += renderAvatar(res);
        }
        html += `</span>`;
        // Collapse button (hidden by default)
        html += `<div id="${tooltipId}-collapse" title="Show fewer" onclick="event.stopPropagation(); document.getElementById('${tooltipId}-hidden').style.display='none'; document.getElementById('${tooltipId}-expand').style.display='flex'; document.getElementById('${tooltipId}-collapse').style.display='none';" style="width: ${avatarSize}px; height: ${avatarSize}px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px; flex-shrink: 0; cursor: pointer; transition: opacity 0.15s;" onmouseover="this.style.opacity='0.8'" onmouseout="this.style.opacity='1'">&minus;</div>`;
      }

      if (!isMulti) {
        html += `<span style="font-size: 12px; font-weight: 500; color: #374151; margin-left: 4px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(matchedResources[0].name)}</span>`;
      }

      html += `</div>`;

      if (isMulti) {
        html += `<div style="font-size: 10px; color: #9ca3af; margin-top: 4px; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 320px;">`;
        html += matchedResources.map((r) => escapeHtml(r.name)).join(', ');
        html += `</div>`;
      }

      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }, [settings.bookingTypes, settings.statusConfigs, settings.timelineDefaultTimezone, settings.businessHoursStart, settings.businessHoursEnd, projects, projectDetails, clients, costCentres, resources, selectedTimezone, enableTimezones, bookings]);

  const generateAbsenceTooltipHtml = useCallback((eventData: BryntumEvent): string => {
    const absenceColor = settings.absenceColor || '#94a3b8';
    // Show the same text on the tooltip as the one rendered on the booking bar
    // (see eventRenderer absence branch: eventRecord.data?.name || 'Annual Leave').
    const absenceLabel = eventData.name || 'Annual Leave';

    // Resolve resource from composite IDs (e.g. role-resource or project-resource grouping)
    const rawResourceId = String(eventData.resourceId || '');
    const parts = rawResourceId.split('-');
    const baseResourceId = parts.length > 1 ? parts[parts.length - 1] : rawResourceId;
    const resource = resources.find(r => String(r.id) === baseResourceId);
    const resourceName = resource?.name || '';

    // Look up the original absence for the reason field
    const absenceId = eventData.absenceId != null
      ? String(eventData.absenceId)
      : String(eventData.id || '').replace('absence-evt-', '');
    const absence = absences.find(a => String(a.id) === absenceId);

    const startDate = eventData.startDate instanceof Date ? eventData.startDate : new Date(eventData.startDate as unknown as string);
    const endDate = eventData.endDate instanceof Date ? eventData.endDate : new Date(eventData.endDate as unknown as string);

    let html = `<div style="padding: 6px 8px; max-width: 300px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">`;

    html += `<div style="display: flex; align-items: center; gap: 8px;">`;
    html += `<div style="width: 10px; height: 10px; border-radius: 2px; background-color: ${absenceColor}; border: 1px dashed rgba(255,255,255,0.4); flex-shrink: 0;"></div>`;
    html += `<div style="font-weight: 700; font-size: 14px; color: #1f2937;">${escapeHtml(absenceLabel)}</div>`;
    html += `</div>`;

    if (absence?.reason) {
      html += `<div style="font-size: 12px; color: #6b7280; margin-top: 4px;">${escapeHtml(absence.reason)}</div>`;
    }

    html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 6px 0;" />`;

    html += `<div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #6b7280;">`;
    if (isValid(startDate)) {
      html += `<div style="display: flex; align-items: center; gap: 6px;">`;
      html += `<span style="color: #9ca3af;">Start:</span>`;
      html += `<span>${format(startDate, 'EEE, MMM d, yyyy')}</span>`;
      html += `</div>`;
    }
    if (isValid(endDate)) {
      html += `<div style="display: flex; align-items: center; gap: 6px;">`;
      html += `<span style="color: #9ca3af;">End:</span>`;
      html += `<span>${format(endDate, 'EEE, MMM d, yyyy')}</span>`;
      html += `</div>`;
    }
    html += `</div>`;

    if (resourceName) {
      html += `<hr style="border: none; border-top: 1px solid #e5e7eb; margin: 6px 0;" />`;
      const resInitials = (() => {
        const nameParts = resourceName.trim().split(/\s+/);
        const first = nameParts[0]?.[0] || '';
        const last = nameParts.length > 1 ? nameParts[nameParts.length - 1]?.[0] || '' : '';
        return (first + last).toUpperCase();
      })();
      const tealBg = 'oklch(50% .0896 194.8)';
      html += `<div style="display: flex; align-items: center; gap: 6px;">`;
      if (resource?.photoUrl && resource.photoUrl.trim() !== '' && resource.photoUrl !== 'null' && resource.photoUrl !== 'undefined') {
        html += `<img src="${escapeHtml(resource.photoUrl)}" alt="${escapeHtml(resourceName)}" style="width: 24px; height: 24px; border-radius: 6px; object-fit: cover;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" />`;
        html += `<div style="width: 24px; height: 24px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px;">${escapeHtml(resInitials)}</div>`;
      } else {
        html += `<div style="width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: ${tealBg}; color: rgba(255,255,255,0.9); font-weight: 600; font-size: 10px;">${escapeHtml(resInitials)}</div>`;
      }
      html += `<span style="font-size: 12px; font-weight: 500; color: #374151;">${escapeHtml(resourceName)}</span>`;
      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }, [resources, absences, settings.absenceColor]);

  const generateTooltipHtmlRef = useRef(generateTooltipHtml);
  generateTooltipHtmlRef.current = generateTooltipHtml;
  const generateAbsenceTooltipHtmlRef = useRef(generateAbsenceTooltipHtml);
  generateAbsenceTooltipHtmlRef.current = generateAbsenceTooltipHtml;

  const generateYearSummaryTooltipHtml = useCallback((summary: YearMonthSummary, resourceName: string): string => {
    const dayLabel = (date: Date) => format(date, 'd MMM');
    const spanLabel = (start: Date, end: Date) => {
      const from = dayLabel(start);
      const to = dayLabel(end);
      return from === to ? from : `${from} – ${to}`;
    };
    const section = (title: string, count: number) =>
      `<div class="eb-ys-section">${escapeHtml(title)} · ${count}</div>`;

    const count = summary.bookings.length;
    let html = `<div class="eb-ys-tip">`;
    html += `<div class="eb-ys-header">`;
    html += `<div class="eb-ys-title">${escapeHtml(resourceName || 'Resource')}</div>`;
    html += `<div class="eb-ys-subtitle">${escapeHtml(format(summary.monthStart, 'MMMM yyyy'))} · ${count} ${count === 1 ? 'booking' : 'bookings'}</div>`;
    if (canCreateBooking) {
      const target = `data-year-summary-row="${escapeHtml(summary.rowId)}" data-year-summary-date="${summary.monthStart.getTime()}"`;
      const forLabel = `${resourceName || 'resource'} in ${format(summary.monthStart, 'MMMM yyyy')}`;
      html += `<div class="eb-ys-actions">`;
      html += `<button type="button" class="eb-ys-action" data-year-summary-add="full" ${target} aria-label="${escapeHtml(`Add booking for ${forLabel}`)}">+ Add booking</button>`;
      if (quickAddEnabled) {
        html += `<button type="button" class="eb-ys-action" data-year-summary-add="quick" ${target} aria-label="${escapeHtml(`Quick add booking for ${forLabel}`)}">Quick add</button>`;
      }
      html += `</div>`;
    }
    html += `</div>`;
    html += `<div class="eb-ys-body">`;

    if (count > 0) {
      html += section('Bookings', count);
      html += `<ul class="eb-ys-list">`;
      summary.bookings.forEach((b, index) => {
        const meta = [spanLabel(b.start, b.displayEnd), b.status].filter(Boolean).join(' · ');
        html += index < YEAR_SUMMARY_VISIBLE_BOOKINGS ? `<li>` : `<li class="eb-ys-extra">`;
        html += `<button type="button" class="eb-ys-item eb-ys-booking" data-year-summary-booking="${escapeHtml(b.bookingId)}" data-year-summary-resource="${escapeHtml(b.resourceId)}" aria-label="${escapeHtml(`Open booking ${b.title}, ${meta}`)}">`;
        html += `<span class="eb-ys-swatch" style="background-color: ${escapeHtml(b.color)};"></span>`;
        html += `<span class="eb-ys-text"><span class="eb-ys-name">${escapeHtml(b.title)}</span><span class="eb-ys-meta">${escapeHtml(meta)}</span></span>`;
        html += `<span class="eb-ys-open" aria-hidden="true">Open ›</span>`;
        html += `</button></li>`;
      });
      const hidden = count - YEAR_SUMMARY_VISIBLE_BOOKINGS;
      if (hidden > 0) {
        html += `<li class="eb-ys-more-row"><button type="button" class="eb-ys-item eb-ys-more" data-year-summary-more="true">+ ${hidden} more ${hidden === 1 ? 'booking' : 'bookings'}</button></li>`;
      }
      html += `</ul>`;
    }

    if (summary.absences.length > 0) {
      html += section('Absences', summary.absences.length);
      html += `<ul class="eb-ys-list">`;
      summary.absences.forEach((a) => {
        const meta = [spanLabel(a.start, a.displayEnd), `${a.days} ${a.days === 1 ? 'day' : 'days'}`, a.reason && a.reason !== a.label ? a.reason : '']
          .filter(Boolean).join(' · ');
        html += `<li class="eb-ys-item">`;
        html += `<span class="eb-ys-swatch eb-ys-swatch-absence" style="border-color: ${escapeHtml(a.color)}; color: ${escapeHtml(a.color)};"></span>`;
        html += `<span class="eb-ys-text"><span class="eb-ys-name">${escapeHtml(a.label)}</span><span class="eb-ys-meta">${escapeHtml(meta)}</span></span>`;
        html += `</li>`;
      });
      html += `</ul>`;
    }

    if (summary.holidays.length > 0) {
      html += section('Bank holidays', summary.holidays.length);
      html += `<ul class="eb-ys-list">`;
      summary.holidays.forEach((h) => {
        html += `<li class="eb-ys-item">`;
        html += `<span class="eb-ys-swatch" style="background-color: ${escapeHtml(h.color)};"></span>`;
        html += `<span class="eb-ys-text"><span class="eb-ys-name">${escapeHtml(h.name)}</span><span class="eb-ys-meta">${escapeHtml(format(h.date, 'EEE d MMM'))}</span></span>`;
        html += `</li>`;
      });
      html += `</ul>`;
    }

    html += `</div></div>`;
    return html;
  }, [canCreateBooking, quickAddEnabled]);
  const generateYearSummaryTooltipHtmlRef = useRef(generateYearSummaryTooltipHtml);
  generateYearSummaryTooltipHtmlRef.current = generateYearSummaryTooltipHtml;

  // A summary list opens on hover; clicking its tile pins it open until the user
  // clicks elsewhere, presses Escape or opens a booking from it.
  const pinnedYearSummaryRef = useRef<string | null>(null);
  const isYearSummaryView = calendarView === 'year' && featureFlags.isEnabled('yearMonthSummary');
  const getEventTooltip = useCallback(
    (): YearSummaryTip | undefined =>
      (schedulerRef.current?.instance as { features?: { eventTooltip?: { tooltip?: YearSummaryTip } } } | undefined)
        ?.features?.eventTooltip?.tooltip,
    [],
  );
  const unpinYearSummaryTip = useCallback((hide: boolean) => {
    pinnedYearSummaryRef.current = null;
    const tip = getEventTooltip();
    if (!tip) return;
    tip.autoHide = true;
    if (hide) tip.hide();
  }, [getEventTooltip]);
  const syncYearSummaryTip = useCallback((tip: YearSummaryTip | undefined, summaryId: string | null) => {
    if (!tip) return;
    tip.autoClose = summaryId == null;
    if (pinnedYearSummaryRef.current !== summaryId) {
      pinnedYearSummaryRef.current = null;
      tip.autoHide = true;
    }
  }, []);
  const syncYearSummaryTipRef = useRef(syncYearSummaryTip);
  syncYearSummaryTipRef.current = syncYearSummaryTip;

  useEffect(() => {
    const pinOrDismissOnPointer = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const tip = getEventTooltip();
      if (tip?.element?.contains(target)) return;
      const summaryId = target?.closest('.eb-year-summary')
        ?.closest('[data-event-id]')?.getAttribute('data-event-id');
      if (summaryId && tip && e.button === 2) {
        // Right-click opens the add-booking menu instead of the list.
        unpinYearSummaryTip(true);
        return;
      }
      if (summaryId && tip) {
        pinnedYearSummaryRef.current = summaryId;
        tip.autoHide = false;
        const tipTarget = target?.closest<HTMLElement>(tip.forSelector);
        if (tipTarget && (!tip.isVisible || tip.activeTarget !== tipTarget)) {
          // Bryntum cancels a pending hover show on mousedown, so a click shows the list itself.
          setTimeout(() => {
            if (pinnedYearSummaryRef.current !== summaryId || !tipTarget.isConnected) return;
            tip.activeTarget = tipTarget;
            void tip.updateContent().then(() => {
              if (pinnedYearSummaryRef.current === summaryId) tip.showByTarget(tipTarget);
            });
          }, 0);
        }
        return;
      }
      if (pinnedYearSummaryRef.current) unpinYearSummaryTip(true);
    };
    const dismissOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pinnedYearSummaryRef.current) unpinYearSummaryTip(true);
    };
    document.addEventListener('mousedown', pinOrDismissOnPointer, true);
    document.addEventListener('keydown', dismissOnEscape);
    return () => {
      document.removeEventListener('mousedown', pinOrDismissOnPointer, true);
      document.removeEventListener('keydown', dismissOnEscape);
    };
  }, [getEventTooltip, unpinYearSummaryTip]);
  const generateHeatmapTooltipHtmlRef = useRef(generateHeatmapTooltipHtml);
  generateHeatmapTooltipHtmlRef.current = generateHeatmapTooltipHtml;
  const findBookingByIdRef = useRef(findBookingById);
  findBookingByIdRef.current = findBookingById;
  const siblingSelectionMutationRef = useRef(false);
  const selectionAtResizeStartRef = useRef<Booking[]>([]);
  const selectionAtDragStartRef = useRef<Booking[]>([]);
  const isMultiSelectionDragRef = useRef(false);
  // Latest set of locked (mid-deletion) booking ids for use inside effect-bound handlers.
  const lockedBookingIdsRef = useRef(lockedBookingIds);
  lockedBookingIdsRef.current = lockedBookingIds;

  const isDateInAbsence = useCallback((resourceId: string, date: Date): boolean => {
    // Resolve base resource ID from composite grouping IDs
    const parts = resourceId.split('-');
    const baseId = parts.length > 1 ? parts[parts.length - 1] : resourceId;
    return absences.some(absence => {
      if (String(absence.resourceId) !== baseId) return false;
      const absStart = new Date(absence.startDate);
      const absEnd = new Date(absence.endDate);
      // Ensure end covers the full day
      if (absEnd.getHours() === 0 && absEnd.getMinutes() === 0) {
        absEnd.setHours(23, 59, 59, 999);
      }
      return date >= absStart && date <= absEnd;
    });
  }, [absences]);

  const isDateInAbsenceRef = useRef(isDateInAbsence);
  isDateInAbsenceRef.current = isDateInAbsence;

  const handleEventClick = useCallback((
    { eventRecord, event }: { eventRecord: { data: BryntumEvent }; event?: MouseEvent },
  ) => {
    if (eventRecord.data?.isAbsenceEvent || eventRecord.data?.yearSummary) return;
    // Ctrl/Cmd-click builds up a selection (Bryntum's own multiEventSelect) rather
    // than opening the booking, so several bookings can be picked and then deleted
    // together from the right-click menu.
    if (event?.ctrlKey || event?.metaKey) return;
    const booking = findBookingById(eventRecord.data.id);
    if (booking && onEditBooking) {
      onEditBooking(booking, eventRecord.data.resourceId);
    }
  }, [findBookingById, onEditBooking]);

  const handleEventDblClick = useCallback(({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
    if (eventRecord.data?.isAbsenceEvent || eventRecord.data?.yearSummary) return false;
    const booking = findBookingById(eventRecord.data.id);
    if (booking && onEditBooking) {
      onEditBooking(booking, eventRecord.data.resourceId);
    }
    // Return false to prevent Bryntum's default event edit behavior
    return false;
  }, [findBookingById, onEditBooking]);

  const handleScheduleClick = useCallback(({ resourceRecord, date }: { resourceRecord: { data: BryntumResource }; date: Date }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block interactions on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) return false as unknown as void;

    // Block booking creation on cells covered by an absence
    if (isDateInAbsence(id, date)) return;

    const resource = findResourceById(id);
    if (resource) {
      // Block booking creation on non-working days.
      // Skipped in Quarter/Year views where a cell spans many days: the click date
      // is the 1st of the period and may land on a weekend, which must not block create.
      if (!isCoarseGranularityView && isNonWorkingDay(resource, date)) return;
      // Block booking creation during non-business hours (Day view only)
      if (isNonBusinessHour(date)) return;
      if (!isCoarseGranularityView && refuseOutOfContract(resource, date)) return;

      if (onSlotClick) {
        // Extract projectId from combined ID in project-resource mode
        // Combined IDs are in format: "projectId-resourceId"
        let bookingData: Partial<Booking> | undefined;
        const combinedId = id;
        if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
          const parts = combinedId.split('-');
          if (parts.length >= 2) {
            const projectId = parts[0];
            bookingData = { projectId };
          }
        }
        const customerName = extractCustomerName(combinedId);
        if (customerName) {
          bookingData = { ...bookingData, customer: customerName };
        }

        onSlotClick(resource, date, { hour: date.getHours(), minute: date.getMinutes() }, bookingData);
      }
    }
  }, [findResourceById, onSlotClick, groupingMode, extractCustomerName, isNonWorkingDay, isNonBusinessHour, isDateInAbsence, isCoarseGranularityView, refuseOutOfContract]);

  const handleScheduleDblClick = useCallback(({ resourceRecord, eventRecord, date }: { resourceRecord: { data: BryntumResource }; date: Date, eventRecord: { startDate: Date; endDate: Date; remove?: () => void }; }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block interactions on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) return false as unknown as void;

    // Block booking creation on cells covered by an absence
    if (isDateInAbsence(id, date)) return false as unknown as void;

    const resource = findResourceById(id);
    if (resource) {
      // Block booking creation on non-working days.
      // Skipped in Quarter/Year views (see handleScheduleClick) where a cell spans many days.
      if (!isCoarseGranularityView && isNonWorkingDay(resource, date)) return false as unknown as void;
      // Block booking creation during non-business hours (Day view only)
      if (isNonBusinessHour(date)) return false as unknown as void;
      if (!isCoarseGranularityView && refuseOutOfContract(resource, date)) return false as unknown as void;
    }
    if (resource && onDragSelect) {
      cleanupPhantomEvents();
      let endTime: Date;

      // For Year view, set end time to the last day of the clicked month
      if (calendarView === 'year') {
        endTime = new Date(date.getFullYear(), date.getMonth() + 1, 0); // Last day of month
      } else {
        endTime = new Date(date);
        endTime.setHours(endTime.getHours() + 1);
      }

      // Extract projectId from combined ID in project-resource mode
      // Combined IDs are in format: "projectId-resourceId"
      let bookingData: Partial<Booking> | undefined;
      const combinedId = resourceRecord.data.id;
      if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
        const parts = combinedId.split('-');
        if (parts.length >= 2) {
          const projectId = parts[0];
          bookingData = { projectId };
        }
      }
      const customerName = extractCustomerName(combinedId);
      if (customerName) {
        bookingData = { ...bookingData, customer: customerName };
      }

      // DYNAMIC GRANULARITY DETECTION:
      // Determine durationType based on the timeline's zoom level and calendar view.
      // This ensures the booking form opens with the correct duration type based on
      // what the user sees on the timeline, not just the nominal view mode.

      // Determine if the timeline is showing hourly granularity based on calendarView and zoomLevel:
      // - Day view: always hourly (zoomLevel 0 = hourly, zoomLevel 1 = 15-min)
      // - Week/Month view: zoomLevel 0 = daily, zoomLevel 1-4 = hourly
      // - Quarter view: weekly granularity (not hourly)
      // - Year view: monthly granularity (not hourly)
      const isHourlyGranularity =
        calendarView === 'day' || // Day view is always hourly
        ((calendarView === 'week' || calendarView === 'month') && zoomLevel >= 1); // Week/Month zoomed in

      if (isHourlyGranularity) {
        bookingData = { ...bookingData, durationType: 'hourly' };
      }

      // Clean up any remaining phantom events as a safety net
      cleanupPhantomEvents();

      // Otherwise, let the handler decide based on the view type (will default to 'full-day')

      onDragSelect(
        resource,
        date,
        { hour: date.getHours(), minute: date.getMinutes() },
        { hour: endTime.getHours(), minute: endTime.getMinutes() },
        bookingData,
        endTime
      );
    }
    // Always block Bryntum's default create-on-dblclick
    return false as unknown as void;
  }, [findResourceById, onDragSelect, calendarView, zoomLevel, groupingMode, extractCustomerName, isNonWorkingDay, isNonBusinessHour, isDateInAbsence, isCoarseGranularityView, refuseOutOfContract]);

  // Double-clicking a YEAR summary tile creates a booking, as on an empty YEAR month.
  const handleEventOrYearSummaryDblClick = useCallback((
    { eventRecord, resourceRecord, event }: { eventRecord: { data: BryntumEvent; startDate: Date; endDate: Date }; resourceRecord?: { data: BryntumResource }; event?: MouseEvent },
  ) => {
    if (!eventRecord.data?.yearSummary) return handleEventDblClick({ eventRecord });
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    const date = event ? scheduler?.getDateFromDomEvent(event) : undefined;
    if (date && resourceRecord) {
      unpinYearSummaryTip(true);
      handleScheduleDblClick({ resourceRecord, eventRecord, date });
    }
    return false;
  }, [handleEventDblClick, handleScheduleDblClick, unpinYearSummaryTip]);

  const handleEventDrop = useCallback(({ eventRecords, context }: { eventRecords: Array<{ data: BryntumEvent }>; context: { newResource?: { data: BryntumResource }; startDate: Date; endDate: Date } }) => {
    const isMultiSelectionDrag = isMultiSelectionDragRef.current;
    isMultiSelectionDragRef.current = false;
    const selectionAtDragStart = selectionAtDragStartRef.current;
    selectionAtDragStartRef.current = [];
    if (eventRecords.length === 0) return;

    const eventRecord = eventRecords[0];
    const booking = findBookingById(eventRecord.data.id);
    // Use booking.resourceId from React state (not updated yet) to get the ORIGINAL resource
    // eventRecord.data.resourceId might have been updated by Bryntum after the drag
    // Unallocated bookings carry no resource id in live data, so fall back to the
    // synthetic Unallocated resource to keep them draggable onto real resources.
    const draggedTileResourceId = booking
      ? tileResourceIdFor(eventRecord.data, booking)
      : undefined;
    const originalResource = booking
      ? findResourceById(draggedTileResourceId ?? booking.resourceId ?? "")
        ?? (booking.resourceId == null ? findResourceById(UNALLOCATED_RESOURCE_ID) : undefined)
      : null;
    const newResourceId = isMultiSelectionDrag && booking
      ? String(draggedTileResourceId ?? booking.resourceId ?? eventRecord.data.resourceId)
      : context.newResource?.data.id || eventRecord.data.resourceId;

    let restoreEventDates: ((
      records: Array<{ data: BryntumEvent }>,
      bookingIds?: ReadonlySet<string>,
    ) => void) | undefined;
    if (isMultiSelectionDrag) {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (scheduler) {
        const resourceIdForBooking = (targetBooking: Booking, targetResource: Resource): string => {
          if (String(targetResource.id) === UNALLOCATED_RESOURCE_ID) {
            return UNALLOCATED_RESOURCE_ID;
          }
          if (groupingMode === 'project-resource' && targetBooking.projectId) {
            return `${targetBooking.projectId}-${targetResource.id}`;
          }
          if (groupingMode === 'role-resource') {
            return `${targetResource.role || 'Unassigned'}-${targetResource.id}`;
          }
          if (groupingMode === 'customer') {
            let customerName = targetBooking.customer?.trim() || '';
            if (!customerName && targetBooking.projectId) {
              const project = projectDetails.find(p => String(p.id) === String(targetBooking.projectId));
              customerName = project?.clientName?.trim() || '';
            }
            if (!customerName) customerName = 'Unassigned';
            return `${customerName}-${targetResource.id}`;
          }
          return String(targetResource.id);
        };

        restoreEventDates = (
          records: Array<{ data: BryntumEvent }>,
          bookingIds?: ReadonlySet<string>,
        ) => {
          for (const record of records) {
            const selectedBooking = findBookingById(record.data.id);
            if (!selectedBooking || (bookingIds && !bookingIds.has(String(selectedBooking.id)))) {
              continue;
            }

            const originalStart = new Date(selectedBooking.startTime);
            const originalEnd = new Date(selectedBooking.endTime);
            if (Number.isNaN(originalStart.getTime()) || Number.isNaN(originalEnd.getTime())) {
              continue;
            }

            const sourceTz = selectedBooking.timezone || 'Europe/London';
            const targetTz = selectedTimezone || sourceTz;
            const offsetMin = enableTimezones
              ? getTimezoneOffset(sourceTz, targetTz, originalStart)
              : 0;
            const eventToRestore = scheduler.eventStore.getById(record.data.id) as EventModel | undefined;
            if (eventToRestore) {
              eventToRestore.beginBatch();
              eventToRestore.startDate = new Date(originalStart.getTime() + offsetMin * 60000);
              eventToRestore.endDate = new Date(originalEnd.getTime() + offsetMin * 60000);
              eventToRestore.endBatch();
            }
          }
        };

        for (const record of eventRecords) {
          const selectedBooking = findBookingById(record.data.id);
          if (!selectedBooking) continue;
          const eventId = String(record.data.id);
          const siblingIndex = eventId.match(/__mr__(\d+)$/)?.[1];
          const resourceId = siblingIndex === undefined
            ? selectedBooking.resourceId
            : selectedBooking.resourceIds?.[Number(siblingIndex)] ?? selectedBooking.resourceId;
          const selectedResource = findResourceById(String(resourceId ?? ''));
          if (!selectedResource) continue;

          const eventToRestore = scheduler.eventStore.getById(record.data.id) as EventModel | undefined;
          if (eventToRestore) {
            eventToRestore.beginBatch();
            eventToRestore.resourceId = resourceIdForBooking(selectedBooking, selectedResource);
            eventToRestore.endBatch();
          }
        }
      }
    }

    // In Project view a drop on a project group row moves the booking to that project
    // while keeping its current resource — this is how childless (booking-less) projects
    // are usable as drop targets.
    const droppedOnProjectGroup = !isMultiSelectionDrag
      && groupingMode === 'project-resource'
      && newResourceId.startsWith('project-');

    // VALIDATION: Prevent dropping bookings on group/parent nodes
    // Group nodes have IDs that start with "group-" (e.g., "group-Department Name")
    // or "project-" (e.g., "project-123" for project groups in project-resource mode)
    // or "role-" (e.g., "role-Developer" for role groups in role-resource mode)
    // Only actual resources (leaf nodes) should accept bookings
    const isGroupNode = newResourceId.startsWith('group-') || newResourceId.startsWith('project-') || newResourceId.startsWith('role-') || newResourceId.startsWith('customer-');
    if (isGroupNode && !droppedOnProjectGroup) {

      // Revert the booking to its original position
      if (booking && originalResource) {
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        // Determine the correct resource ID to revert to based on grouping mode
        let revertResourceId: string;
        if (String(originalResource.id) === UNALLOCATED_RESOURCE_ID) {
          revertResourceId = UNALLOCATED_RESOURCE_ID;
        } else if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          revertResourceId = `${originalResource.role || 'Unassigned'}-${originalResource.id}`;
        } else if (groupingMode === 'customer') {
          let custName: string = booking.customer?.trim() || '';
          if (!custName && booking.projectId) {
            const project = projectDetails.find(p => String(p.id) === String(booking.projectId));
            custName = project?.clientName?.trim() || '';
          }
          if (!custName) custName = 'Unassigned';
          revertResourceId = `${custName}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        // Use the exposed revertEvent method to restore the booking
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(eventRecord.data.id);

          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }
      }

      // Return early without calling onBookingDragEnd
      return;
    }

    // In project-resource or role-resource mode, extract the actual resource ID from the combined ID
    // Combined IDs are in format: "projectId-resourceId" or "roleName-resourceId"
    let actualNewResourceId = newResourceId;
    // Project view: the drop target also identifies the project the booking moves to.
    let newProjectId: string | undefined;
    if (droppedOnProjectGroup) {
      newProjectId = context.newResource?.data.projectId
        ?? newResourceId.slice('project-'.length);
      actualNewResourceId = String(draggedTileResourceId ?? booking?.resourceId ?? originalResource?.id ?? '');
    } else if (!isMultiSelectionDrag && groupingMode === 'project-resource' && newResourceId.includes('-') && !newResourceId.startsWith('group-')) {
      // Composite child rows carry the project and resource ids as fields, which is
      // reliable even when either id itself contains a hyphen.
      const targetRow = context.newResource?.data;
      if (targetRow?.originalResourceId) {
        actualNewResourceId = String(targetRow.originalResourceId);
        newProjectId = targetRow.projectId ? String(targetRow.projectId) : undefined;
      } else {
        console.warn('[BryntumSchedulerTimeline] composite row missing originalResourceId — falling back to ID split', { newResourceId });
        const parts = newResourceId.split('-');
        if (parts.length >= 2) {
          // The resource ID is everything after the first part (project ID)
          actualNewResourceId = parts.slice(1).join('-');
          newProjectId = parts[0];
        }
      }
    } else if (!isMultiSelectionDrag && groupingMode === 'role-resource' && newResourceId.includes('-') && !newResourceId.startsWith('group-') && !newResourceId.startsWith('role-')) {
      // Extract the resource ID from the combined role-resource ID
      const parts = newResourceId.split('-');
      if (parts.length >= 2) {
        // The resource ID is everything after the first part (role name)
        actualNewResourceId = parts.slice(1).join('-');
      }
    } else if (!isMultiSelectionDrag && groupingMode === 'customer' && newResourceId.includes('-') && !newResourceId.startsWith('group-') && !newResourceId.startsWith('customer-')) {
      // Extract the resource ID from the combined customer-resource ID
      // Try matching by suffix first (handles hyphens in customer names)
      const matchedResource = resources.find((r: Resource) => {
        const rid = String(r.id);
        return newResourceId.endsWith(`-${rid}`);
      });
      if (matchedResource) {
        actualNewResourceId = String(matchedResource.id);
      } else {
        // Fallback: everything after first hyphen
        const dashIdx = newResourceId.indexOf('-');
        if (dashIdx !== -1) actualNewResourceId = newResourceId.slice(dashIdx + 1);
      }
    }

    // VALIDATION: Prevent dropping bookings on non-working days
    // Check if the target date falls on a non-working day for the target resource
    const targetResource = findResourceById(actualNewResourceId);
    // The Unallocated row is not a person, so working days and business hours
    // never apply to it.
    const isUnallocatedTarget = actualNewResourceId === UNALLOCATED_RESOURCE_ID;
    if (targetResource && booking && originalResource && !isUnallocatedTarget) {
      const targetDate = context.startDate;

      // Check non-working day first
      if (isNonWorkingDay(targetResource, targetDate)) {

        // Revert the booking to its original position
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        // Determine the correct resource ID to revert to based on grouping mode
        let revertResourceId: string;
        if (String(originalResource.id) === UNALLOCATED_RESOURCE_ID) {
          revertResourceId = UNALLOCATED_RESOURCE_ID;
        } else if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          revertResourceId = `${originalResource.role || 'Unassigned'}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        // Use the scheduler to restore the booking
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(eventRecord.data.id);

          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }

        // Return early without calling onBookingDragEnd
        return;
      }

      // VALIDATION: Prevent dropping bookings during non-business hours (Day view)
      if (isNonBusinessHour(targetDate)) {

        // Revert the booking to its original position
        const originalStartTime = new Date(booking.startTime);
        const originalEndTime = new Date(booking.endTime);

        let revertResourceId: string;
        if (String(originalResource.id) === UNALLOCATED_RESOURCE_ID) {
          revertResourceId = UNALLOCATED_RESOURCE_ID;
        } else if (groupingMode === 'project-resource' && booking.projectId) {
          revertResourceId = `${booking.projectId}-${originalResource.id}`;
        } else if (groupingMode === 'role-resource') {
          revertResourceId = `${originalResource.role || 'Unassigned'}-${originalResource.id}`;
        } else {
          revertResourceId = originalResource.id;
        }

        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler) {
          const eventStore = scheduler.eventStore;
          const eventToRevert = eventStore.getById(eventRecord.data.id);
          if (eventToRevert) {
            eventToRevert.beginBatch();
            (eventToRevert as any).startDate = originalStartTime;
            (eventToRevert as any).endDate = originalEndTime;
            (eventToRevert as any).resourceId = revertResourceId;
            eventToRevert.endBatch();
          }
        }

        return;
      }
    }

    if (booking && originalResource && onBookingDragEnd) {
      // Reverse the display-only timezone shift applied when rendering the event
      // so the dates passed downstream are back in the stored (source) timezone.
      const sourceTz = (booking as any).timezone || 'Europe/London';
      const targetTz = selectedTimezone || sourceTz;
      const offsetMin = enableTimezones ? getTimezoneOffset(sourceTz, targetTz, context.startDate) : 0;
      const correctedStart = new Date(context.startDate.getTime() - offsetMin * 60000);
      const correctedEnd = new Date(context.endDate.getTime() - offsetMin * 60000);

      if (isMultiSelectionDrag && onBookingsDragEnd) {
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        const dragSelection = selectionAtDragStart.length > 0
          ? selectionAtDragStart
          : bookingsFromSelectedEvents(
            (scheduler as unknown as { selectedEvents?: SelectedEventLike[] })?.selectedEvents ?? [],
            {
              findBooking: id => findBookingByIdRef.current(id),
              isLocked: id => lockedBookingIdsRef.current?.has(id) === true,
              isRestricted: b => isBookingRestrictedRef.current(b),
            },
          );
        const targets = bulkActionTargets(booking, dragSelection);
        if (isMultiSelectionDrag) {
          const groupTargets = groupDragTargets({
            dragged: booking,
            selection: targets,
            correctedStart,
          });

          if (groupTargets.targets.length === 0) {
            restoreEventDates?.(eventRecords);
            return;
          }

          const targetList: Array<{
            booking: Booking;
            resource: Resource;
            newStartTime: Date;
            newEndTime: Date;
          }> = [];
          let skippedCount = groupTargets.skipped.length;
          const skippedBookingIds = new Set(
            groupTargets.skipped.map(target => String(target.id)),
          );
          for (const target of groupTargets.targets) {
            const targetResource = findResourceById(String(target.booking.resourceId ?? ''));
            if (!targetResource) {
              skippedCount += 1;
              skippedBookingIds.add(String(target.booking.id));
              continue;
            }
            targetList.push({
              booking: target.booking,
              resource: targetResource,
              newStartTime: target.newStartTime,
              newEndTime: target.newEndTime,
            });
          }

          if (skippedBookingIds.size > 0) {
            restoreEventDates?.(eventRecords, skippedBookingIds);
          }

          if (targetList.length > 0) {
            setTimeout(() => onBookingsDragEnd(targetList, { skippedCount }), 0);
          }
          return;
        }
      }

      setTimeout(() => {
        onBookingDragEnd(
          booking,
          originalResource,
          actualNewResourceId,
          correctedStart,
          correctedEnd,
          false,
          newProjectId
        );
      }, 0);
    }
  }, [findBookingById, findResourceById, tileResourceIdFor, onBookingDragEnd, onBookingsDragEnd, groupingMode, projectDetails, isNonWorkingDay, isNonBusinessHour, enableTimezones, selectedTimezone]);

  const handleEventResizeEnd = useCallback(({ eventRecord, startDate, endDate }: { eventRecord: { data: BryntumEvent; startDate: Date; endDate: Date }; startDate?: Date; endDate?: Date }) => {
    const selectionAtResizeStart = selectionAtResizeStartRef.current;
    selectionAtResizeStartRef.current = [];
    const booking = findBookingById(eventRecord.data.id);
    const resizedTileResourceId = booking ? tileResourceIdFor(eventRecord.data, booking) : undefined;
    const resource = findResourceById(resizedTileResourceId ?? eventRecord.data.resourceId);

    if (!booking || !resource) return;

    // Reverse the display-only timezone shift applied when rendering the event
    // so the dates passed downstream are back in the stored (source) timezone.
    // Bryntum reports the resized dates on the event itself; the record can still
    // hold its pre-resize dates (a re-render syncing the event store mid-gesture
    // reverts them), so the event dates are the authoritative result of the drag.
    const resizeStart = startDate ?? ((eventRecord as any).startDate as Date);
    const resizeEnd = endDate ?? ((eventRecord as any).endDate as Date);
    const sourceTz = (booking as any).timezone || 'Europe/London';
    const targetTz = selectedTimezone || sourceTz;
    const offsetMin = enableTimezones ? getTimezoneOffset(sourceTz, targetTz, resizeStart) : 0;
    const correctedStart = new Date(resizeStart.getTime() - offsetMin * 60000);
    const correctedEnd = new Date(resizeEnd.getTime() - offsetMin * 60000);

    const scheduleSingleResize = () => {
      if (!onBookingDragEnd) return;
      setTimeout(() => {
        onBookingDragEnd(
          booking,
          resource,
          resizedTileResourceId ?? booking.resourceId ?? "",
          correctedStart,
          correctedEnd,
          true
        );
      }, 0);
    };

    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    const liveSelection = bookingsFromSelectedEvents(
      (scheduler as unknown as { selectedEvents?: SelectedEventLike[] })?.selectedEvents ?? [],
      {
        findBooking: id => findBookingByIdRef.current(id),
        isLocked: id => lockedBookingIdsRef.current?.has(id) === true,
        isRestricted: b => isBookingRestrictedRef.current(b),
      },
    );
    const selection = selectionAtResizeStart.length > 0
      ? selectionAtResizeStart
      : liveSelection;
    const targets = bulkActionTargets(booking, selection);

    if (targets.length <= 1 || !onBookingsResizeEnd) {
      scheduleSingleResize();
      return;
    }

    const groupTargets = groupResizeTargets({
      resized: booking,
      selection: targets,
      correctedStart,
      correctedEnd,
    });
    if (groupTargets.targets.length === 0) {
      scheduleSingleResize();
      return;
    }

    const targetList: Array<{
      booking: Booking;
      resource: Resource;
      newStartTime: Date;
      newEndTime: Date;
    }> = [];
    for (const target of groupTargets.targets) {
      const targetResource = findResourceById(String(target.booking.resourceId ?? ''));
      if (!targetResource) {
        continue;
      }

      targetList.push({
        booking: target.booking,
        resource: targetResource,
        newStartTime: target.newStartTime,
        newEndTime: target.newEndTime,
      });
    }

    if (targetList.length > 0) {
      setTimeout(() => onBookingsResizeEnd(targetList), 0);
    }
  }, [findBookingById, findResourceById, tileResourceIdFor, onBookingDragEnd, onBookingsResizeEnd, groupingMode, enableTimezones, selectedTimezone]);

  const handleDragCreateEnd = useCallback(({ resourceRecord, eventRecord, context }: { resourceRecord: { data: BryntumResource }; eventRecord: { startDate: Date; endDate: Date; remove?: () => void }; context?: { finalize?: (createEvent: boolean) => void } }) => {
    const id = resourceRecord.data.id as unknown as string;
    // Block drag-create on grouping headers (group-, project-, role-, customer-)
    if (id?.startsWith('group-') || id?.startsWith('project-') || id?.startsWith('role-') || id?.startsWith('customer-')) {
      if (context?.finalize) context.finalize(false);
      if (eventRecord.remove) eventRecord.remove();
      cleanupPhantomEvents();
      return false as unknown as void;
    }

    const resource = findResourceById(id);

    // Block drag-create if the dragged range includes any non-working day.
    // Skipped in Quarter/Year views where a single cell spans many days (would always match a weekend).
    if (resource && !isCoarseGranularityView) {
      const dragStart = (eventRecord as any).startDate as Date;
      const dragEnd = (eventRecord as any).endDate as Date;
      // Bryntum's endDate is exclusive (a Mon→Fri drag ends at Sat 00:00), so step
      // back one millisecond to get the last instant actually covered by the drag.
      const lastCoveredInstant = new Date(dragEnd.getTime() - 1);
      const lastCoveredDay = new Date(lastCoveredInstant.getFullYear(), lastCoveredInstant.getMonth(), lastCoveredInstant.getDate());
      const checkDate = new Date(dragStart.getFullYear(), dragStart.getMonth(), dragStart.getDate());
      while (checkDate <= lastCoveredDay) {
        if (isNonWorkingDay(resource, checkDate)) {
          if (context?.finalize) context.finalize(false);
          if (eventRecord.remove) eventRecord.remove();
          cleanupPhantomEvents();
          return false as unknown as void;
        }
        checkDate.setDate(checkDate.getDate() + 1);
      }
      if (refuseOutOfContract(resource, dragStart, dragEnd)) {
        if (context?.finalize) context.finalize(false);
        if (eventRecord.remove) eventRecord.remove();
        cleanupPhantomEvents();
        return false as unknown as void;
      }
    }

    if (resource && onDragSelect) {
      const startDate = (eventRecord as any).startDate;



      let endDate = (eventRecord as any).endDate;

      // For non-day views, Bryntum snaps to the START of the next period:
      // - Week/Month views (day snapping): Mon 00:00 → Tue 00:00 for single day
      // - Quarter view (week snapping): Mon 00:00 → next Mon 00:00 for single week
      // - Year view (month snapping): Jan 1 00:00 → Feb 1 00:00 for single month
      // We subtract one day from endDate to get the actual last day of the booking
      if (calendarView !== 'day') {
        if (endDate.getHours() === 0 && endDate.getMinutes() === 0) {
          const adjustedEndDate = new Date(endDate);
          adjustedEndDate.setDate(adjustedEndDate.getDate() - 1);
          endDate = adjustedEndDate;
        }
      }

      // Extract projectId from combined ID in project-resource mode
      // Combined IDs are in format: "projectId-resourceId"
      let bookingData: Partial<Booking> | undefined;
      const combinedId = resourceRecord.data.id;
      if (groupingMode === 'project-resource' && combinedId.includes('-') && !combinedId.startsWith('group-') && !combinedId.startsWith('project-')) {
        const parts = combinedId.split('-');
        if (parts.length >= 2) {
          const projectId = parts[0];
          bookingData = { projectId };
        }
      }
      const customerName = extractCustomerName(combinedId);
      if (customerName) {
        bookingData = { ...bookingData, customer: customerName };
      }

      // DYNAMIC GRANULARITY DETECTION:
      // Determine durationType based on the ACTUAL timeline granularity (from viewPreset)
      // rather than just the nominal view mode. This allows for zoom-aware pre-selection.

      // Get the actual time resolution from the current viewPreset
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      const currentViewPreset = scheduler?.viewPreset as { timeResolution?: { unit?: string; increment?: number } } | undefined;
      const timeResolutionUnit = currentViewPreset?.timeResolution?.unit;

      // Determine if we should use hourly duration type:
      // Only when the timeline is displaying hourly granularity (hour or minute units)
      // Cross-day drag in day-level views (month, week, quarter) should remain full-day
      const isHourlyGranularity = timeResolutionUnit === 'hour' || timeResolutionUnit === 'minute';

      if (isHourlyGranularity) {
        bookingData = { ...bookingData, durationType: 'hourly' };
      }
      // Otherwise, let the handler decide based on the view type (will default to 'full-day')

      onDragSelect(
        resource,
        startDate,
        { hour: startDate.getHours(), minute: startDate.getMinutes() },
        { hour: endDate.getHours(), minute: endDate.getMinutes() },
        bookingData,
        endDate
      );
    }
    // Prevent Bryntum from finalizing the event creation
    // This tells Bryntum not to add the temporary event to the store
    if (context?.finalize) {
      context.finalize(false);
    }
    // Also explicitly remove the temporary event if it was added
    // This ensures the event is removed even if finalize doesn't work as expected
    if (eventRecord.remove) {
      eventRecord.remove();
    }
    // Clean up any remaining phantom events as a safety net
    cleanupPhantomEvents();
    // Return false to prevent the event from being added
    return false;
  }, [findResourceById, onDragSelect, calendarView, zoomLevel, groupingMode, cleanupPhantomEvents, isNonWorkingDay, isNonBusinessHour, isCoarseGranularityView, refuseOutOfContract]);

  // Attach click handler to expand/collapse all button
  useEffect(() => {
    const handleExpandCollapseClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const button = target.closest('#expand-collapse-all-btn');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        toggleAllGroups();
      }
    };

    document.addEventListener('click', handleExpandCollapseClick, true);
    return () => {
      document.removeEventListener('click', handleExpandCollapseClick, true);
    };
  }, [toggleAllGroups]);

  // Sync the global expand/collapse button when individual groups are toggled.
  // Defers the check to requestAnimationFrame so Bryntum has finished updating
  // the record's expanded property before we read it.
  useEffect(() => {
    let rafId = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    type Detacher = (() => void) | { destroy?: () => void } | undefined;
    const detachers: Detacher[] = [];
    const cleanup = (d: Detacher) => {
      if (typeof d === 'function') d();
      else if (d && typeof d.destroy === 'function') d.destroy();
    };

    // Toggles are collected per animation frame, so "expand all" arrives as one
    // batch rather than one reveal per group.
    const toggled: GroupToggles = { opened: [], closed: [] };

    const deferredSync = (event?: ToggleNodeEvent) => {
      // Raised by collapse all or expand all applying itself row by row: the
      // button has already set which groups are collapsed, and reading these
      // back would hand every group it opened a page of its own rows.
      if (bulkGroupToggleRef.current) return;

      const id = event?.record?.id;
      if (id != null && typeof event?.collapse === 'boolean') {
        (event.collapse ? toggled.closed : toggled.opened).push(String(id));
      }

      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        syncGlobalExpandState();
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (scheduler && !scheduler.isDestroyed) {
          syncCollapsedGroupIdsRef.current(scheduler, toggled);
        }
        toggled.opened.length = 0;
        toggled.closed.length = 0;
      });
    };

    // The scheduler is created by the child component, so the instance can be
    // missing on the first run and the listeners have to be retried until it
    // exists — otherwise the button never follows individual group toggles.
    const attach = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) {
        retryTimer = setTimeout(attach, 50);
        return;
      }

      // Listen on the resource store for tree-toggle events
      const store = scheduler.resourceStore as unknown as {
        on?: (event: string, handler: (event?: ToggleNodeEvent) => void) => Detacher;
      };
      if (store.on) {
        detachers.push(store.on('toggleNode', deferredSync));
      }

      // Also listen on the Tree feature for expand/collapse events
      const tree = (scheduler as unknown as {
        features?: { tree?: { on?: (event: string, handler: (event?: ToggleNodeEvent) => void) => Detacher } };
      }).features?.tree;
      if (tree?.on) {
        detachers.push(tree.on('collapseNode', deferredSync));
        detachers.push(tree.on('expandNode', deferredSync));
      }

      // Individual toggles are relayed by the scheduler itself; the store and
      // tree feature stay silent, so it is the only reliable source here.
      const schedulerOn = (scheduler as unknown as {
        on?: (event: string, handler: (event?: ToggleNodeEvent) => void) => Detacher;
      }).on;
      if (schedulerOn) {
        for (const event of ['toggleNode', 'expandNode', 'collapseNode', 'renderRows']) {
          detachers.push(schedulerOn.call(scheduler, event, deferredSync));
        }
      }

      deferredSync();
    };

    attach();

    return () => {
      cancelAnimationFrame(rafId);
      if (retryTimer) clearTimeout(retryTimer);
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (scheduler && !scheduler.isDestroyed) {
        detachers.forEach(cleanup);
      }
      detachers.length = 0;
    };
  }, [syncGlobalExpandState, schedulerKey]);

  // Stable ref for booking permission check — used inside event handlers / effects
  const isBookingRestrictedRef = useRef(isBookingRestricted);
  isBookingRestrictedRef.current = isBookingRestricted;

  // Selecting one tile of a multi-resource booking selects all of its sibling
  // tiles without disturbing an existing selection of unrelated bookings.
  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let detacher: (() => void) | undefined;

    const attach = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler) {
        retryTimer = setTimeout(attach, 50);
        return;
      }

      const handleEventSelectionChange = ({
        action,
        selected,
      }: {
        source: Scheduler;
        action: 'select' | 'deselect' | 'update' | 'clear';
        selected: EventModel[];
        deselected: EventModel[];
        selection: EventModel[];
      }) => {
        if (action === 'deselect' || action === 'clear') return;
        if (siblingSelectionMutationRef.current) return;

        siblingSelectionMutationRef.current = true;
        try {
          const selectedEventIds: string[] = [];

          for (const eventRecord of selected) {
            const event = eventRecord as unknown as SelectedEventLike;
            const data = event.data;
            if (data?.isAbsenceEvent || data?.isHeatmapEvent || data?.isWhatIfGhost) continue;

            const rawEventId = eventRecord.id ?? data?.id;
            if (String(rawEventId ?? '').startsWith(YEAR_SUMMARY_EVENT_ID_PREFIX)) continue;
            if (rawEventId != null) selectedEventIds.push(String(rawEventId));
          }

          const events = new Map(
            (scheduler.eventStore.getRange() as EventModel[])
              .map(event => [String(event.id), event]),
          );
          const linked = linkedEventIds(
            [...new Set([
              ...scheduler.selectedEvents.map(event => String(event.id)),
              ...selectedEventIds,
            ])],
            [...events.keys()],
            bookingId => findBookingByIdRef.current(bookingId),
          );
          const missing = linked
            .map(eventId => events.get(eventId))
            .filter((event): event is EventModel => event != null);

          if (missing.length > 0) {
            scheduler.selectEvents(missing, true);
          }
        } finally {
          siblingSelectionMutationRef.current = false;
        }
      };

      detacher = scheduler.on('eventSelectionChange', handleEventSelectionChange) as () => void;
    };

    attach();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (typeof detacher === 'function') detacher();
    };
  }, [schedulerKey]);

  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let detachers: Array<(() => void) | undefined> = [];

    const attach = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler) {
        retryTimer = setTimeout(attach, 50);
        return;
      }

      const readSelection = (): Booking[] => bookingsFromSelectedEvents(
        (scheduler as unknown as { selectedEvents?: SelectedEventLike[] }).selectedEvents ?? [],
        {
          findBooking: id => findBookingByIdRef.current(id),
          isLocked: id => lockedBookingIdsRef.current?.has(id) === true,
          isRestricted: b => isBookingRestrictedRef.current(b),
        },
      );
      // Bryntum can collapse a multi-selection while a resize is in flight, so the
      // selection is snapshotted as soon as the resize gesture starts.
      const captureResizeSelection = () => {
        selectionAtResizeStartRef.current = readSelection();
      };
      detachers = [
        scheduler.on('beforeEventResize', captureResizeSelection) as () => void,
        scheduler.on('eventResizeStart', captureResizeSelection) as () => void,
      ];
    };

    attach();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      detachers.forEach(detacher => {
        if (typeof detacher === 'function') detacher();
      });
    };
  }, [schedulerKey]);

  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let detacher: (() => void) | undefined;
    let eventDrag: {
      constrainDragToResource?: boolean;
      constrainDragToTimeline?: boolean;
      unifiedDrag?: boolean;
    } | false | undefined;
    let originalConstrainDragToTimeline: boolean | undefined;
    let originalUnifiedDrag: boolean | undefined;

    const attach = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler) {
        retryTimer = setTimeout(attach, 50);
        return;
      }

      eventDrag = scheduler.features.eventDrag as {
        constrainDragToResource?: boolean;
        constrainDragToTimeline?: boolean;
        unifiedDrag?: boolean;
      } | false | undefined;
      originalConstrainDragToTimeline = typeof eventDrag === 'object'
        ? eventDrag.constrainDragToTimeline ?? true
        : undefined;
      originalUnifiedDrag = typeof eventDrag === 'object'
        ? eventDrag.unifiedDrag ?? true
        : undefined;
      const selectedEvents = (): SelectedEventLike[] =>
        (scheduler as unknown as { selectedEvents?: SelectedEventLike[] }).selectedEvents ?? [];

      const handleBeforeEventDrag = ({
        eventRecord,
        eventRecords,
      }: {
        eventRecord?: { id?: string | number; data?: { id?: string | number } };
        eventRecords?: Array<{ id?: string | number; data?: { id?: string | number } }>;
      }) => {
        const currentSelection = selectedEvents();
        const selectedBookings = bookingsFromSelectedEvents(currentSelection, {
          findBooking: id => findBookingByIdRef.current(id),
          isLocked: id => lockedBookingIdsRef.current?.has(id) === true,
          isRestricted: b => isBookingRestrictedRef.current(b),
        });
        const selectedBookingIds = new Set(selectedBookings.map(booking => String(booking.id)));
        const selectedBookingTileCount = currentSelection.reduce((count, eventRecord) => {
          const rawId = eventRecord.id ?? eventRecord.data?.id;
          if (rawId == null) return count;

          const booking = findBookingByIdRef.current(String(rawId)) ?? eventRecord.data?.originalBooking;
          return booking && selectedBookingIds.has(String(booking.id)) ? count + 1 : count;
        }, 0);
        const draggedRecord = eventRecord ?? eventRecords?.[0];
        const rawDraggedId = draggedRecord?.id ?? draggedRecord?.data?.id;
        const draggedBookingId = rawDraggedId == null
          ? undefined
          : baseBookingIdFromEventId(String(rawDraggedId));
        const isMultiSelectionDrag = selectedBookingTileCount > 1
          && draggedBookingId !== undefined
          && selectedBookingIds.has(draggedBookingId);

        selectionAtDragStartRef.current = selectedBookings;
        if (eventDrag) {
          eventDrag.constrainDragToResource = isMultiSelectionDrag;
          eventDrag.constrainDragToTimeline = isMultiSelectionDrag
            ? true
            : originalConstrainDragToTimeline;
          eventDrag.unifiedDrag = isMultiSelectionDrag
            ? false
            : originalUnifiedDrag;
        }
        isMultiSelectionDragRef.current = isMultiSelectionDrag;
      };

      detacher = scheduler.on('beforeEventDrag', handleBeforeEventDrag) as () => void;
    };

    attach();

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      if (typeof detacher === 'function') detacher();
      if (eventDrag) {
        eventDrag.constrainDragToResource = false;
        eventDrag.constrainDragToTimeline = originalConstrainDragToTimeline;
        eventDrag.unifiedDrag = originalUnifiedDrag;
      }
    };
  }, [schedulerKey]);

  // Stable refs for role-based booking permissions so the context-menu closures
  // (created once per effect run) always read the latest values.
  const canCreateBookingRef = useRef(canCreateBooking);
  canCreateBookingRef.current = canCreateBooking;
  const quickAddEnabledRef = useRef(quickAddEnabled);
  quickAddEnabledRef.current = quickAddEnabled;
  const canEditBookingRef = useRef(canEditBooking);
  canEditBookingRef.current = canEditBooking;
  const canDeleteBookingRef = useRef(canDeleteBooking);
  canDeleteBookingRef.current = canDeleteBooking;

  // In project-resource and customer modes the composite row ID is passed on so the
  // booking form can pre-populate the project / customer.
  const addBookingForRow = useCallback((resourceId: string, date: Date) => {
    const resource = findResourceById(resourceId);
    if (!resource || !onAddBooking) return;
    if (groupingMode === 'project-resource' && resourceId.includes('-') &&
      !resourceId.startsWith('group-') && !resourceId.startsWith('project-')) {
      onAddBooking({ ...resource, id: resourceId }, date);
    } else if (groupingMode === 'customer' && resourceId.includes('-') &&
      !resourceId.startsWith('group-') && !resourceId.startsWith('customer-')) {
      onAddBooking({ ...resource, id: resourceId }, date);
    } else {
      onAddBooking(resource, date);
    }
  }, [findResourceById, onAddBooking, groupingMode]);

  const quickAddBookingForRow = useCallback((resourceId: string, date: Date) => {
    const resource = findResourceById(resourceId);
    if (!resource || !onQuickAddBooking) return;
    if (groupingMode === 'project-resource' && resourceId.includes('-') &&
      !resourceId.startsWith('group-') && !resourceId.startsWith('project-')) {
      onQuickAddBooking({ ...resource, id: resourceId }, date);
    } else {
      onQuickAddBooking(resource, date);
    }
  }, [findResourceById, onQuickAddBooking, groupingMode]);

  // Delegated click handler for "Edit" buttons inside heatmap tooltip booking rows
  const onEditBookingRef = useRef(onEditBooking);
  onEditBookingRef.current = onEditBooking;

  // Delegated click handler for "Resolve" buttons inside heatmap tooltip booking rows
  const onResolveConflictRef = useRef(onResolveConflict);
  onResolveConflictRef.current = onResolveConflict;

  useEffect(() => {
    const handleHeatmapEditClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-heatmap-edit-booking]') as HTMLElement | null;
      if (!btn) return;
      const bookingId = btn.getAttribute('data-heatmap-edit-booking');
      if (!bookingId) return;
      e.preventDefault();
      e.stopPropagation();
      const booking = bookings.find(bk => String(bk.id) === String(bookingId));
      if (booking && onEditBookingRef.current) {
        // Hide the Bryntum tooltip first so it doesn't linger behind the modal
        const scheduler = schedulerRef.current?.instance as any;
        scheduler?.features?.eventTooltip?.tooltip?.hide?.();
        onEditBookingRef.current(booking, booking.resourceId ?? undefined);
      }
    };
    document.addEventListener('click', handleHeatmapEditClick, true);
    return () => {
      document.removeEventListener('click', handleHeatmapEditClick, true);
    };
  }, [bookings]);

  useEffect(() => {
    const handleHeatmapResolveClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('[data-heatmap-resolve-bookings]') as HTMLElement | null;
      if (!btn) return;
      const bookingIdsAttr = btn.getAttribute('data-heatmap-resolve-bookings');
      if (!bookingIdsAttr) return;
      e.preventDefault();
      e.stopPropagation();
      // Decode the comma-separated list of IDs and look up each booking
      const ids = bookingIdsAttr.split(',').map(id => id.trim()).filter(Boolean);
      const cellBookings = ids
        .map(id => bookings.find(bk => String(bk.id) === id))
        .filter((bk): bk is Booking => bk !== undefined);
      if (cellBookings.length > 0 && onResolveConflictRef.current) {
        // Hide the Bryntum tooltip first so it doesn't linger behind the dialog
        const scheduler = schedulerRef.current?.instance as any;
        scheduler?.features?.eventTooltip?.tooltip?.hide?.();
        onResolveConflictRef.current(cellBookings);
      }
    };
    document.addEventListener('click', handleHeatmapResolveClick, true);
    return () => {
      document.removeEventListener('click', handleHeatmapResolveClick, true);
    };
  }, [bookings]);

  // Delegated click handler for booking rows inside a YEAR summary tile's list
  useEffect(() => {
    const handleYearSummaryBookingClick = (e: MouseEvent) => {
      const more = (e.target as HTMLElement).closest('[data-year-summary-more]');
      if (more) {
        e.preventDefault();
        e.stopPropagation();
        more.closest('.eb-ys-list')?.classList.add('eb-ys-expanded');
        return;
      }
      const add = (e.target as HTMLElement).closest<HTMLElement>('[data-year-summary-add]');
      if (add) {
        e.preventDefault();
        e.stopPropagation();
        const rowId = add.dataset.yearSummaryRow;
        const date = new Date(Number(add.dataset.yearSummaryDate));
        if (!rowId || Number.isNaN(date.getTime()) || !canCreateBookingRef.current) return;
        unpinYearSummaryTip(true);
        const rowResource = findResourceById(rowId);
        if (rowResource && !monthHasContractedDay(rowResource, date)) {
          refuseOutOfContract(rowResource, date);
          return;
        }
        if (add.dataset.yearSummaryAdd === 'quick') {
          if (quickAddEnabledRef.current) quickAddBookingForRow(rowId, date);
        } else {
          addBookingForRow(rowId, date);
        }
        return;
      }
      const btn = (e.target as HTMLElement).closest('[data-year-summary-booking]') as HTMLElement | null;
      if (!btn) return;
      const bookingId = btn.getAttribute('data-year-summary-booking');
      if (!bookingId) return;
      e.preventDefault();
      e.stopPropagation();
      const booking = bookings.find(bk => String(bk.id) === bookingId);
      if (booking && onEditBookingRef.current) {
        unpinYearSummaryTip(true);
        onEditBookingRef.current(booking, btn.getAttribute('data-year-summary-resource') ?? undefined);
      }
    };
    document.addEventListener('click', handleYearSummaryBookingClick, true);
    return () => {
      document.removeEventListener('click', handleYearSummaryBookingClick, true);
    };
  }, [bookings, unpinYearSummaryTip, addBookingForRow, quickAddBookingForRow, findResourceById, refuseOutOfContract]);



  // Ensure createEventOnDblClick is disabled after every mount/remount.
  // After navigating away from the calendar and back, the Bryntum React wrapper
  // may not properly re-apply the createEventOnDblClick config from props.
  // This useEffect explicitly sets it on the scheduler instance.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    (scheduler as any).createEventOnDblClick = false;
  }, []);


  // Prevent Bryntum's default event edit form from opening
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Listen for beforeEventEdit to prevent the default edit form
    const detacher = scheduler.on('beforeEventEdit', () => {
      // Return false to prevent Bryntum's event edit form from opening
      return false;
    });

    return () => {
      if (typeof detacher === 'function') {
        detacher();
      }
    };
  }, []);

  // Re-render suggested/plan-preview events when the view preset changes so the
  // correct visual treatment (dashed/solid/stripe) is applied for the new scale.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as any;
    if (!scheduler) return;

    const detacher = scheduler.on('zoomChange', () => {
      // refreshRows re-invokes eventRenderer for every visible event, picking
      // up the new viewPreset.name without requiring a full data reload.
      scheduler.refreshRows();
    });

    return () => {
      if (typeof detacher === 'function') detacher();
    };
  }, []);

  // Prevent drag and resize on repeat bookings and bookings the user cannot edit.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    const denyEdit = () => {
      toast.error('Permission Denied', {
        description: "You don't have permission to edit this booking.",
      });
      return false;
    };

    const detachDrag = scheduler.on('beforeEventDrag', ({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
      if (eventRecord.data?.isAbsenceEvent || eventRecord.data?.yearSummary) return false;
      if (!canEditBookingRef.current) return denyEdit();
      const booking = findBookingById(eventRecord.data.id);
      if (booking?.recurrenceGroupId) return false;
      if (booking && isBookingRestrictedRef.current(booking)) return denyEdit();
    });
    const detachResize = scheduler.on('beforeEventResize', ({ eventRecord }: { eventRecord: { data: BryntumEvent } }) => {
      if (eventRecord.data?.isAbsenceEvent || eventRecord.data?.yearSummary) return false;
      if (!canEditBookingRef.current) return denyEdit();
      const booking = findBookingById(eventRecord.data.id);
      if (booking?.recurrenceGroupId) return false;
      if (booking && isBookingRestrictedRef.current(booking)) return denyEdit();
    });

    return () => {
      if (typeof detachDrag === 'function') detachDrag();
      if (typeof detachResize === 'function') detachResize();
    };
  }, [findBookingById]);

  // Prevent right-click context menu on timeline header
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    // Aggressively disable header-related menu features on the scheduler instance
    // Note: scheduleMenu and eventMenu are NOT disabled here because custom context
    // menus are wired up via scheduler.on() listeners in separate useEffect hooks
    const headerFeatureNames = ['timeAxisHeaderMenu', 'headerMenu', 'cellMenu', 'contextMenu'] as const;
    for (const featureName of headerFeatureNames) {
      const feature = (scheduler.features as unknown as Record<string, unknown>)[featureName] as { disabled?: boolean; menu?: { destroy?: () => void } } | undefined;
      if (feature && typeof feature === 'object') {
        feature.disabled = true;
        // Destroy any already-created menu widget
        if (feature.menu && typeof feature.menu.destroy === 'function') {
          feature.menu.destroy();
        }
      }
    }

    // Block contextmenu at the document level for all header-related elements
    // This catches the event before Bryntum can process it
    const globalHandler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target?.closest('.b-grid-headers, .b-sch-header-row, .b-sch-timeaxiscolumn, .b-sch-header-timeaxis-cell, .b-grid-header-scroller, .b-grid-header-container, .b-timeaxis-cell, .b-sch-header')) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    };
    document.addEventListener('contextmenu', globalHandler, true);

    return () => {
      document.removeEventListener('contextmenu', globalHandler, true);
    };
  }, []);

  // Custom context menu for empty schedule cells
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    let contextMenu: Menu | null = null;

    const handleScheduleContextMenu = (event: { resourceRecord?: { id: string }; date?: Date; domEvent: MouseEvent }) => {
      // Prevent default browser context menu
      event.domEvent.preventDefault();

      // Only show menu for resource rows (not group rows)
      if (!event.resourceRecord || !event.date) return;

      // Check if this is a group row (has children)
      const record = event.resourceRecord as unknown as { children?: unknown[] };
      if (record.children && record.children.length > 0) return;

      // Also check if this is a group node (group-, project-, role-, or customer- prefix)
      const resourceId = event.resourceRecord.id;
      if (resourceId.startsWith('group-') || resourceId.startsWith('project-') || resourceId.startsWith('role-') || resourceId.startsWith('customer-')) return;
      const clickDate = event.date;

      // Block context menu on cells covered by an absence
      if (isDateInAbsenceRef.current(resourceId, clickDate)) return;

      // Check if this is a non-working day for the resource - disable context menu if so.
      // Skipped in Quarter/Year views where a cell spans many days (would always match a weekend).
      const resource = findResourceById(resourceId);
      if (!isCoarseGranularityView && resource && isNonWorkingDay(resource, clickDate)) {
        // Don't show context menu on non-working days
        return;
      }

      // Check if this is during non-business hours - disable context menu if so (Day view)
      if (isNonBusinessHour(clickDate)) {
        return;
      }

      // No Add / Quick Add / Paste menu on a day outside the resource's contract.
      if (!isCoarseGranularityView && resource && refuseOutOfContract(resource, clickDate)) {
        return;
      }

      // Destroy previous menu if exists
      if (contextMenu) {
        contextMenu.destroy();
      }

      // SVG icons for menu items
      const addIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>';
      const boltIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>';
      const pasteIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/></svg>';

      // Build menu items. Add/Quick Add/Paste are all create operations, so they
      // are only shown when the user's role grants the createBooking permission.
      const canCreate = canCreateBookingRef.current;
      const quickAddIsEnabled = quickAddEnabledRef.current;
      const menuItems: Array<{ html: string; onItem: () => void }> = [];

      if (canCreate) {
        menuItems.push(
          {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${addIcon}<span>Add Booking</span></div>`,
            onItem: () => addBookingForRow(resourceId, clickDate),
          },
        );
      }

      // Quick Add Booking is only shown when the user can create bookings and
      // quick add is enabled (Quick Add Default is not set to "None").
      if (canCreate && quickAddIsEnabled) {
        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${boltIcon}<span>Quick Add Booking</span></div>`,
          onItem: () => quickAddBookingForRow(resourceId, clickDate),
        });
      }

      // Only add Paste Booking if the user can create and a booking has been copied
      if (canCreate && copiedBooking) {
        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${pasteIcon}<span>Paste Booking</span></div>`,
          onItem: () => {
            const resource = findResourceById(resourceId);
            if (resource && onPasteBooking && copiedBooking) {
              const startTime = { hour: clickDate.getHours(), minute: clickDate.getMinutes() };
              onPasteBooking(resource, clickDate, startTime, copiedBooking);
            }
          },
        });
      }

      // Nothing the user is allowed to do here — don't show an empty menu.
      if (menuItems.length === 0) return;

      // Create custom context menu with smaller styling
      contextMenu = new Menu({
        anchor: true,
        autoShow: false,
        cls: 'custom-booking-menu',
        items: menuItems,
      });

      // Show menu at mouse position
      showMenuAtPointer(contextMenu, event.domEvent.clientX, event.domEvent.clientY);
    };

    // A YEAR summary tile covers its whole month, so right-clicking it offers the
    // same menu as an empty cell, for the day under the pointer.
    const handleYearSummaryContextMenu = (event: { eventRecord?: { data: BryntumEvent }; resourceRecord?: { id: string }; domEvent: MouseEvent }) => {
      if (!event.eventRecord?.data?.yearSummary) return;
      const date = scheduler.getDateFromDomEvent(event.domEvent);
      if (!date) return;
      handleScheduleContextMenu({ resourceRecord: event.resourceRecord, date, domEvent: event.domEvent });
    };

    // Add listener for schedule context menu - use the detacher function returned by on()
    const detacher = scheduler.on('scheduleContextMenu', handleScheduleContextMenu);
    const yearSummaryDetacher = scheduler.on('eventContextMenu', handleYearSummaryContextMenu);

    return () => {
      // Use the detacher function to remove the listener
      if (typeof detacher === 'function') {
        detacher();
      }
      if (typeof yearSummaryDetacher === 'function') {
        yearSummaryDetacher();
      }
      if (contextMenu) {
        contextMenu.destroy();
      }
    };
  }, [findResourceById, addBookingForRow, quickAddBookingForRow, onPasteBooking, copiedBooking, isNonWorkingDay, isNonBusinessHour, groupingMode, quickAddEnabled, isCoarseGranularityView, refuseOutOfContract]);

  // Custom context menu for events (bookings)
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    let eventContextMenu: Menu | null = null;

    // The bookings currently Ctrl/Cmd-selected on the timeline.
    const readSelection = (): Booking[] => bookingsFromSelectedEvents(
      (scheduler as unknown as { selectedEvents?: SelectedEventLike[] }).selectedEvents ?? [],
      {
        findBooking: id => findBookingByIdRef.current(id),
        isLocked: id => lockedBookingIdsRef.current?.has(id) === true,
        isRestricted: b => isBookingRestrictedRef.current(b),
      },
    );

    // Bryntum selects the event under the pointer on right-click, which would
    // collapse a multi-selection before the menu is built — so the selection is
    // snapshotted in the capture phase, ahead of Bryntum's own mousedown handling.
    let selectionAtRightClick: Booking[] = [];
    const captureSelection = (domEvent: MouseEvent) => {
      if (domEvent.button === 2) selectionAtRightClick = readSelection();
    };
    const schedulerEl = (scheduler as unknown as { element?: HTMLElement }).element;
    schedulerEl?.addEventListener('mousedown', captureSelection, true);

    const handleEventContextMenu = (event: { eventRecord?: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown }; domEvent: MouseEvent }) => {
      // Prevent default browser context menu
      event.domEvent.preventDefault();

      if (!event.eventRecord) return;

      // Block context menu on absence events and YEAR summary tiles
      if (event.eventRecord.data?.isAbsenceEvent || event.eventRecord.data?.yearSummary) return;

      // Look up the latest booking from the bookings array first so that
      // tooltips and context menus always reflect the most recent data
      // (including changes made via a different resource row for the same
      // multi-resource booking).
      const eventRecord = event.eventRecord;
      const eventId = (eventRecord as unknown as { id?: string }).id || eventRecord.data?.id || (typeof eventRecord.get === 'function' ? eventRecord.get('id') as string : null);
      let booking: Booking | undefined;
      if (eventId) {
        booking = findBookingByIdRef.current(String(eventId));
      }

      // Fallback: try originalBooking stored on the event record
      if (!booking) {
        booking = eventRecord.data?.originalBooking;
      }
      if (!booking && eventRecord.originalData) {
        booking = eventRecord.originalData.originalBooking;
      }
      if (!booking && typeof eventRecord.get === 'function') {
        booking = eventRecord.get('originalBooking') as Booking | undefined;
      }

      if (!booking) {
        console.warn('Could not find booking for event context menu');
        return;
      }

      // Locked occurrences are being deleted in the background — suppress the
      // right-click menu entirely so nothing can be changed on them.
      if (lockedBookingIdsRef.current?.has(String(booking.id))) return;

      // Extract the resource ID from the event record for context-aware conflict filtering
      const contextResourceId = eventRecord.data?.resourceId;

      // Right-clicking inside a multi-selection applies bulk actions to the
      // whole selection; right-clicking anything else targets that booking alone.
      const actionTargets = bulkActionTargets(
        booking,
        selectionAtRightClick.length > 0 ? selectionAtRightClick : readSelection(),
      );
      // The pieces a split booking was broken into are selected together and stand for the
      // one booking the user sees, so a menu opened on one of them is not a bulk action.
      const targetCount = distinctBookingCount(actionTargets);
      const isBulk = targetCount > 1;
      const deleteLabel = isBulk ? `Delete ${targetCount} Bookings` : 'Delete Booking';
      const runDelete = () => {
        if (isBulk && onDeleteBookings) {
          onDeleteBookings(actionTargets);
        } else if (onDeleteBooking) {
          onDeleteBooking(booking);
        }
      };

      // Destroy previous menu if exists
      if (eventContextMenu) {
        eventContextMenu.destroy();
      }

      // Role lacks the editBooking permission. If the user can still delete this
      // booking (deleteBooking permission and the booking type isn't read-only),
      // show a menu containing only "Delete Booking"; otherwise show nothing.
      if (!canEditBookingRef.current) {
        if (!canDeleteBookingRef.current || isBookingRestrictedRef.current(booking)) return;

        const deleteOnlyTrashIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';

        eventContextMenu = new Menu({
          anchor: true,
          autoShow: false,
          cls: 'custom-booking-menu',
          items: [
            {
              html: `<div style="display: flex; align-items: center; gap: 8px;">${deleteOnlyTrashIcon}<span style="font-size: 12px; font-weight: 400;">${deleteLabel}</span></div>`,
              onItem: runDelete,
            },
          ],
        });

        showMenuAtPointer(eventContextMenu, event.domEvent.clientX, event.domEvent.clientY);
        return;
      }

      // Check whether the user has permission to edit this booking. A caller is
      // restricted when the booking type is read-only for them — the menu is
      // then reduced to "View Booking" only.
      const restricted = isBookingRestrictedRef.current(booking);

      // SVG icons for event menu items
      const editIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>';
      const viewIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
      const copyIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
      const moveIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/></svg>';
      const trashIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>';
      const checkIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>';
      const chevronIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>';

      // Filter to only show enabled statuses
      const enabledStatuses = statusConfigs.filter(status => status.enabled);

      // Build menu items for event context menu
      const menuItems: Array<{
        html: string;
        disabled?: boolean;
        onItem?: () => void;
        menu?: Array<{
          html: string;
          onItem: () => void;
        }>;
      }> = restricted
        ? [
          // Restricted users can only view the booking
          {
            disabled: isBulk,
            html: `<div style="display: flex; align-items: center; gap: 8px;">${viewIcon}<span style="font-size: 12px; font-weight: 400;">View Booking</span></div>`,
            onItem: () => {
              if (onEditBooking) {
                onEditBooking(booking, contextResourceId);
              }
            },
          },
        ]
        : [
          {
            disabled: isBulk,
            html: `<div style="display: flex; align-items: center; gap: 8px;">${editIcon}<span style="font-size: 12px; font-weight: 400;">Edit Booking</span></div>`,
            onItem: () => {
              if (onEditBooking) {
                onEditBooking(booking, contextResourceId);
              }
            },
          },
          {
            disabled: !canCopyBooking(booking, isBulk),
            html: `<div style="display: flex; align-items: center; gap: 8px;">${copyIcon}<span style="font-size: 12px; font-weight: 400;">Copy Booking</span></div>`,
            onItem: () => {
              if (onCopyBooking && canCopyBooking(booking, isBulk)) {
                onCopyBooking(booking);
              }
            },
          },
          {
            disabled: !canMoveBooking(booking, isBulk),
            html: `<div style="display: flex; align-items: center; gap: 8px;">${moveIcon}<span style="font-size: 12px; font-weight: 400;">Move</span></div>`,
            onItem: () => {
              if (onMoveBooking) {
                onMoveBooking(booking);
              }
            },
          },
        ];

      // Add Status submenu if there are enabled statuses and a handler is
      // provided (only for full-access users).
      if (!restricted && (onStatusChange || onStatusChangeBookings) && enabledStatuses.length > 0) {
        const statusMenuItems = enabledStatuses.map((status) => {
          const displayText = status.customText || status.text;
          const isCurrentStatus = actionTargets.every(target => String(target.status) === String(status.id));
          const colorCircle = `<div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${status.color}; flex-shrink: 0;"></div>`;
          const checkmark = isCurrentStatus ? checkIcon : '<span style="width: 14px; display: inline-block;"></span>';

          return {
            html: `<div style="display: flex; align-items: center; gap: 8px;">${colorCircle}<span style="font-size: 12px; ${isCurrentStatus ? 'font-weight: 600;' : 'font-weight: 400;'}">${displayText}</span>${checkmark}</div>`,
            onItem: () => {
              if (isBulk && onStatusChangeBookings) {
                onStatusChangeBookings(actionTargets, String(status.id), status.color);
              } else if (onStatusChange) {
                onStatusChange(booking, String(status.id), status.color);
              }
            },
          };
        });

        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;"><span style="font-size: 12px; font-weight: 400;">Change Status</span>${chevronIcon}</div>`,
          menu: statusMenuItems,
        });
      }

      // Add Delete at the end (only for full-access users who also hold the
      // deleteBooking role permission).
      if (!restricted && canDeleteBookingRef.current) {
        menuItems.push({
          html: `<div style="display: flex; align-items: center; gap: 8px;">${trashIcon}<span style="font-size: 12px; font-weight: 400;">${deleteLabel}</span></div>`,
          onItem: runDelete,
        });
      }

      // Create custom context menu with smaller styling
      eventContextMenu = new Menu({
        anchor: true,
        autoShow: false,
        cls: 'custom-booking-menu',
        items: menuItems,
      });

      // Show menu at mouse position
      showMenuAtPointer(eventContextMenu, event.domEvent.clientX, event.domEvent.clientY);
    };

    // Add listener for event context menu
    const detacher = scheduler.on('eventContextMenu', handleEventContextMenu);

    return () => {
      if (typeof detacher === 'function') {
        detacher();
      }
      schedulerEl?.removeEventListener('mousedown', captureSelection, true);
      if (eventContextMenu) {
        eventContextMenu.destroy();
      }
    };
  }, [onEditBooking, onCopyBooking, onMoveBooking, onDeleteBooking, onDeleteBookings, onStatusChange, onStatusChangeBookings, statusConfigs, bookings, isBookingRestricted]);

  // Deleting with the [Delete]/[Backspace] key. Bryntum's own handling only drops
  // the records from its event store, so the tiles come back with the next
  // timeline refresh — the removal is vetoed and routed through the same delete
  // the context menu uses, which persists it.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler) return;

    const handleBeforeEventDelete = ({ eventRecords }: { eventRecords?: SelectedEventLike[] }) => {
      if (!canDeleteBookingRef.current) return false;

      const targets = bookingsFromSelectedEvents(eventRecords ?? [], {
        findBooking: id => findBookingByIdRef.current(id),
        isLocked: id => lockedBookingIdsRef.current?.has(id) === true,
        isRestricted: b => isBookingRestrictedRef.current(b),
      });
      if (targets.length === 0) return false;

      if (targets.length > 1 && onDeleteBookings) {
        onDeleteBookings(targets);
      } else if (onDeleteBooking) {
        onDeleteBooking(targets[0]);
      }
      return false;
    };

    const detacher = scheduler.on('beforeEventDelete', handleBeforeEventDelete);

    return () => {
      if (typeof detacher === 'function') detacher();
    };
  }, [onDeleteBooking, onDeleteBookings]);

  // Rebuilding the columns makes Bryntum swap its column store and refresh every
  // rendered row, which throws while rows are being removed (collapseAll). The
  // header button is the only part that reacts to the expanded state and it is
  // kept in sync imperatively by updateExpandCollapseButton, so the definitions
  // read the state from a ref and never change identity.
  const columns = useMemo(() => {
    // Icon shows available action: minus (collapse) when expanded, plus (expand) when collapsed
    const expandCollapseSquareIcon = () => (allGroupsExpandedRef.current ? COLLAPSE_ALL_ICON : EXPAND_ALL_ICON);

    return [
    {
      type: 'tree',
      text: '',
      field: 'name',
      width: 260,
      htmlEncode: false,
      editor: false,
      editable: false,
      sortable: false,
      // Prevent Bryntum's JS from calculating or applying any tree indent.
      // CSS overrides alone don't survive re-mounts because Bryntum re-runs
      // its row rendering pipeline which re-applies padding-inline-start via
      // JS after our stylesheet rules are resolved. indentSize: 0 stops it
      // at the source — no indent is ever written to the DOM.
      indentSize: 0,
      headerRenderer: () => {
        // Only the expand/collapse button lives here — the grouping trigger is a
        // React overlay (see the .grouping-header-overlay div in the JSX return)
        // so it is never subject to Bryntum header click interception.
        return `
          <div style="display: flex; align-items: center; justify-content: flex-end; width: 100%; padding: 0 12px 0 0; height: 100%;">
            <button
              id="expand-collapse-all-btn"
              title="${allGroupsExpandedRef.current ? 'Collapse all' : 'Expand all'}"
              style="
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                width: 32px;
                height: 32px;
                border: 1px solid hsl(var(--border));
                border-radius: 6px;
                background: transparent;
                color: hsl(var(--foreground));
                cursor: pointer;
                transition: background 0.15s, border-color 0.15s;
              "
              onmouseover="this.style.background='hsl(var(--accent))'; this.style.borderColor='hsl(var(--ring))';"
              onmouseout="this.style.background='transparent'; this.style.borderColor='hsl(var(--border))';"
            >
              ${expandCollapseSquareIcon()}
            </button>
          </div>
        `;
      },
      renderer: ({ record, cellElement, row }: { record: { id?: string; name: string; role?: string; imageUrl?: string; children?: unknown[]; expanded?: boolean; isParent?: boolean; data?: { id?: string; name?: string; role?: string; imageUrl?: string; children?: unknown[]; isProjectGroup?: boolean; projectColor?: string }; get?: (field: string) => unknown }; cellElement?: HTMLElement; row?: { elements?: Record<string, HTMLElement> } }) => {
        // Access data - Bryntum may store it in record.data or directly on record
        const getData = (field: string): string | boolean | undefined => {
          if (record.get && typeof record.get === 'function') {
            return record.get(field) as string | boolean | undefined;
          }
          if (record.data && field in record.data) {
            return (record.data as unknown as Record<string, unknown>)[field] as string | boolean | undefined;
          }
          return (record as unknown as Record<string, unknown>)[field] as string | boolean | undefined;
        };

        const id = getData('id') as string || record.id || '';
        const name = getData('name') as string || record.name || '';
        const role = getData('role') as string || record.role;
        const imageUrl = getData('imageUrl') as string || record.imageUrl;
        const children = record.children || record.data?.children;
        const isExpanded = record.expanded;
        const isParent = record.isParent || (children && (children as unknown[]).length > 0);
        const isProjectGroup = getData('isProjectGroup') as boolean || record.data?.isProjectGroup;
        const projectColor = getData('projectColor') as string || record.data?.projectColor;

        // Alternate shading covers every other leaf row, skipping group headers.
        // The row carries an element per subgrid (locked and normal), so both are
        // shaded without searching the document for them.
        const allRowEls: HTMLElement[] = row?.elements ? Object.values(row.elements) : [];
        if (allRowEls.length === 0 && cellElement) {
          const rowEl = cellElement.closest('.b-grid-row') as HTMLElement | null;
          if (rowEl) allRowEls.push(rowEl);
        }

        if (allRowEls.length > 0) {
          const shouldShade = !isParent
            && (resourceRowIndexRef.current.altShading.get(String(record.id ?? id)) ?? false);
          allRowEls.forEach((el) => {
            if (shouldShade) {
              el.setAttribute('data-alt-shading', 'true');
            } else {
              el.removeAttribute('data-alt-shading');
            }
          });
        }

        // Group header row - show arrow icon and name (smaller text)
        if (isParent) {
          const arrowIcon = isExpanded
            ? `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="min-width: 12px;"><polyline points="6 9 12 15 18 9"></polyline></svg>`
            : `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="min-width: 12px;"><polyline points="9 18 15 12 9 6"></polyline></svg>`;

          // resourceCount is every resource of the group, not just the rows a
          // page has reached, so the badge stays honest while rows page in.
          const childCount = (getData('resourceCount') as unknown as number | undefined)
            ?? (record as any).childCount
            ?? (children ? (children as unknown[]).length : 0);
          const countBadge = childCount > 0
            ? `<span style="display: inline-flex; align-items: center; justify-content: center; min-width: 20px; padding: 0 5px; border-radius: 10px; font-size: 10px; font-weight: 600; color: #fff; background: rgba(0,0,0,0.35); line-height: 1.6;">${childCount}</span>`
            : '';

          // Project group - show with color indicator and project code
          if (isProjectGroup && projectColor) {
            const projectCode = getData('projectCode') as string || (record as any).data?.projectCode;

            return `
                          <div style="display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 12px; cursor: pointer;">
                            ${arrowIcon}
                            <div style="width: 10px; height: 10px; border-radius: 3px; background-color: ${projectColor}; flex-shrink: 0;"></div>
                            <div style="display: flex; flex-direction: column; gap: 2px;">
                              <span style="line-height: 1.2;">${escapeHtml(name)}</span>
                              ${projectCode ? `<span style="font-size: 10px; font-weight: 400; opacity: 0.7; line-height: 1.2;">${escapeHtml(projectCode)}</span>` : ''}
                            </div>
                            ${countBadge}
                          </div>
                        `;
          }

          // Regular department/scheduler group
          return `
                        <div style="display: flex; align-items: center; gap: 6px; font-weight: 600; font-size: 12px; cursor: pointer;">
                          ${arrowIcon}
                          <span>${escapeHtml(name)}</span>
                          ${countBadge}
                        </div>
                      `;
        }
        // Resource row - show profile image or teal-gradient initials fallback, name, utilisation %, and role
        const initials = (() => {
          const parts = name.trim().split(/\s+/);
          if (parts.length === 0) return '?';
          const first = parts[0]?.[0] || '';
          const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
          return (first + last).toUpperCase();
        })();
        const hasPhoto = !!imageUrl && imageUrl.trim() !== '' && imageUrl !== 'null' && imageUrl !== 'undefined';

        const origResId = (getData('originalResourceId') as string) || id;
        const resIdStr = String(origResId);
        const curTiers = settingsRef.current?.utilisationTiers || DEFAULT_UTILIZATION_TIERS;
        const isUnallocatedRow = resIdStr === UNALLOCATED_RESOURCE_ID;
        let utilPct = 0;
        let utilColor = '#6b7280';
        if (!isUnallocatedRow) {
          const utilisation = resourcePeriodUtilisationRef.current(resIdStr);
          if (utilisation) {
            utilPct = utilisation.utilizationPercentage;
            utilColor = getUtilizationColor(utilPct, curTiers);
          }
        }
        const utilBadge = `<span style="display: inline-flex; align-items: center; justify-content: center; min-width: 36px; padding: 1px 5px; border-radius: 4px; font-size: 10px; font-weight: 600; color: #1a1a1a; background: ${utilColor}; white-space: nowrap; line-height: 1.4;">${utilPct}%</span>`;
        // Unallocated rows represent unassigned demand, so a utilisation % is meaningless.
        const utilCell = isUnallocatedRow ? '' : utilBadge;

        return `
                      <div style="display: flex; align-items: center; gap: 6px; width: 100%; min-width: 0;">
                        <div style="position: relative; flex-shrink: 0;">
                          ${hasPhoto
                            ? `<img src="${escapeHtml(imageUrl || '')}" alt="${escapeHtml(name)}" loading="lazy" decoding="async" style="width: 30px; height: 30px; border-radius: 6px; object-fit: cover; display: block;" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';" /><div style="width: 30px; height: 30px; border-radius: 6px; display: none; align-items: center; justify-content: center; background: oklch(50% .0896 194.8); color: rgba(255,255,255,0.9); font-weight: 600; font-size: 11px; letter-spacing: 0.5px;">${escapeHtml(initials)}</div>`
                            : `<div style="width: 30px; height: 30px; border-radius: 6px; display: flex; align-items: center; justify-content: center; background: oklch(50% .0896 194.8); color: rgba(255,255,255,0.9); font-weight: 600; font-size: 11px; letter-spacing: 0.5px;">${escapeHtml(initials)}</div>`
                          }
                        </div>
                        <div style="flex: 1; min-width: 0; overflow: hidden;">
                          <div style="font-weight: 500; font-size: 12px; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(name)}</div>
                          ${role ? `<div style="font-size: 10px; opacity: 0.7; line-height: 1.2; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(role)}</div>` : ''}
                        </div>
                        <div style="flex-shrink: 0; margin-left: auto;">
                          ${utilCell}
                        </div>
                      </div>
                    `;
      },
    },
  ];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schedulerConfig = useMemo(() => {
    return {
      emptyText: ' ',
      // Ctrl/Cmd-click adds a booking to the selection instead of replacing it,
      // so a set of bookings can be deleted in one action.
      multiEventSelect: true,
      barMargin: 6,
      rowHeight: 50,
      eventStyle: 'plain' as any,
      eventColor: 'blue',
      eventLayout: 'pack',
      snap: true,
      eventRenderer: renderSchedulerEvent,
      createEventOnDblClick: false,
      zoomOnMouseWheel: false,
      zoomOnTimeAxisDoubleClick: false,
      readOnly: isHeatmapMode,
      infiniteScroll: false,
      autoAdjustTimeAxis: false,
      useInitialAnimation: false,
      transition: null,
      updateTimelineContextOnScroll: false,
      stickyHeaders: false,
      // Feature props — Bryntum React wrapper accepts each feature as
      // a top-level prop with a "Feature" suffix instead of nesting
      // them inside a `features` object.
      treeFeature: {
        expandOnCellClick: true,
      },
      rowCheckboxesFeature: false,
      cellEditFeature: false,
      quickFindFeature: false,
      filterFeature: false,
      sortFeature: false,
      columnReorderFeature: false,
      columnResizeFeature: false,
      eventDragFeature: isHeatmapMode ? false : {
        constrainDragToResource: false,
        showExactDropPosition: true,
        snapToTimeResolution: true,
        unifiedDrag: true,
        disabled: !isDragEnabled || isCoarseGranularityView,
      },
      eventResizeFeature: isHeatmapMode ? false : {
        showExactResizePosition: true,
        snapToTimeResolution: true,
        disabled: !isDragEnabled || isCoarseGranularityView,
      },
      eventEditFeature: false,
      eventTooltipFeature: {
        allowOver: true,
        hideDelay: isYearSummaryView ? YEAR_SUMMARY_HIDE_DELAY_MS : EVENT_TOOLTIP_HIDE_DELAY_MS,
        hoverDelay: isYearSummaryView ? YEAR_SUMMARY_HOVER_DELAY_MS : EVENT_TOOLTIP_HOVER_DELAY_MS,
        // Moving straight from one summary tile to the next restarts the hover delay.
        hideOnDelegateChange: isYearSummaryView,
        // Constrain the tooltip inside the viewport so it never gets clipped
        // when hovering over rows near the top/left/right edges.
        // 'b-t' means "align tooltip bottom-centre to target top-centre" (show above),
        // but Bryntum auto-flips to below/left/right when it doesn't fit.
        align: 'b-t',
        anchorToTarget: true,
        constrainTo: document.body,
        cls: 'b-heatmap-tooltip-popup',
        template: ({ eventRecord, tip }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string }; tip?: YearSummaryTip }) => {
          const hData = eventRecord.data?.heatmapData ||
            (eventRecord.get ? eventRecord.get('heatmapData') as BryntumEvent['heatmapData'] : null);
          if (hData) {
            const tickStart: Date = (eventRecord.data?.startDate as Date) || new Date();
            return generateHeatmapTooltipHtml(hData, tickStart);
          }

          const yearSummary = eventRecord.data?.yearSummary;
          syncYearSummaryTip(tip, yearSummary?.id ?? null);
          if (yearSummary) {
            return generateYearSummaryTooltipHtml(yearSummary, (eventRecord as { resource?: { name?: string } }).resource?.name ?? '');
          }

          // Absence events: show absence-specific tooltip
          const isAbsence = eventRecord.data?.isAbsenceEvent ||
            (eventRecord.get ? eventRecord.get('isAbsenceEvent') : false);
          if (isAbsence) {
            return generateAbsenceTooltipHtml(eventRecord.data);
          }

          const ghostReduction = eventRecord.data?.whatIfReduction;
          const rawInitId = ghostReduction?.bookingId ?? eventRecord.id ?? eventRecord.data?.id ?? (eventRecord.get ? eventRecord.get('id') : null);
          const eventId = rawInitId != null ? String(rawInitId) : null;
          let booking: Booking | null = null;
          if (eventId) {
            booking = findBookingById(eventId) || null;
          }
          if (!booking) {
            booking = eventRecord.data?.originalBooking ||
              eventRecord.originalData?.originalBooking ||
              (eventRecord.get ? eventRecord.get('originalBooking') as Booking : null);
          }
          if (!booking) {
            return '<div style="padding: 8px;">No booking data</div>';
          }
          return withWhatIfReductionHtml(generateTooltipHtml(booking), booking, whatIfReductionsRef.current);
        },
      },
      eventDragCreateFeature: isHeatmapMode ? false : {
        showTooltip: false,
      },
      // Disable all default context menus
      scheduleMenuFeature: false,
      cellMenuFeature: false,
      headerMenuFeature: false,
      contextMenuFeature: false,
      eventMenuFeature: false,
      timeAxisHeaderMenuFeature: false,
      nonWorkingTimeFeature: true,
      eventNonWorkingTimeFeature: true,
      timeRangesFeature: {
        enableResizing: false,
        showCurrentTimeLine: false,
        showHeaderElements: false,
      },
      // Enable resource time ranges for absences and bank holidays
      resourceTimeRangesFeature: true,
      // Disable schedule tooltip for empty cells (shows incorrect dates in Year/Quarter views)
      scheduleTooltipFeature: false,
      stickyEventsFeature: false,
    };
  }, [viewPreset, generateTooltipHtml, generateAbsenceTooltipHtml, generateHeatmapTooltipHtml, generateYearSummaryTooltipHtml, syncYearSummaryTip, findBookingById, calendarView, isYearSummaryView, isHeatmapMode, isDragEnabled, isCoarseGranularityView]);

  useEffect(() => {
    const applyTooltipTemplate = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;
      if (scheduler.features.eventTooltip) {
        const eventTooltipFeature = scheduler.features.eventTooltip as { template?: (data: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string }; tip?: YearSummaryTip }) => string; allowOver?: boolean; hideDelay?: number; align?: string; anchorToTarget?: boolean; constrainTo?: Element | null };
        eventTooltipFeature.allowOver = true;
        // Ensure tooltip stays within the viewport and auto-flips when near edges
        eventTooltipFeature.align = 'b-t';
        eventTooltipFeature.anchorToTarget = true;
        eventTooltipFeature.constrainTo = document.body;
        eventTooltipFeature.template = ({ eventRecord, tip }: { eventRecord: { data: BryntumEvent; originalData?: BryntumEvent; get?: (key: string) => unknown; id?: string }; tip?: YearSummaryTip }) => {
          try {
            const hData = eventRecord.data?.heatmapData ||
              (eventRecord.get ? eventRecord.get('heatmapData') as BryntumEvent['heatmapData'] : null);
            if (hData) {
              const tickStart: Date = (eventRecord.data?.startDate as Date) || new Date();
              return generateHeatmapTooltipHtmlRef.current(hData, tickStart);
            }

            const yearSummary = eventRecord.data?.yearSummary;
            syncYearSummaryTipRef.current(tip, yearSummary?.id ?? null);
            if (yearSummary) {
              return generateYearSummaryTooltipHtmlRef.current(yearSummary, (eventRecord as { resource?: { name?: string } }).resource?.name ?? '');
            }

            // Absence events: show absence-specific tooltip
            const isAbsenceEvt = eventRecord.data?.isAbsenceEvent ||
              (eventRecord.get ? eventRecord.get('isAbsenceEvent') : false);
            if (isAbsenceEvt) {
              return generateAbsenceTooltipHtmlRef.current(eventRecord.data);
            }

            // Resolve the event ID, coercing to string so __mr__ stripping works
            // even when Bryntum stores the id as a non-string type.
            // A reduce-hours ghost stands for its source booking.
            const ghostReduction = eventRecord.data?.whatIfReduction;
            const rawId = ghostReduction?.bookingId ?? eventRecord.id ?? eventRecord.data?.id ?? (eventRecord.get ? eventRecord.get('id') : null);
            const eventId = rawId != null ? String(rawId) : null;
            let booking: Booking | null = null;
            if (eventId) {
              booking = findBookingByIdRef.current(eventId) || null;
            }
            if (!booking) {
              booking = eventRecord.data?.originalBooking ||
                eventRecord.originalData?.originalBooking ||
                (eventRecord.get ? eventRecord.get('originalBooking') as Booking : null);
            }
            if (!booking) {
              return '<div style="padding: 8px;">No booking data</div>';
            }
            return withWhatIfReductionHtml(generateTooltipHtmlRef.current(booking), booking, whatIfReductionsRef.current);
          } catch (err) {
            console.error('Tooltip template error:', err);
            return '<div style="padding: 8px;">Error loading tooltip</div>';
          }
        };
      }
    };
    applyTooltipTemplate();
    const timerId = setTimeout(applyTooltipTemplate, 100);
    return () => clearTimeout(timerId);
  }, [generateTooltipHtml, generateAbsenceTooltipHtml, generateHeatmapTooltipHtml, findBookingById]);

  useEffect(() => {
    const tip = getEventTooltip();
    if (!tip) return;
    tip.hoverDelay = isYearSummaryView ? YEAR_SUMMARY_HOVER_DELAY_MS : EVENT_TOOLTIP_HOVER_DELAY_MS;
    tip.hideDelay = isYearSummaryView ? YEAR_SUMMARY_HIDE_DELAY_MS : EVENT_TOOLTIP_HIDE_DELAY_MS;
    tip.hideOnDelegateChange = isYearSummaryView;

    let switchTarget: HTMLElement | null = null;
    let switchTimer: ReturnType<typeof setTimeout> | undefined;
    const cancelSwitch = () => {
      clearTimeout(switchTimer);
      switchTimer = undefined;
      switchTarget = null;
    };
    const switchTo = (target: HTMLElement) => {
      switchTarget = null;
      switchTimer = undefined;
      if (pinnedYearSummaryRef.current || !target.isConnected || !target.matches(':hover')) return;
      tip.abortDelayedHide();
      tip.activeTarget = target;
      tip.updateContent().then(() => {
        if (!pinnedYearSummaryRef.current && tip.activeTarget === target) tip.showByTarget(target);
      }).catch((err) => {
        console.error('[YearSummary] updateContent failed:', err);
      });
    };
    // An open YEAR summary list stays put while the pointer crosses other tiles on
    // its way to the list, and moves to another tile only once the pointer rests
    // there. A pinned list moves only when another tile is clicked.
    const detach = tip.on('pointerOver', ({ target }) => {
      if (target === tip.activeTarget) {
        cancelSwitch();
        return true;
      }
      if (pinnedYearSummaryRef.current) return false;
      if (!isYearSummaryView || !tip.isVisible) return true;
      if (switchTarget !== target) {
        cancelSwitch();
        switchTarget = target;
        switchTimer = setTimeout(() => switchTo(target), YEAR_SUMMARY_HOVER_DELAY_MS);
      }
      return false;
    });
    return () => {
      cancelSwitch();
      detach();
    };
  }, [isYearSummaryView, getEventTooltip]);

  // Dynamically enable/disable drag features when the user preference changes at runtime
  // Also re-apply after groupingMode changes (e.g. switching from Resource to Project & Resource)
  // because the store sync rebuilds resources/events and Bryntum may reset feature state.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    if (!scheduler || scheduler.isDestroyed) return;

    const shouldDisable = isHeatmapMode || !isDragEnabled || isCoarseGranularityView;

    if (scheduler.features.eventDrag) {
      (scheduler.features.eventDrag as { disabled?: boolean }).disabled = shouldDisable;
    }
    if (scheduler.features.eventResize) {
      (scheduler.features.eventResize as { disabled?: boolean }).disabled = shouldDisable;
    }
  }, [isDragEnabled, isHeatmapMode, groupingMode, isCoarseGranularityView]);

  useEffect(() => {
    const overrideDragCreateTooltip = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return;
      const dragCreate = scheduler.features.eventDragCreate as {
        showTooltip?: boolean;
        tooltipTemplate?: (data: { startDate: Date; endDate: Date; startClockHtml: string; endClockHtml: string }) => string;
        tip?: { html?: string };
      } | undefined;
      if (dragCreate) {
        dragCreate.tooltipTemplate = ({ startDate, endDate }: { startDate: Date; endDate: Date }) => {
          const clockSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
          const startStr = format(startDate, 'HH:mm');
          const endStr = format(endDate, 'HH:mm');
          return `<div style="padding: 8px; font-size: 13px; line-height: 1.8;">\n            <div style="display: flex; align-items: center; gap: 6px;">${clockSvg} ${startStr}</div>\n            <div style="display: flex; align-items: center; gap: 6px;">${clockSvg} ${endStr}</div>\n          </div>`;
        };
      }
    };
    overrideDragCreateTooltip();
    const timerId = setTimeout(overrideDragCreateTooltip, 200);
    return () => clearTimeout(timerId);
  }, []);



  // When groupingMode changes, force-refresh the scheduler's resource and event stores
  // so the new tree structure and resourceId mappings take effect without a full remount.
  useEffect(() => {
    const sync = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) return false;

      const eventsData = isHeatmapMode ? heatmapEvents : bryntumEvents;

      // Tracks whether the incremental diff actually added or removed events
      // (as opposed to only mutating existing records in place, e.g. on
      // drag/resize).  Used after resumeRefresh to force an event-bar repaint
      // only when the event *set* changed — see the guarded repaint below.
      let eventSetChanged = false;
      // Tracks whether the resource-time-range set (public holidays / shaded
      // absences drawn via the ResourceTimeRanges feature) actually changed
      // during this pass.  Like eventSetChanged, used to force a repaint of the
      // affected rows after resumeRefresh so navigated-span shading appears
      // without a remount.
      let timeRangesChanged = false;
      // Tracks whether the global time-range set (timeRangeStore, e.g.
      // non-working hours in Day view) changed.  These render on the TimeRanges
      // feature's own canvas rather than the per-resource event pipeline, so
      // they need a separate feature refresh below.
      let globalTimeRangesChanged = false;
      let resourceStoreChanged = false;
      // Tracks whether any absence / public-holiday event was updated in place
      // (existing.set(newEvt)).  These never appear in toAdd/toRemove, so
      // eventSetChanged stays false for them even though their bars must be
      // repainted for the navigated span.
      let absenceUpdated = false;
      let timeSpanAdvanced = false;
      let groupingChanged = false;
      let resourcesApplied = false;
      let eventStoreSynced = false;
      let syncCompleted = false;
      // Resource ids whose event bars must be repainted (owners of added/removed
      // events).  Lets us do a scoped repaint instead of refreshing every row.
      const affectedResourceIds = new Set<string>();

      const endSync = perfStart('store-sync');
      const syncStartedAt = performance.now();

      // Suspend ALL Bryntum rendering for the entire operation so the engine
      // never sees a partial state (empty store between removeAll and add).
      scheduler.suspendRefresh();
      try {
        const nextView = { viewPreset, startDate, endDate };
        timeSpanAdvanced = applySchedulerView(scheduler, lastSyncedViewRef.current, nextView);
        groupingChanged = lastSyncedGroupingRef.current !== groupingMode;
        if (shouldLoadEventDataset({
          initialised: lastSyncedViewRef.current != null,
          groupingChanged,
          timeSpanAdvanced,
          eventsChanged: eventsData !== lastSyncedEventsRef.current,
        })) {
          loadSchedulerEventDataset(scheduler.eventStore, eventsData);
        }
        lastSyncedGroupingRef.current = groupingMode;
        if (timeSpanAdvanced) {
          lastSyncedViewRef.current = nextView;
        }

        if (prevAppliedResourcesRef.current !== pagedResources) {
          resourcesApplied = true;
          // A resource page only adds rows, so insert those records instead of
          // replacing the data: replacing rebuilds every row and resets the
          // vertical scroll position the user paged down to.
          const delta = diffResourceTreeInsert(
            (prevAppliedResourcesRef.current ?? []) as ResourceTreeNodeConfig[],
            pagedResources as ResourceTreeNodeConfig[],
          );
          const scrollTopBeforeInsert = readVerticalScrollMetrics(scheduler)?.scrollTop ?? 0;
          const inserted = delta.kind === 'insert'
            && prevAppliedResourcesRef.current != null
            && applyResourceTreeInsert(scheduler.resourceStore, delta.additions);
          // Bryntum can still hold a scroll restore it queued while the grid had
          // no rows, and plays it back — at the top — on the first row count
          // change. Rows only ever arrive below or within what is loaded, so the
          // position the user had scrolled to still stands.
          if (inserted && (readVerticalScrollMetrics(scheduler)?.scrollTop ?? 0) < scrollTopBeforeInsert) {
            setVerticalScroll(scheduler, scrollTopBeforeInsert);
          }

          if (!inserted && delta.kind !== 'unchanged') {
            // Replacing the data rebuilds every row from the tree's own
            // `expanded`, so the groups the user collapsed are handed over
            // already collapsed rather than springing open and then closing.
            // Taken from the collapsed groups rather than the live rows, which
            // also read as collapsed for a group the window has just reached.
            syncSchedulerResources(
              scheduler.resourceStore,
              withCollapsedGroups(
                pagedResources as ResourceTreeNode[],
                collapsedGroupIdsRef.current,
              ) as ResourceModelConfig[],
            );
          }

          syncGroupRowExpansion(scheduler);

          // Imperatively apply dynamic rowHeight on leaf resource records.
          // Plain data properties are not picked up by Bryntum's tree model
          // — we must call set() on the model instance.
          scheduler.resourceStore.forEach((record: any) => {
            if (!record.isParent) {
              if (record.data?.rowHeight) {
                record.set('rowHeight', record.data.rowHeight);
              }
            }
          });

          prevAppliedResourcesRef.current = pagedResources;
          resourceStoreChanged = delta.kind !== 'unchanged';
        }

        // A pass that brings no new events, what-if or rows (e.g. only the
        // holiday shading was rebuilt) leaves the event store as it is.
        eventStoreSynced = shouldSyncEventStore({
          eventsChanged: eventsData !== lastSyncedEventsRef.current,
          whatIfChanged: whatIfScenario !== lastSyncedWhatIfRef.current,
          resourcesChanged: resourceStoreChanged,
          timeSpanAdvanced,
          groupingChanged,
        });
        if (eventStoreSynced) {
          // ── Incremental event store sync ──────────────────────────────────
          // Instead of removeAll() + add() which destroys Bryntum's internal
          // event layout cache for ALL rows (causing bar heights to be
          // recalculated from scratch and potentially shrink on unrelated
          // resources), we diff the current store against the new data and
          // only add, remove, or update records that actually changed.
          // This preserves Bryntum's pack-layout state for untouched rows.
          const newEventsById = new Map<string, (typeof eventsData)[number]>();
          eventsData.forEach(e => newEventsById.set(String(e.id), e));

          // Remove events that are no longer present
          const toRemove = (scheduler.eventStore.getRange() as any[]).filter(
            (r: any) => !newEventsById.has(String(r.id)),
          );
          if (toRemove.length > 0) {
            toRemove.forEach((r: { resourceId?: unknown; data?: { resourceId?: unknown } }) => {
              const rid = r.resourceId ?? r.data?.resourceId;
              if (rid != null) affectedResourceIds.add(String(rid));
            });
            scheduler.eventStore.remove(toRemove);
          }

          // Add new events and update changed existing events
          const toAdd: typeof eventsData = [];
          newEventsById.forEach((newEvt, id) => {
            const existing = scheduler.eventStore.getById(id) as any;
            if (!existing) {
              toAdd.push(newEvt);
            } else if ((newEvt as any).isAbsenceEvent) {
              // Always force-update absence events after resource store rebuild.
              // Without this, Bryntum can lose the resource association for absence
              // events when the resource store is rebuilt (removeAll + add), causing
              // them to not render after date navigation even though they are in the store.
              existing.set(newEvt);
              // In-place updates never show up in toAdd/toRemove, so flag them
              // explicitly and record the owning row so the post-resumeRefresh
              // repaint below covers absence / public-holiday bars on navigation.
              absenceUpdated = true;
              if (newEvt.resourceId != null) affectedResourceIds.add(String(newEvt.resourceId));
            } else {
              // Update if key visual fields changed
              const d = existing.data ?? {};
              const startChanged = !sameEventValue(d.startDate, newEvt.startDate);
              const endChanged = !sameEventValue(d.endDate, newEvt.endDate);
              const resChanged = String(d.resourceId ?? '') !== String(newEvt.resourceId ?? '');
              const nameChanged = String(d.name ?? '') !== String(newEvt.name ?? '');
              const colorChanged = String(d.eventColor ?? '') !== String(newEvt.eventColor ?? '');
              const clsChanged = String(d.cls ?? '') !== String(newEvt.cls ?? '');
              const heatmapChanged = newEvt.heatmapData != null && d.heatmapData !== newEvt.heatmapData;
              const yearSummaryChanged = newEvt.yearSummary != null && d.yearSummary?.signature !== newEvt.yearSummary.signature;
              if (startChanged || endChanged || resChanged || nameChanged || colorChanged || clsChanged || heatmapChanged || yearSummaryChanged) {
                existing.set(newEvt);
              } else if ((newEvt as any).originalBooking && d.originalBooking !== (newEvt as any).originalBooking) {
                // Keep originalBooking in sync even when visual fields are unchanged
                // so tooltips and context menus always reflect the latest booking data
                // (e.g. after editing a multi-resource booking from one resource row).
                d.originalBooking = (newEvt as any).originalBooking;
              }
            }
          });
          if (toAdd.length > 0) {
            toAdd.forEach((e) => {
              if (e.resourceId != null) affectedResourceIds.add(String(e.resourceId));
            });
            const endAdd = perfStart('eventStore.add');
            scheduler.eventStore.add(toAdd as any);
            endAdd({ added: toAdd.length });
          }

          // Record whether the visible event set changed so we can force a
          // repaint of the newly added/removed bars after resumeRefresh.  Pure
          // in-place updates (drag/resize) do not flip this flag and therefore
          // never trigger the guarded repaint below.
          eventSetChanged = timeSpanAdvanced || toAdd.length > 0 || toRemove.length > 0;

          // ── What-if ghost tile injection ──────────────────────────────────────
          // Injected here — inside suspendRefresh, after the regular events are
          // loaded — so it is atomic with the store population and fires exactly
          // when the scheduler is confirmed ready (no setTimeout guesswork).
          // Reduce/delay scenarios change a booking in place, so they carry ghost
          // assignments without a candidate resource — preview those too.
          if (
            whatIfScenario &&
            whatIfScenario.type === 'rebalance' &&
            (whatIfScenario.candidateResourceId ||
              (whatIfScenario.ghostAssignments?.length ?? 0) > 0)
          ) {
            const {
              atRiskBookingIds,
              atRiskResourceId,
              candidateResourceId,
              projectName,
              affectedWeekStarts,
              atRiskBookingDates,
              ghostAssignments,
              bookingAssignments,
            } = whatIfScenario;

            // The ghost is the *proposed new* booking, so it must render on the
            // resource that would RECEIVE the work — never the over-utilised
            // at-risk resource it is being moved off of. Per-booking branches use
            // their own mapped target; the fallbacks use the single candidate.
            const fallbackGhostResourceId = String(candidateResourceId ?? atRiskResourceId);

            // Build ghost event(s) sized to the actual booking span when available,
            // falling back to a Mon 09:00–17:00 block per affected week.
            let ghostEvents: object[];
            if (ghostAssignments && ghostAssignments.length > 0) {
              // Capacity-aware preview: draw each ghost on the resource that would
              // actually receive it, so the preview never shows an over-utilised
              // resource that isn't free.
              ghostEvents = ghostAssignments.map((ga, idx) => ({
                id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
                resourceId: String(ga.resourceId),
                name: '',
                startDate: new Date(ga.startDate),
                endDate:   new Date(ga.endDate),
                cls: ga.reduction ? 'whatif-ghost-proposed whatif-ghost-reduced' : 'whatif-ghost-proposed',
                isWhatIfGhost: true,
                whatIfReduction: ga.reduction,
                draggable: false,
                resizable: false,
                eventStyle: 'hollow',
                eventColor: 'none',
                style: WHAT_IF_GHOST_STYLE,
              }));
            } else if (bookingAssignments && bookingAssignments.length > 0) {
              // Per-booking distribution plan without pre-sized ghost dates: place
              // each ghost on its mapped resource, sizing from the matching source
              // booking record when available.
              ghostEvents = bookingAssignments.map((ba, idx) => {
                const rec = scheduler.eventStore.getById(ba.bookingId) as
                  | { startDate?: Date | string; endDate?: Date | string }
                  | null;
                const start = rec?.startDate ? new Date(rec.startDate) : new Date(whatIfScenario.targetWeekStart);
                const end = rec?.endDate ? new Date(rec.endDate) : new Date(whatIfScenario.targetWeekStart);
                return {
                  id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
                  resourceId: String(ba.candidateResourceId),
                  name: '',
                  startDate: start,
                  endDate: end,
                  cls: 'whatif-ghost-proposed',
                  isWhatIfGhost: true,
                  eventStyle: 'hollow',
                  eventColor: 'none',
                  style: WHAT_IF_GHOST_STYLE,
                };
              });
            } else if (atRiskBookingDates && atRiskBookingDates.length > 0) {
              ghostEvents = atRiskBookingDates.map((bd, idx) => ({
                id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
                resourceId: fallbackGhostResourceId,
                name: '',
                startDate: new Date(bd.startDate),
                endDate:   new Date(bd.endDate),
                cls: 'whatif-ghost-proposed',
                isWhatIfGhost: true,
                eventStyle: 'hollow',
                eventColor: 'none',
                style: WHAT_IF_GHOST_STYLE,
              }));
            } else {
              const weeks = affectedWeekStarts && affectedWeekStarts.length > 0
                ? affectedWeekStarts
                : [whatIfScenario.targetWeekStart];
              ghostEvents = weeks.map((ws, idx) => {
                const dayStart = new Date(ws);
                dayStart.setHours(9, 0, 0, 0);
                const dayEnd = new Date(ws);
                dayEnd.setHours(17, 0, 0, 0);
                return {
                  id: `${GHOST_EVENT_ID_PREFIX}${idx}`,
                  resourceId: fallbackGhostResourceId,
                  name: '',
                  startDate: dayStart,
                  endDate: dayEnd,
                  cls: 'whatif-ghost-proposed',
                  isWhatIfGhost: true,
                  eventStyle: 'hollow',
                  eventColor: 'none',
                  style: WHAT_IF_GHOST_STYLE,
                };
              });
            }
            scheduler.eventStore.add(ghostEvents as any);

            // Apply whatif-moving class to the at-risk source booking tiles.
            if (atRiskBookingIds && atRiskBookingIds.length > 0) {
              atRiskBookingIds.forEach((bid) => {
                const rec = scheduler.eventStore.getById(bid);
                if (rec) {
                  const existing: string = (rec as any).data?.cls ?? '';
                  if (!existing.includes(MOVING_CLS)) {
                    rec.set('cls', [existing, MOVING_CLS].filter(Boolean).join(' '));
                  }
                }
              });
            } else if (atRiskResourceId) {
              ((scheduler.eventStore as any).allRecords ?? []).forEach((rec: any) => {
                const ob = rec.data?.originalBooking;
                if (!ob) return;
                if (
                  String(ob.resourceId) !== String(atRiskResourceId) ||
                  (projectName && ob.projectName !== projectName)
                ) return;
                const existing: string = rec.data?.cls ?? '';
                if (!existing.includes(MOVING_CLS)) {
                  rec.set('cls', [existing, MOVING_CLS].filter(Boolean).join(' '));
                }
              });
            }
          }
        }

        // Public holidays and shaded absences (.bank-holiday-range /
        // .absence-range) are drawn through the event render pipeline (the
        // ResourceTimeRanges feature chains getEventsToRender), so — exactly
        // like event bars — after resumeRefresh(true) they only lay out for the
        // navigated span if we force a repaint of their resource rows.  Detect
        // whether the range set actually changed (ids/span/resource/shading) so
        // the guarded repaint below fires, and record the owning rows so it can
        // be scoped.
        const rtrs = (scheduler as any).resourceTimeRangeStore;
        if (rtrs) {
          const newRanges = (resourceTimeRanges ?? []) as Array<Record<string, unknown>>;
          const current = ((rtrs.getRange?.() ?? []) as Array<{ data?: Record<string, unknown> }>);
          const rangeSig = (d: Record<string, unknown>) =>
            `${String(d.id)}|${String(d.resourceId ?? '')}|${String(d.startDate)}|${String(d.endDate)}|${String(d.cls ?? '')}|${String(d.timeRangeColor ?? '')}|${String(d.name ?? '')}|${String(d.style ?? '')}`;
          const currentSigs = new Set(current.map((r) => rangeSig(r.data ?? (r as Record<string, unknown>))));
          const newSigs = new Set(newRanges.map((r) => rangeSig(r)));
          timeRangesChanged =
            currentSigs.size !== newSigs.size ||
            [...newSigs].some((s) => !currentSigs.has(s));
          if (timeRangesChanged) {
            const collectRid = (d: Record<string, unknown>) => {
              const rid = d.resourceId;
              if (rid != null) affectedResourceIds.add(String(rid));
            };
            newRanges.forEach(collectRid);
            current.forEach((r) => collectRid(r.data ?? (r as Record<string, unknown>)));
          }
          if (timeRangesChanged || resourceStoreChanged) {
            rtrs.removeAll();
            rtrs.add(resourceTimeRanges as any);
          }
        }

        // Re-populate the timeRangeStore for global time ranges (non-working
        // hours in Day view etc.).  These render on the TimeRanges feature's own
        // canvas, not the per-resource event pipeline, so a scoped resource
        // repaint will not cover them — track the change separately and refresh
        // the feature after resumeRefresh.
        const trs = (scheduler as any).timeRangeStore;
        if (trs) {
          const newGlobal = (timeRanges ?? []) as Array<Record<string, unknown>>;
          const current = ((trs.getRange?.() ?? []) as Array<{ data?: Record<string, unknown> }>);
          const sig = (d: Record<string, unknown>) =>
            `${String(d.id)}|${String(d.startDate)}|${String(d.endDate)}|${String(d.cls ?? '')}|${String(d.name ?? '')}`;
          const currentSigs = new Set(current.map((r) => sig(r.data ?? (r as Record<string, unknown>))));
          const newSigs = new Set(newGlobal.map((r) => sig(r)));
          globalTimeRangesChanged =
            currentSigs.size !== newSigs.size ||
            [...newSigs].some((s) => !currentSigs.has(s));
          if (globalTimeRangesChanged) {
            trs.removeAll();
            trs.add(timeRanges as any);
          }
        }

        lastSyncedEventsRef.current = eventsData;
        lastSyncedWhatIfRef.current = whatIfScenario;
        syncCompleted = true;
      } finally {
        scheduler.resumeRefresh(!syncCompleted || syncPassChangedScheduler({
          timeSpanAdvanced,
          groupingChanged,
          resourcesApplied,
          eventStoreSynced,
          timeRangesChanged,
          globalTimeRangesChanged,
        }));
        endSync({ events: eventsData.length, resourceStoreChanged });
      }

      // Force a repaint for the rows whose event set OR resource-time-range set
      // changed.
      //
      // In the stable-key non-heatmap navigation path the Bryntum instance is
      // not remounted on date change.  When new bookings for the new date range
      // arrive *after* the time span has already been advanced,
      // resumeRefresh(true) alone does not lay out the freshly added events
      // for the unchanged span — they stay
      // invisible until the component is remounted (e.g. switching tabs).
      //
      // The exact same race applies to resource time ranges (public holidays /
      // shaded absences): they are drawn through the event render pipeline, so
      // the freshly added ranges for the navigated span are not laid out until
      // a remount.  Since repaintEventsForResource() re-renders a row's whole
      // event canvas (event bars AND resource time ranges), the same scoped
      // repaint fixes both — guarded on timeRangesChanged as well as
      // eventSetChanged (their owning rows were added to affectedResourceIds
      // above).
      //
      // refreshRows() only re-renders grid CELLS, not event bars, so it does
      // not fix this.  repaintEventsForResource() rerenders the event bars for
      // a specific row, which is exactly the scoped repaint we need and it does
      // not recompute layout for unrelated rows (avoiding the bar-shrinking
      // regression).  Guarded so pure in-place drag/resize updates never
      // trigger it.
      //
      // repaint/refresh alone is not enough, though: freshly-added event
      // records have NO layout yet.  Bryntum's scheduling project normalises
      // added records asynchronously, and since the span did not change here,
      // resumeRefresh(true) re-renders the *existing* layout without waiting
      // for that calculation — so the new bars have nothing to draw.  We must
      // commit the project first (commitAsync resolves once the engine has laid
      // the new records into the current time span) and only then repaint.
      //
      // Trigger the forced re-layout whenever ANY of the navigation-affecting
      // conditions held this pass:
      //   • eventSetChanged     — bookings/absences added or removed
      //   • timeRangesChanged   — resource time ranges (public holidays /
      //                           shaded absences) changed
      //   • absenceUpdated      — absence / public-holiday events updated in
      //                           place (never in toAdd/toRemove)
      //   • resourceStoreChanged — resource rows or their layout changed
      //   • timeSpanAdvanced    — the visible span moved to already-loaded data
      // Pure in-place drag/resize updates flip none of these, so they still
      // never trigger a full relayout.
      if (
        (eventSetChanged || timeRangesChanged || absenceUpdated || resourceStoreChanged || timeSpanAdvanced) &&
        !scheduler.isDestroyed
      ) {
        const sched = scheduler as unknown as {
          repaintEventsForResource?: (r: unknown) => void;
          refreshResources?: (records?: unknown[]) => void;
          resourceStore: {
            getById: (id: string) => unknown;
            query?: (fn: (r: { id?: unknown; resourceId?: unknown; data?: { resourceId?: unknown } }) => boolean) => unknown[];
          };
          project?: { commitAsync?: () => Promise<void> };
        };

        // Resolve the resource records owning the changed events.  In grouped
        // modes (e.g. Project & Resource) a leaf record's id is a composite
        // that differs from the raw event resourceId, so getById misses it —
        // fall back to querying the store by the underlying resourceId.
        const resolveResourceRecords = (): unknown[] => {
          const records: unknown[] = [];
          affectedResourceIds.forEach((rid) => {
            const direct = sched.resourceStore.getById(rid);
            if (direct) {
              records.push(direct);
            } else if (typeof sched.resourceStore.query === 'function') {
              const matches = sched.resourceStore.query(
                (r) => String(r.resourceId ?? r.data?.resourceId ?? r.id) === rid,
              );
              matches.forEach((m) => records.push(m));
            }
          });
          return records;
        };

        const repaint = () => {
          if (scheduler.isDestroyed) return;

          if (resourceStoreChanged && typeof sched.refreshResources === 'function') {
            sched.refreshResources();
            return;
          }

          const records = resolveResourceRecords();
          let repainted = 0;
          if (records.length > 0 && typeof sched.repaintEventsForResource === 'function') {
            records.forEach((rec) => {
              sched.repaintEventsForResource!(rec);
              repainted++;
            });
          }
          // Fallback: if no specific row could be resolved (e.g. a pure
          // timeSpanAdvanced pass), repaint all resource rows' event bars
          // (still layout-preserving, unlike refreshRows()/refreshEvents()).
          if (repainted === 0 && typeof sched.refreshResources === 'function') {
            sched.refreshResources();
          }
        };

        if (sched.project && typeof sched.project.commitAsync === 'function') {
          sched.project.commitAsync().then(repaint).catch(() => {
            // Scheduler was torn down mid-commit — nothing to repaint.
          });
        } else {
          repaint();
        }
      }

      // Global time ranges (timeRangeStore) render on the TimeRanges feature's
      // own canvas rather than the per-resource event pipeline, so the resource
      // repaint above does not cover them.  Force the feature to re-render when
      // its set changed so newly added ranges appear on same-view navigation.
      if (globalTimeRangesChanged && !scheduler.isDestroyed) {
        const trFeature = (scheduler.features as unknown as {
          timeRanges?: { refresh?: () => void };
        }).timeRanges;
        if (trFeature && typeof trFeature.refresh === 'function') {
          trFeature.refresh();
        }
      }

      // Re-apply drag/resize feature state after store rebuild.
      // Switching grouping modes (e.g. Resource → Project & Resource) rebuilds
      // the resource/event stores which can reset Bryntum's internal feature state.
      const shouldDisableDrag = isHeatmapMode || !isDragEnabled || isCoarseGranularityView;
      if (scheduler.features.eventDrag) {
        (scheduler.features.eventDrag as { disabled?: boolean }).disabled = shouldDisableDrag;
      }
      if (scheduler.features.eventResize) {
        (scheduler.features.eventResize as { disabled?: boolean }).disabled = shouldDisableDrag;
      }

      // NOTE: Do NOT call scheduler.refreshRows() here — it triggers a
      // full event-layout recalculation that can shrink existing event
      // bars when unrelated bookings are added to other resources.

      // The loading overlay stays up until the events are on screen.
      // The committed events are drawn on Bryntum's next row-render pass, which
      // over a long range (a year of bookings) lands several frames later than
      // the commit — waiting for `renderRows` keeps the overlay up until the
      // bars exist rather than clearing a second or two early.
      const notifyRendered = () => {
        if (scheduler.isDestroyed) return;
        const endRender = perfStart('commit→renderRows');
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          detach();
          clearTimeout(timer);
          endRender({ timedOut: !renderedByEvent });
          if (scheduler.isDestroyed) return;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              perfLog('events visible', {
                msSinceSyncStart: Math.round(performance.now() - syncStartedAt),
                events: eventsData.length,
              });
              onEventsRenderedRef.current?.();
            });
          });
        };
        let renderedByEvent = false;
        const detach = (scheduler as unknown as {
          on: (config: { renderRows: () => void; once: boolean }) => () => void;
        }).on({
          renderRows: () => {
            renderedByEvent = true;
            finish();
          },
          once: true,
        });
        // A sync that renders nothing (no row-level change) never fires the
        // event, so the overlay must not depend on it alone.
        const timer = setTimeout(finish, EVENT_RENDER_WAIT_MS);
      };
      const project = (scheduler as unknown as { project?: { commitAsync?: () => Promise<void> } }).project;
      if (project && typeof project.commitAsync === 'function') {
        const endCommit = perfStart('project.commitAsync');
        project.commitAsync().then(() => {
          endCommit();
          notifyRendered();
        }).catch(() => onEventsRenderedRef.current?.());
      } else {
        notifyRendered();
      }

      return true;
    };

    // On a fresh mount (initial mount or a heatmap mode toggle) the Bryntum
    // React wrapper may not have attached the instance to schedulerRef yet when
    // this effect first fires.  Retry after a short delay so the store is always in sync
    // even if the ref attaches asynchronously.
    if (!sync()) {
      const t = setTimeout(() => sync(), 50);
      return () => clearTimeout(t);
    }

  }, [groupingMode, pagedResources, bryntumEvents, heatmapEvents, isHeatmapMode, startDate, endDate, resourceTimeRanges, timeRanges, viewPreset, whatIfScenario, isDragEnabled, isCoarseGranularityView]);


  // ── Vertical lazy loading of resource rows ───────────────────────────────
  // A request stays pending until the owner hands back a new resources array,
  // so a burst of scroll events only ever pulls in one page.
  const pendingResourceLoadRef = useRef(false);
  // Rows on screen when the window was last grown to fill the viewport, so a
  // growth that brought no rows with it is not repeated.
  const lastViewportTopUpRowsRef = useRef<number | null>(null);
  const lastBlockedLogRef = useRef(0);
  const lastVerticalScrollRef = useRef(0);
  // Read inside the scroll closure so the listener is attached once per
  // Bryntum instance instead of being torn down on every page load.
  const resourceLoadStateRef = useRef({
    loadedResources: materialisedResourceIds.length,
    hasMoreRows: pagedTree.hasMoreRows,
    isLoadingMoreResources,
  });
  resourceLoadStateRef.current = {
    loadedResources: materialisedResourceIds.length,
    hasMoreRows: pagedTree.hasMoreRows,
    isLoadingMoreResources,
  };

  const hasMoreResources = pagedTree.hasMoreRows;

  useEffect(() => {
    pendingResourceLoadRef.current = false;
  }, [pagedResources]);

  // A view change drops the window back to the first page. Without scrolling
  // back up, the clamped scroll position would sit at the bottom of the
  // remaining rows and immediately pull the next page back in.
  const previousResetTokenRef = useRef(resourceWindowResetToken);
  useEffect(() => {
    if (previousResetTokenRef.current === resourceWindowResetToken) return;
    previousResetTokenRef.current = resourceWindowResetToken;
    lastVerticalScrollRef.current = 0;
    setVerticalScroll(schedulerRef.current?.instance as Scheduler | undefined, 0);
  }, [resourceWindowResetToken]);

  // Replacing the store data resets Bryntum's vertical scroll position, which
  // would throw the user back to the top of the rows they just scrolled down.
  useEffect(() => {
    if (!featureFlags.isEnabled('lazyLoad')) return;
    const target = lastVerticalScrollRef.current;
    if (target <= 0) return;
    const frame = requestAnimationFrame(() => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      const metrics = scheduler ? readVerticalScrollMetrics(scheduler) : null;
      if (!metrics || metrics.scrollTop >= target) return;
      setVerticalScroll(scheduler, target);
    });
    return () => cancelAnimationFrame(frame);
  }, [pagedResources]);

  // Rows that don't overflow the viewport can never produce a scroll event, so
  // keep pulling pages in until they do (or until every resource is loaded).
  useEffect(() => {
    if (!featureFlags.isEnabled('lazyLoad')) return;
    if (!hasMoreResources || isLoadingMoreResources) return;
    // The rows the window has left to give out may all belong to groups that are
    // closed — every group, after collapse all — and growing the window then
    // shows nothing while spending the pages that a group opened by hand would
    // otherwise be given, so it opens far past its first page. One page is
    // pulled in per set of rows on screen, and the next only once that one
    // brought rows with it.
    if (lastViewportTopUpRowsRef.current === materialisedResourceIds.length) return;
    const timer = setTimeout(() => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed || pendingResourceLoadRef.current) return;
      const metrics = readVerticalScrollMetrics(scheduler);
      if (!metrics || !(isViewportUnderfilled(metrics) || isNearVerticalBottom(metrics))) return;
      lastViewportTopUpRowsRef.current = materialisedResourceIds.length;
      perfLog('scroll-load: loaded rows do not fill the viewport, requesting more resources', {
        loadedResources: resourceLoadStateRef.current.loadedResources,
        ...metrics,
      });
      loadMoreRowsRef.current();
      pendingResourceLoadRef.current = true;
    }, 100);
    return () => clearTimeout(timer);
  }, [pagedResources, materialisedResourceIds, hasMoreResources, isLoadingMoreResources]);

  useEffect(() => {
    // Without lazy loading there is nothing for a scroll to pull in, so the rows
    // scroll without a listener measuring them.
    if (!featureFlags.isEnabled('lazyLoad')) return;

    let animationFrame: number | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attachedTo: Scheduler | null = null;

    const handleScroll = () => {
      if (animationFrame !== null) return;
      animationFrame = requestAnimationFrame(() => {
        animationFrame = null;
        const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
        if (!scheduler || scheduler.isDestroyed) return;

        const metrics = readVerticalScrollMetrics(scheduler);
        if (!metrics) return;
        lastVerticalScrollRef.current = metrics.scrollTop;
        if (!isNearVerticalBottom(metrics)) return;

        const { loadedResources, hasMoreRows: hasMore, isLoadingMoreResources: isLoading } =
          resourceLoadStateRef.current;

        if (!hasMore || isLoading || pendingResourceLoadRef.current) {
          const now = Date.now();
          if (now - lastBlockedLogRef.current > 1000) {
            lastBlockedLogRef.current = now;
            perfLog('scroll-load: vertical bottom reached, no load', {
              loadedResources,
              hasMoreResources: hasMore,
              isLoadingMoreResources: isLoading,
              requestPending: pendingResourceLoadRef.current,
            });
          }
          return;
        }

        perfLog('scroll-load: vertical bottom reached, requesting more resources', {
          loadedResources,
          ...metrics,
        });
        loadMoreRowsRef.current();
        pendingResourceLoadRef.current = true;
      });
    };

    // The Bryntum React wrapper can attach the instance after this effect
    // first runs, so keep retrying until the listener is on a live instance.
    const attach = () => {
      const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
      if (!scheduler || scheduler.isDestroyed) {
        retryTimer = setTimeout(attach, 50);
        return;
      }
      attachedTo = scheduler;
      scheduler.on('scroll', handleScroll);
    };
    attach();

    return () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
      if (animationFrame !== null) cancelAnimationFrame(animationFrame);
      if (attachedTo && !attachedTo.isDestroyed) attachedTo.un('scroll', handleScroll);
    };
  }, [schedulerKey]);


  // The utilisation badge is produced by the resource column's cell renderer,
  // which Bryntum only re-invokes when it repaints that column's cells.  A
  // booking change that neither adds nor removes rows — most notably a booking
  // reassigned to another resource — leaves both rows' badges showing the
  // previous utilisation.  Refreshing just this column's cells recomputes the
  // badges from the (already fresh) refs without touching the event layout, so
  // it avoids the bar-shrinking regression that refreshRows() causes.
  useEffect(() => {
    const scheduler = schedulerRef.current?.instance as Scheduler | undefined;
    // With no resource rows (e.g. a search matching nothing in the period) Bryntum keeps
    // detached row cells it can no longer resolve, and repainting them throws.
    if (!scheduler || scheduler.isDestroyed || scheduler.resourceStore.count === 0) return;
    const columns = scheduler.columns as ColumnStore | undefined;
    const resourceColumn = columns?.visibleColumns?.find((c: Column) => c.field === 'name');
    resourceColumn?.refreshCells();
    // Keyed on the utilisation content rather than on array/settings identity:
    // those change on every unrelated state update and repaint every row.
  }, [utilisationRefreshKey]);


  // Show loading state while theme is loading
  if (!isThemeLoaded) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Loading theme...</p>
        </div>
      </div>
    );
  }

  // CRITICAL: Prevent rendering if dates are invalid
  if (!startDate || !endDate || startDate >= endDate) {
    console.error('🚨 CRITICAL: Cannot render scheduler with invalid dates:', {
      startDate: startDate?.toString(),
      endDate: endDate?.toString(),
      startTime: startDate?.getTime(),
      endTime: endDate?.getTime()
    });
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 text-destructive">⚠︝</div>
          <p className="text-sm text-destructive">Invalid date range. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  // Cast to any to bypass strict Bryntum library type incompatibilities
  const AnyBryntumScheduler = BryntumScheduler as any;

  return (
    <div className={`h-full w-full bryntum-scheduler-container custom-style${calendarView === 'year' ? ' eb-year-view' : ''}`}>

      <AnyBryntumScheduler
        key={schedulerKey}
        ref={schedulerRef as any}
        id={schedulerKey}
        startDate={initialDatesRef.current.startDate}
        endDate={initialDatesRef.current.endDate}
        viewPreset={initialViewPresetRef.current}
        resources={initialDataRef.current.resources}
        events={initialDataRef.current.events}
        resourceTimeRanges={initialDataRef.current.resourceTimeRanges}
        timeRanges={initialDataRef.current.timeRanges}
        columns={columns}
        {...schedulerConfig}
        onEventClick={handleEventClick}
        onEventDblClick={handleEventOrYearSummaryDblClick}
        onScheduleClick={handleScheduleClick}
        onScheduleDblClick={handleScheduleDblClick}
        onEventDrop={handleEventDrop}
        onEventResizeEnd={handleEventResizeEnd}
        onDragCreateEnd={handleDragCreateEnd}
      />


    </div>
  );
});

BryntumSchedulerTimeline.displayName = 'BryntumSchedulerTimeline';
